/**
 * =============================================================================
 * GameScene - Main game scene for the 2D MMORPG client
 * =============================================================================
 * 
 * RENDERING ARCHITECTURE & FIXES
 * ==============================
 * 
 * This file contains critical rendering fixes for smooth, pixel-perfect gameplay.
 * The rendering pipeline follows these principles:
 * 
 * 1. PIXEL-PERFECT RENDERING
 *    - All sprite positions are rounded to integers before rendering
 *    - Interpolation uses floats internally for smooth motion
 *    - Final render positions snap to pixel boundaries
 *    - Phaser config uses roundPixels:true and pixelArt:true
 * 
 * 2. CAMERA SYSTEM
 *    - Camera uses DIRECT CONTROL (not Phaser's follow system)
 *    - updateCameraPosition() is called every frame to center on local player
 *    - This eliminates any lag/jitter from the follow system's interpolation
 *    - Camera scroll is set directly based on player's interpolated position
 * 
 * 3. MOVEMENT INTERPOLATION
 *    - Server sends discrete tile positions
 *    - Client interpolates between positions using constant velocity
 *    - Delta time is capped at 50ms to prevent jerky motion on frame drops
 *    - Visual position (currentWorldX/Y) moves toward target (targetWorldX/Y)
 * 
 * 4. ASSET LOADING
 *    - Textures are loaded once in preload() and converted to spritesheets in create()
 *    - BodySpriteManager tracks which bodies have been set up to prevent re-creation
 *    - Sprite recreation during gameplay is avoided to prevent flickering
 * 
 * 5. UPDATE ORDER
 *    - Server state sync happens first (addOrUpdatePlayer)
 *    - Interpolation updates target positions based on server state
 *    - Visual positions are updated with capped delta time
 *    - Camera follows the interpolated visual position
 * 
 * PERFORMANCE TARGETS:
 * - Stable 60 FPS on desktop
 * - No visible flickering
 * - No jitter or snapping during movement
 * - Smooth camera tracking similar to classic MMORPGs
 * 
 * =============================================================================
 */

import Phaser from 'phaser';

import { ColyseusClient, type GameRoom } from '../network/ColyseusClient';
import { isDecorationKey, getDecorationAssetUrl } from '../util/decorationAssets';

import {
  ClientMessageType,

  type MoveMessage,

  ServerMessageType,

  type ServerChatMessage,

  type AttackResultMessage,

  type ChatMessage,

  type InventorySlotData,

  type NpcTemplateListMessage,

  type InteractNpcMessage,

  type NpcDialogueMessage,

  type MerchantOpenedMessage,

  type GoldUpdatedMessage,

  type NpcRespawnedMessage,

  type OpenMerchantMessage,

  type MerchantBuyMessage,

  type MerchantSellMessage,

  type SpellVendorOpenedMessage,

  type BuySpellMessage,

  type CloseVendorMessage,

  type ExchangeOpenedMessage,
  type ExchangeViewItemMessage,
  type ExchangeCreateOrderMessage,
  type ExchangeCancelOrderMessage,
  type ExchangeClaimAllMessage,

  type MarketplaceOpenedMessage,
  type OpenMarketplaceMessage,
  type MarketplaceSearchMessage,
  type MarketplacePublishMessage,
  type MarketplaceBuyMessage,
  type MarketplaceCancelMessage,
  type MarketplaceAppraiseMessage,
  type MarketplaceSaveSearchMessage,
  type MarketplaceDeleteSearchMessage,
  type MarketplaceDealsMessage,
  type ExchangeAddWatchMessage,
  type ExchangeRemoveWatchMessage,
  type MarketAlertsSeenMessage,
  type MarketAlertsMessage,
  type RequestTraderStatsMessage,

  type GroundItemsSyncMessage,

  type ItemTemplateListMessage,

  type InventorySyncMessage,

  type PlayAnimationMessage,
  type CharacterStatsMessage,
  type RequestCharacterStatsMessage,
  type RequestTileInfoMessage,
  type TileInfoMessage,
  type SkillTreeSyncMessage,
  type SkillNodeAllocatedMessage,
  type SkillTreeResetMessage,
  type AllocateSkillNodeMessage,
  type ResetSkillTreeMessage,
  type RequestSkillTreeMessage,
  type MovementConfirmedMessage,
  type PlayerTeleportedMessage,
  type PlayerMapChangeMessage,
  type PlayEntityFxMessage,
  type AreaEnteredMessage,
  type FactionStatsMessage,
  type FactionJoinPromptMessage,
  type FactionRankInfoMessage,
} from '@shared/types/messages';
import { MarketSide } from '@shared/types/market';
import type { MarketplaceFilters, WatchDirection } from '@shared/types/market';

import { Direction, EntityState } from '@shared/types/entity';
import { CharacterRaceId, CharacterSexId, getNakedBodyId } from '@shared/types/character';
import {
  type FactionId,
  MINIMAP_ALLY_RANK_INDEX,
  FACTIONS,
  getFactionColor,
  getFactionLabel,
  getRankByIndex,
  getNextRank,
} from '@shared/data/factions';
import { formatItemName } from '@shared/utils/items';
import { getAllItemTemplates, getItemTemplate, type ItemDefinition } from '@shared/data/items';
import { buildNpcDisplayInfo, getNPCTemplate } from '@shared/data/npcs';

import { BOSS_COLOR, DEFAULT_MAP_ID, GOLD_TEXT_COLOR, MAP_BORDER_SIZE, TILE_SIZE, WORLD_TILES, SITE_URL, AVAILABLE_BITMAP_FONTS, DEFAULT_BITMAP_FONTS, BITMAP_FONT_CONFIG, MONOGRAM_BMP_FONT, GAME_FONT_FAMILY, ENTITY_NAME_FONT_FAMILY, AVAILABLE_INVENTORY_FONTS, AVAILABLE_PLAYER_NAMES_FONTS, generateBitmapFontXML, ASSETS_BASE_URL, EquipmentSlot, ItemRarity, ITEM_RARITY_COLORS, MOVEMENT_CONFIG, VIEW_HALF_RANGE_X, VIEW_HALF_RANGE_Y, COMBAT_CONFIG, FOOTSTEP_BASE_DURATION_MS } from '@shared/constants';

import type { GameStateView, PlayerStateView, NPCStateView, SchemaMap, ResourceNodeStateView } from '../state/GameStateView';
import { ResourceNodeRenderer } from './ui/ResourceNodeRenderer';
import { CraftingStationRenderer, type CraftingStationStateView } from './ui/CraftingStationRenderer';
import { CraftingPanel } from './ui/CraftingPanel';
import { WorkProgressPanel } from './ui/WorkProgressPanel';
import { SkillsPanel } from './ui/SkillsPanel';
import { SkillXpFeed } from './ui/SkillXpFeed';
import { DebugSkillXpPanel } from './ui/DebugSkillXpPanel';
import { getSkillDef } from '@shared/data/skills';
import { getLevelFromTotalExp } from '@shared/data/experience';
import { RESOURCE_NODE_TYPES, getResourceNodeType } from '@shared/data/resourceNodes';

import type { MapData, TileData } from '@shared/types/map';

import { getAreasAtTile, classifyZoneType, type ZoneType } from '@shared/utils/areaUtils';
import { buildWaterIndex } from '@shared/utils/waterUtils';

import { getMap1Bordered } from '../boot/mapData';

import {
  preloadBodySprites,
  setupBodySpritesheets,
  createBodyAnimations,
  getBodyAnimKey,
  getBodyIdleFrame,
  getBodySpriteKey,
  getBodyFrameDimensions,
  BODY_COLUMNS,
  getBodyColumns,
  getBodyLateralColumns,
  WALK_ANIM_FRAME_RATE,
  getBodyCenterOffset,
  getAllExistingBodyIds,
} from '../sprites/BodySpriteManager';

import {
  preloadHeadSprites,
  getHeadSpriteKey,
  getHeadFrameForDirection,
  EXISTING_HEAD_IDS,
} from '../sprites/HeadSpriteManager';
import { preloadHelmetSprites, getHelmetSpriteKey, getHelmetFrameForDirection } from '../sprites/HelmetSpriteManager';
import { preloadBootSprites, getBootSpriteKey, getBootFrameForDirection } from '../sprites/BootSpriteManager';
import {
  preloadFootsteps,
  getFootstepMode,
  getDirectionalFootstepFrames,
  getAnimationFootstepFrames,
  getDirectionalFootstepAnimKey,
  getAnimationFootstepAnimKey,
  isFootstepSetEmpty,
  resolveFootstepId,
  FOOTSTEP_SHEET_KEY,
  DEFAULT_PLAYER_FOOTSTEP_ID,
} from '../sprites/FootstepSpriteManager';
import { ICON_ATLASES, validateIconAtlases, formatThousands, getNpcTypeIconName, getIconTexture } from '../sprites/IconAtlases';
import { IconAtlasViewerPanel } from './ui/IconAtlasViewerPanel';
import { cursorCss, domDragCursorCss, type GameCursor } from '../ui/cursors';
import { createSlider, type SliderControl } from '../ui/formControls';
import { type UiButton } from '../ui/uiButton';

import {
  preloadShieldSprites,

  getShieldSpriteKey,

  getShieldIdleFrame,
  getShieldFrameFromBodyIndex,
  createShieldAnimations,
} from '../sprites/ShieldSpriteManager';

import {
  preloadWeaponSprites,

  getWeaponSpriteKey,

  getWeaponIdleFrame,
  getWeaponFrameFromBodyIndex,
  getWeaponSheetOffsetForTextureKey,
  createWeaponAnimations,
} from '../sprites/WeaponSpriteManager';

import { InventoryPanel } from './ui/InventoryPanel';
import { CharacterInfoPanel } from './ui/CharacterInfoPanel';
import { PassiveTreePanel } from './ui/PassiveTreePanel';
import { SystemConsole } from './ui/SystemConsole';
import { Minimap, type MinimapNpc, type MinimapEntity } from './ui/Minimap';
import { InvokePanel } from './ui/InvokePanel';
import { ItemPanel } from './ui/ItemPanel';
import { NpcInfoPanel } from './ui/NpcInfoPanel';
import { MerchantPanel } from './ui/MerchantPanel';
import { ExchangePanel } from './ui/ExchangePanel';
import { MarketplacePanel } from './ui/MarketplacePanel';
import { QuestUI } from './ui/QuestUI';
import { SpellVendorPanel } from './ui/SpellVendorPanel';
import { AnimPanel } from './ui/AnimPanel';
import { ParticlePanel } from './ui/ParticlePanel';
import { ParticleEmitter, PARTICLE_SHEET } from '../systems/ParticleSystem';
import { v2ToV1 } from './particleEditor/v1v2Convert';
import type { ParticleEffectDefV2 } from '@shared/types/particles';
import { ParticleManager, type EmitterHandle } from '../systems/ParticleManager';
import { PlayerSoul, soulPaletteForFaction } from '../systems/PlayerSoul';
import type {
  EntityAnchorSlot,
  EntityRenderable,
  ItemGroundRenderable,
} from '../systems/AnchorResolver';
import { getParticleEffect } from '@shared/data/particleEffects';
import { PASSIVE_TREE_DATA } from '@shared/data/passiveTreeData';
import { getItemTemplateByVisualId } from '@shared/data/items';
import { BodyGalleryPanel } from './ui/BodyGalleryPanel';
import { WeaponGalleryPanel } from './ui/WeaponGalleryPanel';
import { CharFrameDebugPanel } from './ui/CharFrameDebugPanel';
import type { LocalPlayerSpriteKeys } from './ui/CharFrameDebugPanel';
import { BuffBar } from './ui/BuffBar';
import { SpellsPanel } from './ui/SpellsPanel';
import { SpellQuickBar } from './ui/SpellQuickBar';
import { TargetPanel } from './ui/TargetPanel';
import { TargetController } from './game/TargetController';
import { FootstepSystem } from './game/FootstepSystem';
import { AudioManager } from '../audio/AudioManager';
import { getAudioManager } from '../audio/sharedAudio';
import { AudioWorldController } from './game/AudioWorldController';
import { SoundBrowserPanel } from './ui/SoundBrowserPanel';
import { TextureBrowserPanel } from './ui/TextureBrowserPanel';
import { HUDController } from './game/HUDController';
import { ShadowSystem } from './game/ShadowSystem';
import { AnimationPanel } from './game/AnimationPanel';
import { FactionUI } from './game/FactionUI';
import { NpcInfoController } from './game/NpcInfoController';
import { EntityLightSystem } from './game/EntityLightSystem';
import { MarketNetwork } from './game/MarketNetwork';
import { ItemPanelController } from './game/ItemPanelController';
import { VendorPanelController } from './game/VendorPanelController';
import { OverheadTextController } from './game/OverheadTextController';
import { ParticleFxController } from './game/ParticleFxController';
import { FullscreenController } from './game/FullscreenController';
import { GroundItemsController } from './game/GroundItemsController';
import { MenuController, type SoundControls } from './game/MenuController';
import { ReconnectionController } from './game/ReconnectionController';
import { EntityVfxController } from './game/EntityVfxController';
import { EntityLabelController } from './game/EntityLabelController';
import { LightingDebugController } from './game/LightingDebugController';
import { MapTileRenderer } from './game/MapTileRenderer';
import { LoadingScreenController } from './game/LoadingScreenController';
import { ChatUiController } from './game/ChatUiController';
import { DevPanelsController } from './game/DevPanelsController';
import { MapLoader } from './game/MapLoader';
import { MapAmbientController } from './game/MapAmbientController';
import { CameraController } from './game/CameraController';
import { PlayerSyncController } from './game/PlayerSyncController';
import { NpcSyncController } from './game/NpcSyncController';
import { MovementController } from './game/MovementController';
import { InteractionController } from './game/InteractionController';
import { InvokeController } from './game/InvokeController';
import { WorldMessageHandlers } from './game/WorldMessageHandlers';
import { EntityStateVfxController } from './game/EntityStateVfxController';
import { CastVisualsController } from './game/CastVisualsController';
import { CursorController } from './game/CursorController';
import { SpellTargetingController } from './game/SpellTargetingController';
import { LayoutController } from './game/LayoutController';
import { PersistenceController } from './game/PersistenceController';
import { PassiveTreeUiController } from './game/PassiveTreeUiController';
import { TileHighlightController } from './game/TileHighlightController';
import { LoadingBar } from './ui/LoadingBar';
import {
  LOADING_LAYOUT,
  loadingLogoWidthPx,
  loadingBarWidthPx,
} from '../ui/loadingLayout';
import { MapOverlay } from './ui/MapOverlay';
import { getSpellDefinition, getAllSpellDefinitions, BUFF_EXPIRE_FX_BY_ACTIVE } from '@shared/data/spells';
import { SUMMON_MODE_LABEL_ES, type SummonMode } from '@shared/types/summon';
import { getTalkOffset } from '@shared/types/bodyConfig';

import { ACTION_KEY_IDS, ActionKeyId, InputManager, SavedKeyBinding, createDefaultKeyBindings } from '../input/InputManager';
import { registerServerMessageHandlers } from '../network/NetworkSystem';
import { PartyController } from './game/PartyController';
import { InputSystem } from '../systems/InputSystem';
import { MapDebugGridSystem } from '../systems/MapDebugGridSystem';
import { TimeOfDayManager } from '../systems/TimeOfDayManager';
import { LightingSystem, type LightConfig } from '../systems/LightingSystem';

import {
  preloadAnimationSprites,

  createAnimations,

  playAnimationAt,

  type AnimationDefinition,
} from '../sprites/AnimationSpriteManager';

import {
  preloadSurfaceAtlases,
  setupSurfaceTextures,
  getSurfaceTextureKey,
  isSurfaceTextureAvailable,
  getSurfaceById,
} from '../sprites/SurfaceSpriteManager';
import {
  preloadWaterFrames,
  getWaterTypeForSprite,
  getWaterFrameTextureKey,
  getWaterFrameCount,
  WATER_FRAME_MS,
} from '../sprites/WaterSpriteManager';
import { WaterPipeline, WATER_PIPELINE_KEY } from '../sprites/WaterPipeline';
import { InvisibilityPipeline, INVIS_PIPELINE_KEY } from '../sprites/InvisibilityPipeline';

/**
 * Elige el slot de anchor más adecuado para previewar un FX sobre el local
 * player (panel 🎆 + broadcast PLAY_ENTITY_FX). Se basa en los tags de la
 * def:
 *  - "weapon" → weapon_tip (para que clipToAnchorSprite enmascare al arma).
 *  - "shield" → shield_front.
 *  - "helm" / "head" → helm_top.
 *  - "feet"  → feet (auras de pies).
 *  - default → center (mitad del body).
 */
export function pickPreviewSlotForFx(def: ParticleEffectDefV2): EntityAnchorSlot {
  const tags = def.tags ?? [];
  if (tags.includes('weapon')) return 'weapon_tip';
  if (tags.includes('shield')) return 'shield_front';
  if (tags.includes('helm') || tags.includes('head')) return 'helm_top';
  if (tags.includes('feet')) return 'feet';
  return 'center';
}

export interface GameSceneConfig {
  serverUrl: string;

  characterId: string;

  /** Opcional: mapa al que conectarse (default DEFAULT_MAP_ID). Lo usa translado cross-map vía scene.restart(). */
  mapId?: number;

  /** Opcional: animación de llegada (default 1). Lo usa translado cross-map. */
  arrivalAnimationId?: number;

  /** Opcional: spawn aleatorio entre 9 candidatos en el mapa destino. Lo usa translado cross-map con randomizeArrival. */
  randomizeArrival?: boolean;

  /**
   * True cuando esta entrada a la room viene de un translado cross-map (no de un
   * login inicial). El server lo usa para NO otorgar protección de aparición en
   * translados. Ver docs/SPAWN_PROTECTION.md.
   */
  isMapChange?: boolean;
}

export interface ActiveAnimation {
  sprite: Phaser.GameObjects.Sprite;

  back: boolean; // Si está detrás del personaje

  offsetY: number; // Offset vertical
}

/**
 * Sombra proyectada (cast shadow): un "gemelo" negro por cada capa visible del
 * personaje (body/head/weapon/shield/helm/boots), tirado en el piso con una
 * transformación afín (rotación + achatado) derivada de la posición del sol
 * (`shadowAngle`/`shadowLength`). Reconstruye la silueta COMPLETA inclinada,
 * a diferencia del `dropshadow` que es solo un óvalo de contacto. Los gemelos
 * se crean lazy en `updateCastShadow` según qué capa real exista en el frame
 * (robusto a equipar/desequipar). Ver SPRITE_SYSTEM.md.
 */
export interface CastShadowLayers {
  body: Phaser.GameObjects.Sprite | null;
  head: Phaser.GameObjects.Sprite | null;
  weapon: Phaser.GameObjects.Sprite | null;
  shield: Phaser.GameObjects.Sprite | null;
  helm: Phaser.GameObjects.Sprite | null;
  boots: Phaser.GameObjects.Sprite | null;
}

export interface PlayerRenderObjects {
  sprite: Phaser.GameObjects.Sprite; // Body (naked o armadura, según bodyId del servidor)

  // Sombra proyectada multi-capa (silueta inclinada según la hora). Ver CastShadowLayers.
  castShadow: CastShadowLayers | null;

  // Simple elliptical shadow below entity feet (sistema viejo, inerte: SHADOWS_ENABLED=false)
  shadow: Phaser.GameObjects.Sprite | null;

  // Dropshadow independiente (escalado según ancho del body)
  dropshadow: Phaser.GameObjects.Sprite | null;

  // Fade (0..1) de las sombras: 1 = sombra plena (afuera), 0 = sin sombra (bajo techo/casa).
  // Se eased por frame hacia el target según si la entidad está en un área `interior`.
  shadowFade: number;

  head: Phaser.GameObjects.Sprite | null;

  helm: Phaser.GameObjects.Sprite | null; // Casco (se dibuja encima de la cabeza)

  boots: Phaser.GameObjects.Sprite | null; // Botas (se dibuja encima del body)

  label: Phaser.GameObjects.Text | Phaser.GameObjects.BitmapText; // Texto normal o bitmap font

  arrow: Phaser.GameObjects.Sprite;

  shield: Phaser.GameObjects.Sprite | null;

  weapon: Phaser.GameObjects.Sprite | null;

  // Target invisible para la cámara. Se mantiene en el centro del tile
  // para evitar saltos de cámara cuando cambia el body (todos los bodies ahora sin offset).
  cameraTarget: Phaser.GameObjects.Zone | null;

  // ID del body actual (puede cambiar cuando equipa/desequipa armadura)
  bodyId: number;
  // Offset X para centrar el body (varía según el ancho del frame)
  bodyCenterOffsetX: number;

  // Animaciones activas sobre este personaje

  activeAnimations: ActiveAnimation[];

  // Tracking de movimiento en tiles (para animación y lógica)

  lastTileX: number;

  lastTileY: number;

  // Sistema de interpolación basada en tiempo (time-based interpolation)
  // Esto garantiza movimiento perfectamente lineal sin importar el framerate
  currentWorldX: number;
  currentWorldY: number;
  targetWorldX: number;
  targetWorldY: number;
  
  // Posición inicial del movimiento (para interpolación)
  startWorldX: number;
  startWorldY: number;
  
  // Tiempo de inicio y duración del movimiento
  moveStartTime: number;
  moveDuration: number;
  
  // Delta time acumulado para interpolación (estilo argentumonline.io)
  moveElapsed: number;
  
  // Velocidad de movimiento (para compatibilidad y cálculo de duración)
  moveSpeed: number;

  // Marca si ya confirmamos que llegamos al centro del tile actual antes de girar
  cornerReady: boolean;

  // Cola de pasos discretos (tile por tile) enviados por el servidor
  stepQueue: Array<{ x: number; y: number; dir: Direction }>;

  // Última posición conocida del servidor en tiles
  serverTileX: number;
  serverTileY: number;

  // Eje activo durante la interpolación para evitar alternar entre ejes y simular diagonal
  activeAxis: 'x' | 'y' | null;

  lastUpdateTime: number; // tiempo del último update del servidor

  // Tiempo mínimo hasta el que debe seguir la animación de caminata

  walkUntil: number;

  // Dirección usada para renderizar (puede diferir momentáneamente de la reportada por el servidor cuando se dobla una esquina)
  visualDirection: Direction;

  // Dirección pendiente de aplicar al completar la esquina (vertical ↔ horizontal)
  pendingDirection: Direction | null;

  // Para detectar movimiento continuo

  lastDirection: Direction | null;

  isMoving: boolean;

  // Offset DETERMINÍSTICO (hash del id) para la fase de respiración: escalona las entidades y hace
  // que todos los clientes vean la misma animación para la misma entidad (ver util/hash.ts).
  breathingOffset: number;

  // Momento (scene.time.now) en que la entidad empezó a estar idle; null si se está moviendo. El
  // jugador respira sólo tras 5 s idle (gate). Ver PlayerSyncController §BREATHING.
  idleSince: number | null;

  // Tracking de HP/Mana previos para detectar ganancias
  previousHp: number | null;
  previousMana: number | null;

  // "Alma" de partículas que reemplaza el render del personaje al morir
  // (ver updatePlayerSoulVisuals). null mientras está vivo.
  soul: PlayerSoul | null;
}

export interface NPCRenderObjects {
  sprite: Phaser.GameObjects.Sprite;

  // Sombra proyectada multi-capa (silueta inclinada según la hora). Ver CastShadowLayers.
  castShadow: CastShadowLayers | null;

  // Simple elliptical shadow below NPC feet (sistema viejo, inerte: SHADOWS_ENABLED=false)
  shadow: Phaser.GameObjects.Sprite | null;

  // Dropshadow independiente (escalado según ancho del body)
  dropshadow: Phaser.GameObjects.Sprite | null;

  // Fade (0..1) de las sombras: 1 = sombra plena (afuera), 0 = sin sombra (bajo techo/casa).
  // Se eased por frame hacia el target según si la entidad está en un área `interior`.
  shadowFade: number;

  head: Phaser.GameObjects.Sprite | null;

  shield: Phaser.GameObjects.Sprite | null;

  weapon: Phaser.GameObjects.Sprite | null;

  label: Phaser.GameObjects.Text | Phaser.GameObjects.BitmapText; // Texto normal o bitmap font

  // Ícono del tipo de NPC (vendor/priest/...), a la izquierda del nombre. null si
  // el NPC no tiene tipo con ícono. Espeja posición/alpha/visible del label cada frame.
  typeIcon?: Phaser.GameObjects.Sprite | null;

  tileZone?: Phaser.GameObjects.Zone; // Zona invisible para detectar hover sobre el tile

  // True mientras el cursor está sobre el tileZone del NPC. updateNpcNameVisibility
  // (única autoridad per-frame de visibilidad de nombres) lo usa para decidir el label.
  nameHovered: boolean;

  // True si el template del NPC es hostil (getNPCTemplate(...).hostile). Decide qué modo
  // de visibilidad de nombre aplica (neutral vs hostil) en updateNpcNameVisibility.
  hostile: boolean;

  bodyId: number; // Para calcular talkOffset

  lastTileX: number;

  lastTileY: number;

  walkUntil: number;

  // Sistema de interpolación basada en tiempo (igual que jugadores)
  currentWorldX: number;
  currentWorldY: number;
  targetWorldX: number;
  targetWorldY: number;
  startWorldX: number;
  startWorldY: number;
  moveStartTime: number;
  moveDuration: number;
  moveSpeed: number;

  // Offset DETERMINÍSTICO (hash del id) para la fase de respiración: sincroniza la animación de la
  // misma entidad entre clientes y escalona entidades distintas (ver util/hash.ts).
  breathingOffset: number;

  // Tracking de HP/Mana previos para detectar ganancias
  previousHp: number | null;
  previousMana: number | null;
}

export interface SavedClientOptions {
  showGrid: boolean;
  showGridCoords: boolean;
  npcNameVisibilityMode: 'always' | 'hover';
  hostileNpcNameVisibilityMode?: 'always' | 'hover';

  showBorderBlocks?: boolean;

  showFloorBlocks?: boolean;

  showPlayerTile?: boolean;

  inventoryPanelX?: number;

  inventoryPanelY?: number;

  characterInfoPanelX?: number;

  characterInfoPanelY?: number;

  invokePanelX?: number;

  invokePanelY?: number;

  animPanelX?: number;

  animPanelY?: number;

  particlePanelX?: number;

  particlePanelY?: number;

  itemPanelX?: number;

  itemPanelY?: number;

  bodyGalleryPanelX?: number;

  bodyGalleryPanelY?: number;

  weaponGalleryPanelX?: number;

  weaponGalleryPanelY?: number;

  charFramePanelX?: number;

  charFramePanelY?: number;

  systemConsoleX?: number;

  systemConsoleY?: number;

  systemConsoleWidth?: number;

  systemConsoleHeight?: number;

  hudScale?: number;
  keyBindings?: Partial<Record<ActionKeyId, SavedKeyBinding>>;
  
  // Selección de fuentes bitmap
  uiFont?: string; // ID de la fuente para UI

  // Selección de fuente para números de inventario (cantidad y símbolo de equipado)
  inventoryFont?: string; // ID de la fuente de Google Fonts para números de inventario (ej: 'nunito-sans', 'montserrat')
  
  // Selección de fuente para nombres de personajes (jugadores)
  playerNamesFont?: string; // ID de la fuente de Google Fonts para nombres de personajes (ej: 'nunito-sans', 'montserrat')
  
  // Mostrar flecha de heading
  showHeadingArrow?: boolean;

  // Target panel (target lock system)
  targetPanelX?: number;
  targetPanelY?: number;
  targetPanelWidth?: number;
  targetPanelHeight?: number;

  // Auto-retarget al morir el target (default true)
  // TODO: exponer en options panel cuando exista
  autoRetargetOnDeath?: boolean;

  // Configuración de audio (volumen maestro + por categoría + toggles). Ver docs/SOUND_AND_MUSIC.md.
  audio?: SavedAudioConfig;
}

/**
 * Config de audio persistida (subset serializable de AudioConfig). `categories` mapea cada
 * `SoundCategory` a su { enabled, volume }.
 */
export interface SavedAudioConfig {
  masterVolume: number;
  categories: Record<string, { enabled: boolean; volume: number }>;
  musicLowHpSpeedup: boolean;
}

export const CLIENT_OPTIONS_STORAGE_KEY = 'aureum_game_options_v1';

/**
 * Height formula for all overhead text (chat, spell words, errors, NPC dialogue).
 * Returns the Y position above a character's head.
 * Cast bar uses this too but with a small extra offset.
 */
export function getOverheadTextY(spriteY: number, displayHeight: number, talkOffset: number = 0): number {
  return spriteY - displayHeight + 10 - talkOffset;
}

export class GameScene extends Phaser.Scene {
  // Tamaño lógico del mapa en tiles (debug grid)

  public static readonly WORLD_TILES_X = WORLD_TILES;

  public static readonly WORLD_TILES_Y = WORLD_TILES;

  // Offset base (pequeño) para profundidades de entidades (body/head/weapon/shield)

  // respecto al mapa. Se mantiene menor que la diferencia de depth entre tiles

  // para que el orden "abajo delante / arriba detrás" funcione correctamente.
  // Offset 0.5 ensures entities are in FRONT of ground_deco_over (0.4) when on same tile.
  // Y igual o mayor = entidad adelante de la decoración
  private static readonly ENTITY_DEPTH_OFFSET = 0.5;
  public static readonly LABEL_DEPTH_DELTA = 0.03;
  /**
   * Rango de desempate por X para decoraciones dinámicas (deco_over). Dos árboles en la
   * MISMA fila (mismo tileY) comparten depth exacto (rowDepth + 0.4); si se solapan, su
   * orden de pintado queda ambiguo y al re-ordenarse la display-list cada frame pueden
   * alternar cuál queda adelante (z-fight → "titilan"). Sumamos un epsilon determinístico
   * proporcional a X (normalizado, máx ~0.08) para que nunca empaten y el orden sea estable.
   * 0.08 < 0.1 = margen entre el offset de deco (0.4) y el de entidades (0.5), así la
   * decoración sigue quedando por detrás de la entidad en el mismo tile.
   */
  private static readonly DECO_X_TIEBREAK_RANGE = 0.08;

  // Configuración de movimiento desde shared/constants (fuente única de verdad)
  // Esto garantiza que cliente y servidor usen exactamente los mismos tiempos
  public static readonly SERVER_STEP_MS = MOVEMENT_CONFIG.STEP_DURATION_MS;
  
  // Velocidad de movimiento en píxeles por milisegundo (derivada de la constante compartida)
  public static readonly PLAYER_MOVE_SPEED = MOVEMENT_CONFIG.PIXELS_PER_MS;
  
  // Máximo de pasos en cola para absorber latencia sin acumular demasiados inputs
  public static readonly MAX_STEP_QUEUE = MOVEMENT_CONFIG.MAX_STEP_QUEUE;

  // Duración de interpolación para entidades remotas (jugadores y NPCs).
  // Igual al step del servidor — la fluidez viene del stepQueue y la protección IDLE.
  public static readonly REMOTE_STEP_MS = MOVEMENT_CONFIG.STEP_DURATION_MS;

  // Gracia (ms) que la animación de walk del jugador local sobrevive después del
  // fin visual de un step, para puentear el micro-gap inter-step en caminata
  // continua (el rate-limit del siguiente step puede caer 1-2 frames después del
  // fin de la interpolación). < 1 frame de anim (9fps≈111ms): imperceptible al
  // frenar, pero suficiente para evitar el reset del ciclo. Tunable.
  public static readonly WALK_ANIM_GRACE_MS = 100;
  
  // Delta time del frame actual (cappeado a 50ms), usado para interpolación
  public currentDelta = 0;
  
  /**
   * Obtiene la duración efectiva del movimiento para el jugador local.
   * Considera el moveSpeed del jugador (si tiene buffs de velocidad, etc.)
   * 
   * IMPORTANTE: Esta duración debe calcularse AL INICIO del movimiento
   * y no modificarse durante la interpolación.
   */
  public getLocalPlayerMoveDuration(): number {
    const localPlayer = this.localSessionId 
      ? this.room?.state.players.get(this.localSessionId) 
      : null;
    const moveSpeed = (localPlayer as any)?.moveSpeed ?? 1.0;
    return moveSpeed > 0 
      ? GameScene.SERVER_STEP_MS / moveSpeed 
      : GameScene.SERVER_STEP_MS;
  }

  // =====================================================================
  // RENDERING FIX: Fixed timestep for consistent interpolation.
  // Using a fixed step prevents jittery movement under variable FPS.
  // We accumulate time and process physics in fixed increments.
  // 16.67ms = 60 FPS equivalent fixed step
  // =====================================================================
  private static readonly FIXED_TIMESTEP_MS = 16.67; // Fixed step duration in ms
  private static readonly MAX_ACCUMULATED_TIME_MS = 100; // Cap to prevent spiral of death

  // Opciones de escala del HUD (match con los labels del slider)
  // Las etiquetas muestran valores relativos, pero los valores numéricos reales son diferentes
  // x0.5 = 1.0, x1 = 2.0 (base), x1.5 = 3.0, x2 = 4.0

  public static readonly HUD_SCALE_VALUES = [1.0, 2.0, 3.0, 4.0];

  public static readonly HUD_SCALE_LABELS = ['x0.5', 'x1', 'x1.5', 'x2'];

  // Offsets visuales para sprites (en píxeles de mundo)

  // Body: se dibuja unos píxeles más arriba del centro del tile
  // Ajustado para frames de 64px de alto (cuerpos desnudos)
  public static readonly BODY_OFFSET_Y = -26;

  // Offsets de equipamiento respecto al eje X del esqueleto (body centrado)
  public static readonly SHIELD_OFFSET_X = 0;
  // Offset global del arma sobre el personaje. Mismo punto de partida para
  // TODAS las armas y direcciones (los sheets están recentrados por celda).
  // Tunable: ajustar acá mueve todas las armas por igual.
  // El ajuste fino por arma se hace POR SHEET en WEAPON_SHEET_OFFSETS
  // (WeaponSpriteManager.ts), porque el corrimiento de la "mano" depende del
  // canvas con el que se autoró cada sheet, no es global.
  public static readonly WEAPON_OFFSET_X = 0; // derecha (+X) en todas las direcciones
  public static readonly WEAPON_OFFSET_Y = 0; // +Y = baja
  // Delta X extra para las direcciones LATERALES (izq/der), que necesitan estar
  // un poco más a la derecha que los frames verticales (abajo/arriba).
  public static readonly WEAPON_LATERAL_DX = 0;

  /** Offset X del arma según dirección (laterales corridos `WEAPON_LATERAL_DX`). */
  public static weaponOffsetX(dir: Direction): number {
    const lateral = dir === Direction.LEFT || dir === Direction.RIGHT;
    return GameScene.WEAPON_OFFSET_X + (lateral ? GameScene.WEAPON_LATERAL_DX : 0);
  }

  /**
   * Obtiene el offset X necesario para centrar un body.
   * El offset se calcula automáticamente en BodySpriteManager analizando los píxeles.
   */
  public static getBodyCenterOffsetX(bodyId: number): number {
    return getBodyCenterOffset(bodyId);
  }

  // Alturas de frame (de los spritesheets reales)
  // Cuerpos desnudos (body1-body8): 258x256 = 43x64 por frame
  // Armaduras (body11+): 192x208 = 32x52 por frame
  public static readonly BODY_FRAME_HEIGHT = 64; // Referencia del naked body más grande
  
  /**
   * Calcula el offset Y para un body dado, alineando los pies con el naked body de referencia.
   * Todos los bodies usan origin(0.5, 0.5), así que el centro del sprite está en (x, y).
   * Para alinear pies: pieNaked = bodyY + nakedHeight/2, pieArmor = armorY + armorHeight/2
   * Si queremos pieArmor == pieNaked: armorY = bodyY + (nakedHeight - armorHeight) / 2
   */

  public static readonly HEAD_FRAME_HEIGHT = 30;

  // Configuración de zoom
  private static readonly ZOOM_DEFAULT = 1;
  private static readonly ZOOM_MIN = 0.5;
  private static readonly ZOOM_MAX = 8;
  private static readonly ZOOM_STEP = 0.1;
  private static readonly ZOOM_INDICATOR_FADE_DURATION = 300; // ms para fade in/out
  private static readonly ZOOM_INDICATOR_DISPLAY_TIME = 800; // ms que se muestra antes de fade out
  // Master toggle del zoom con rueda. Se combina con el requisito de Ctrl:
  // zoomea solo si está enable Y Ctrl apretado.
  private enableWheelZoom = true;


  // Offset vertical de la head respecto a los pies del body (en píxeles, positivo = hacia arriba)
  // Se guarda en positivo para evitar confusiones con el signo en los cálculos.
  public static readonly HEAD_OFFSET_FROM_BODY_BOTTOM = 27;

  // ==========================================================================
  // SHADOW CONFIGURATION (currently disabled)
  // ==========================================================================
  public static readonly SHADOWS_ENABLED = false;      // Shadows disabled for now
  public static readonly SHADOW_TEXTURE_KEY = 'entity_shadow';
  public static readonly SHADOW_TEXTURE_WIDTH = 48;
  public static readonly SHADOW_TEXTURE_HEIGHT = 24;
  private static readonly SHADOW_ALPHA = 0.5;
  private static readonly SHADOW_SCALE_X = 0.7;
  private static readonly SHADOW_SCALE_Y = 0.7;
  private static readonly SHADOW_OFFSET_Y = 0;
  public static readonly SHADOW_DEPTH_DELTA = -0.03;
  
  // ==========================================================================
  // ENTITY LIGHT CONFIGURATION
  // ==========================================================================
  public static readonly ENTITY_LIGHT_RADIUS = 150;    // Large radius for soft, ambient light
  public static readonly ENTITY_LIGHT_INTENSITY = 0.65; // Reduced intensity for less illumination
  public static readonly ENTITY_LIGHT_COLOR = 0xFFEECC; // Warm white light
  public static readonly ENTITY_LIGHT_OFFSET_Y = -24;  // Light emanates from entity center

  // ==========================================================================
  // DROPSHADOW CONFIGURATION (independent from shadow system)
  // ==========================================================================
  public static readonly DROPSHADOW_TEXTURE_KEY = 'dropshadow';
  public static readonly DROPSHADOW_TEXTURE_WIDTH = 64;  // Ancho real de dropshadow.png
  private static readonly DROPSHADOW_TEXTURE_HEIGHT = 32; // Alto real de dropshadow.png
  public static readonly DROPSHADOW_ALPHA = 0.6;
  public static readonly DROPSHADOW_OFFSET_Y = 0; // Posicionado en los pies
  /**
   * Depth FIJO del dropshadow: una sombra de piso es un decal de suelo y nunca debe
   * dibujarse por encima de una decoración (árboles, columnas). La banda de deco_over
   * va de ~0.4 a ~700, así que un depth fijo por debajo de 0.4 garantiza que la sombra
   * quede SIEMPRE bajo cualquier árbol (sin importar su fila), pero por encima del suelo
   * (ground -200) y los floor_objects/caminos (-100) para que siga siendo visible.
   * Antes el dropshadow usaba un depth relativo a la fila de la entidad (rowDepth + 0.47),
   * que superaba al del árbol (rowDepth + 0.4) y lo oscurecía/titilaba al moverse.
   */
  public static readonly DROPSHADOW_GROUND_DEPTH = -1;

  // ==========================================================================
  // FOOTSTEPS (huellas cosméticas al caminar)
  // ==========================================================================
  /**
   * Depth de las huellas: en el piso (por encima de `ground` -200 y de los
   * floor_objects/caminos -100) pero por DEBAJO del dropshadow (-1) y la cast
   * shadow (-1.1), para que la sombra del personaje las tape.
   */
  public static readonly FOOTSTEP_GROUND_DEPTH = -2;
  /** Duración de la animación de aparición de una huella direccional (3 frames). */
  public static readonly FOOTSTEP_ANIM_MS = 250;
  /** Duración del fade-out final antes de destruir una huella direccional. */
  public static readonly FOOTSTEP_FADE_MS = 500;
  /** Alpha inicial de la huella (tenue, es un decal de piso). */
  public static readonly FOOTSTEP_ALPHA = 0.7;
  /**
   * Offset (px) de cada pie respecto del centro del tile, PERPENDICULAR a la
   * dirección de avance. Las huellas direccionales se marcan de a dos (izq/der).
   */
  public static readonly FOOTSTEP_FOOT_OFFSET = 6;
  /** FrameRate de la animación de 12 frames de los footsteps de tipo splash. */
  public static readonly FOOTSTEP_SPLASH_FRAME_RATE = 24;

  // ==========================================================================
  // CAST SHADOW CONFIGURATION (silueta proyectada multi-capa según la hora)
  // ==========================================================================
  public static readonly CAST_SHADOW_ENABLED = true;
  /**
   * Velocidad del fade de sombra al entrar/salir de una casa (por frame, estilo delta como
   * `interiorLevel`): 1 = sombra plena afuera, 0 = sin sombra bajo techo. Chico → suave.
   */
  public static readonly SHADOW_FADE_STEP = 0.15;
  /**
   * Depth fijo, apenas por debajo del dropshadow de contacto, para que la
   * proyección quede bajo decoraciones (árboles/columnas) y bajo el óvalo de
   * contacto, pero sobre el suelo. Mismo criterio que DROPSHADOW_GROUND_DEPTH.
   */
  public static readonly CAST_SHADOW_DEPTH = GameScene.DROPSHADOW_GROUND_DEPTH - 0.1;
  /** Inclinación máxima (rad) desde la vertical, en amanecer/atardecer (sombra larga). */
  public static readonly CAST_SHADOW_MAX_LEAN = 1.2;
  /** Achatado vertical al mediodía (sombra corta bajo los pies). */
  public static readonly CAST_SHADOW_MIN_SQUASH = 0.4;
  /** Estiramiento vertical en amanecer/atardecer (sombra a largo). */
  public static readonly CAST_SHADOW_MAX_STRETCH = 1.0;

  /**
   * Helper para crear textos con posiciones redondeadas automáticamente.
   * Evita el problema de subpixel rendering que causa textos borrosos o cortados.
   * Incluye la fuente del juego por defecto y configuración de resolución para textos nítidos.
   */
  public addText(
    x: number,
    y: number,
    text: string,
    style?: Phaser.Types.GameObjects.Text.TextStyle
  ): Phaser.GameObjects.Text {
    const defaultStyle = {
      // Pixel font del juego (Monogram, ver GAME_FONT_FAMILY). Misma familia que el
      // nombre del personaje (addPlayerNameText) para que todo el texto in-game sea
      // consistente. NO usar bold: Monogram tiene un solo peso y el faux-bold del
      // browser arruina la pixel font; la sombra estilo Argentum (1px, sin blur) ya
      // da cuerpo y contraste contra el mundo.
      fontFamily: this.getSelectedPlayerNamesFont(),
      resolution: 2, // Mayor resolución para textos nítidos (independiente de pixelArt)
      shadow: {
        offsetX: 1,
        offsetY: 1,
        color: '#000000',
        blur: 0,
        fill: true,
        stroke: false,
      },
      ...style,
    };
    const textObj = this.add.text(Math.round(x), Math.round(y), text, defaultStyle);
    // Asegurar que el texto use resolución alta incluso si no se especifica en el estilo
    if (!style?.resolution) {
      textObj.setResolution(2);
    }
    return textObj;
  }

  /**
   * Obtiene la fuente UI seleccionada (o la por defecto)
   */
  public getSelectedUIFont(): string {
    const options = this.persistence.loadClientOptions();
    return options?.uiFont ?? DEFAULT_BITMAP_FONTS.UI;
  }

  /**
   * Obtiene el tamaño de fuente para la fuente UI seleccionada
   */
  private getSelectedUIFontSize(): number {
    const fontId = this.getSelectedUIFont();
    const font = AVAILABLE_BITMAP_FONTS.find((f: { id: string; name: string; fontSize: number }) => f.id === fontId);
    return font?.fontSize ?? 12;
  }

  /**
   * Helper para crear textos usando bitmap font para UI (paneles, chat, inventario, etc.)
   * Las bitmap fonts se ven perfectamente nítidas y tienen espacios normalizados.
   * Usa el tamaño de fuente configurado para la fuente seleccionada.
   * Agrega un texto de sombra para mejor visibilidad.
   */
  private addBitmapTextUI(
    x: number,
    y: number,
    text: string,
    size?: number
  ): Phaser.GameObjects.BitmapText {
    const fontSize = size ?? this.getSelectedUIFontSize();
    const fontId = this.getSelectedUIFont();
    
    // Asegurar posiciones enteras para nitidez
    const roundedX = Math.round(x);
    const roundedY = Math.round(y);
    
    // Crear texto de sombra (offset 1px en cada dirección, color negro semi-transparente)
    const shadow = this.add.bitmapText(roundedX + 1, roundedY + 1, fontId, text, fontSize);
    shadow.setTint(0x000000);
    shadow.setAlpha(0.7);
    shadow.setOrigin(0, 0);
    shadow.setDepth(9999); // Detrás del texto principal
    // Asegurar posiciones enteras para nitidez
    shadow.setPosition(Math.round(shadow.x), Math.round(shadow.y));
    
    // Crear texto principal
    const mainText = this.add.bitmapText(roundedX, roundedY, fontId, text, fontSize);
    mainText.setOrigin(0, 0);
    mainText.setDepth(10000);
    // Asegurar posiciones enteras para nitidez
    mainText.setPosition(Math.round(mainText.x), Math.round(mainText.y));
    
    // Retornar el texto principal (la sombra se mantiene como objeto separado)
    return mainText;
  }


  /**
   * Fuente de todos los textos in-game (nombres de personajes/NPCs y, vía addText,
   * globos de habla, daño, magias, diálogos NPC, HUD, etc.).
   *
   * Ya NO es configurable: siempre devuelve la pixel font del juego (Monogram, ver
   * GAME_FONT_FAMILY). Se mantiene como método (en vez de usar la constante directo)
   * para no tocar los ~call sites que ya lo invocan.
   */
  public getSelectedPlayerNamesFont(): string {
    return GAME_FONT_FAMILY;
  }


  public configData: GameSceneConfig;

  /**
   * Gate de warmup. La escena arranca durante el login SIN characterId y hace
   * todo el trabajo pesado (texturas, animaciones, superficies, pre-render del
   * mapa) detrás del overlay de auth; `create()` espera esta promesa justo antes
   * de joinear la room. La resuelve `provideCharacter()` cuando el jugador elige
   * personaje. Si `init()` ya recibe un characterId (scene.restart de translado
   * cross-map, reconexión), se resuelve de inmediato (sin gate). Ver
   * docs/AUTH_AND_LOGIN.md.
   */
  private characterReady!: Promise<void>;
  private resolveCharacterReady?: () => void;
  /**
   * Personaje entregado por provideCharacter(). Sobrevive a `init()` (que reinicia
   * configData), así el gate es robusto aunque provideCharacter llegue antes de que
   * la escena haya corrido init (assets lentos): init lo relee y abre el gate.
   */
  private pendingCharacterId: string | null = null;

  public colyseusClient: ColyseusClient | null = null;

  public room: GameRoom | null = null;

  public currentMapId: number = DEFAULT_MAP_ID;

  public cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;

  public localSessionId: string | null = null;

  // Track whether grayscale postFX is currently applied to the game camera
  // (when local player is DEAD). Solo se toca en update() por transiciones de
  // state — para evitar re-aplicar el shader en cada frame.
  private deathGrayscaleApplied: boolean = false;

  public playerObjects: Map<string, PlayerRenderObjects> = new Map();

  // Target lock system (paradigma WoW, server-authoritative).
  // selectedTargetId es CACHE local del PlayerState.targetId del schema.
  // Sistema de target-lock extraído a scenes/game/TargetController.ts (posee el target
  // seleccionado + retícula + panel). GameScene delega vía this.targets.*.
  public targets: TargetController = new TargetController(this);
  // Huellas cosméticas al caminar, extraídas a scenes/game/FootstepSystem.ts.
  public footsteps: FootstepSystem = new FootstepSystem(this);
  // Motor de audio (WebAudio: buses, 3D, música, filtros). Instancia COMPARTIDA con el boot/login
  // (música de intro/createchar) para un handoff sin cortes. Ver docs/SOUND_AND_MUSIC.md.
  public audio: AudioManager = getAudioManager();
  // Controlador de audio del mundo (música por zona, ambiente, heartbeat, baja vida, muerte).
  public audioWorld: AudioWorldController = new AudioWorldController(this);
  // Visor de sonidos in-game (dev/admin): lista + reproducción local/broadcast. Ver docs/SOUND_AND_MUSIC.md.
  public soundBrowser: SoundBrowserPanel = new SoundBrowserPanel(this);
  // Visor de gráficos indexeados (dev): grilla de texturas de personaje/ítem + detalle. Ver docs/TEXTURE_BROWSER.md.
  public textureBrowser: TextureBrowserPanel = new TextureBrowserPanel(this);
  // HUD (globos HP/mana, XP, botones), extraído a scenes/game/HUDController.ts.
  public hud: HUDController = new HUDController(this);
  // Sombras proyectadas + dropshadow (cosmético), extraídas a scenes/game/ShadowSystem.ts.
  public shadows: ShadowSystem = new ShadowSystem(this);
  // Panel de dev de animaciones + handler PLAY_ANIMATION, extraído a scenes/game/AnimationPanel.ts.
  public animPanelCtl: AnimationPanel = new AnimationPanel(this);
  // UI de facciones (overlay/stats/rango), extra�da a scenes/game/FactionUI.ts.
  private factionUI: FactionUI = new FactionUI(this);
  // Panel de info de NPC (right-click), extra�do a scenes/game/NpcInfoController.ts.
  public npcInfo: NpcInfoController = new NpcInfoController(this);
  // Luz de entidad + textura de sombra legacy, extraídas a scenes/game/EntityLightSystem.ts.
  public entityLight: EntityLightSystem = new EntityLightSystem(this);
  // Red de la economía (Marketplace + Exchange), extraída a scenes/game/MarketNetwork.ts.
  public marketNet: MarketNetwork = new MarketNetwork(this);
  // Panel de dev de items (visor de templates), extraído a scenes/game/ItemPanelController.ts.
  public itemPanelCtl: ItemPanelController = new ItemPanelController(this);
  // Apertura/gestión de los 4 paneles de vendedor, extraída a scenes/game/VendorPanelController.ts.
  public vendorPanels: VendorPanelController = new VendorPanelController(this);
  // Texto flotante overhead (burbujas/daño/gain/palabras/error), extraído a game/OverheadTextController.ts.
  public overheadText: OverheadTextController = new OverheadTextController(this);
  // Partículas/FX cosméticos (panel dev + emitters + FX de entidad/melee), extraído a game/ParticleFxController.ts.
  public particleFx: ParticleFxController = new ParticleFxController(this);
  // Pantalla completa + reset de input por pérdida de foco, extraído a game/FullscreenController.ts.
  public fullscreen: FullscreenController = new FullscreenController(this);
  // Items en el piso (sync/render/pickup/reposición), extraído a game/GroundItemsController.ts.
  public groundItems: GroundItemsController = new GroundItemsController(this);
  // Menú principal in-game (⚙/Esc) + panel de Opciones, extraído a game/MenuController.ts.
  public menu: MenuController = new MenuController(this);
  // Reconexión + teardown de render, extraído a game/ReconnectionController.ts.
  public reconnect: ReconnectionController = new ReconnectionController(this);
  // VFX de emitters en entidades (auras/impair/equipped), extraído a game/EntityVfxController.ts.
  public entityVfx: EntityVfxController = new EntityVfxController(this);
  // Labels de nombre sobre entidades (players+NPCs), extraído a game/EntityLabelController.ts.
  public labels: EntityLabelController = new EntityLabelController(this);
  // Panel dev de iluminación/hora del día, extraído a game/LightingDebugController.ts.
  public lightingDebug: LightingDebugController = new LightingDebugController(this);
  // Render de tiles del mapa + viewport culling, extraído a game/MapTileRenderer.ts.
  public mapTiles: MapTileRenderer = new MapTileRenderer(this);
  // Pantalla de carga inicial (logo + barra), extraído a game/LoadingScreenController.ts.
  public loadingScreenUi: LoadingScreenController = new LoadingScreenController(this);
  // UI de chat (input + volcado a consola), extraído a game/ChatUiController.ts.
  public chat: ChatUiController = new ChatUiController(this);
  // Paneles dev/debug (invoke/galerías/charFrame) toggleables desde el menú, extraído a game/DevPanelsController.ts.
  public devPanels: DevPanelsController = new DevPanelsController(this);
  // Carga y montaje del mapa (assets + tiles + agua + translados + luces), extraído a game/MapLoader.ts.
  public mapLoader: MapLoader = new MapLoader(this);
  // Efectos ambientales de mapa (agua calm + roofs/interior), extraído a game/MapAmbientController.ts.
  public mapAmbient: MapAmbientController = new MapAmbientController(this);
  // Cámara + conversiones de coordenadas (world↔screen, follow), extraído a game/CameraController.ts.
  public cameraCtl: CameraController = new CameraController(this);
  // Sync de render de players (addOrUpdatePlayer + naked body + removePlayer), extraído a game/PlayerSyncController.ts.
  public playerSync: PlayerSyncController = new PlayerSyncController(this);
  // Sync de render de NPCs (addOrUpdateNPC + interpolación + removeNPC), extraído a game/NpcSyncController.ts.
  public npcSync: NpcSyncController = new NpcSyncController(this);
  // Movimiento del player local (prediction + reconciliación + teleport/mapchange), extraído a game/MovementController.ts.
  public movement: MovementController = new MovementController(this);
  // Interacción del mundo por puntero (click → spell/pickup/target), extraído a game/InteractionController.ts.
  public interaction: InteractionController = new InteractionController(this);
  // Panel de invocación de NPCs + preview de galerías (dev), extraído a game/InvokeController.ts.
  public invoke: InvokeController = new InvokeController(this);
  // Handlers de mensajes del mundo (npc dialogue/respawn, área/banner), extraído a game/WorldMessageHandlers.ts.
  public worldMsg: WorldMessageHandlers = new WorldMessageHandlers(this);
  // Visuales de estado de entidad (meditación/taunt/spawn-protection), extraído a game/EntityStateVfxController.ts.
  public stateVfx: EntityStateVfxController = new EntityStateVfxController(this);
  // Visuales de casteo (cast bars + boat bar + animación de casteo), extraído a game/CastVisualsController.ts.
  public castVisuals: CastVisualsController = new CastVisualsController(this);
  // Gestión del cursor del juego (setGameCursor + drag cursor), extraído a game/CursorController.ts.
  public cursor: CursorController = new CursorController(this);
  // Casteo de hechizos con targeting (send + enter/exit/preview), extraído a game/SpellTargetingController.ts.
  public spellCast: SpellTargetingController = new SpellTargetingController(this);
  // Layout responsivo de la UI (reposiciona HUD + consola al resize), extraído a game/LayoutController.ts.
  public layout: LayoutController = new LayoutController(this);
  // Persistencia de opciones del cliente en localStorage, extraido a game/PersistenceController.ts.
  public persistence: PersistenceController = new PersistenceController(this);
  // UI del Arbol de Espiritu (panel + sync), extraido a game/PassiveTreeUiController.ts.
  public passiveTreeUi: PassiveTreeUiController = new PassiveTreeUiController(this);
  // Resaltado del tile del jugador/server, extraido a game/TileHighlightController.ts.
  public tileHighlight: TileHighlightController = new TileHighlightController(this);

  // World map overlay (tecla M)
  public mapOverlay: MapOverlay | null = null;

  public npcObjects: Map<string, NPCRenderObjects> = new Map();

  // Time-based shadow projection (calculated from sun position)
  public shadowAngle = 0;        // Angle in radians (0 = right, PI/2 = down, PI = left)
  public shadowLength = 1.0;     // Length multiplier (longer at dawn/dusk, shorter at noon)
  public shadowAlpha = 0.5;      // Shadow opacity (varies with time)

  // Day/night cycle and lighting systems
  public timeOfDayManager: TimeOfDayManager | null = null;
  public lightingSystem: LightingSystem | null = null;

  // Opciones de debug de grilla (controladas desde el menú de Opciones -> Vídeo)

  public showGrid: boolean = false;

  public showGridCoords: boolean = false;

  public showHeadingArrow: boolean = false;

  private gridContainer: Phaser.GameObjects.Container | null = null;

  private gridGraphics: Phaser.GameObjects.Graphics | null = null;

  private gridLabelContainer: Phaser.GameObjects.Container | null = null;

  private gridLabelPool: Phaser.GameObjects.Text[] = [];

  private activeGridLabelCount = 0;

  private lastGridTileStartX: number | null = null;

  private lastGridTileStartY: number | null = null;

  // La cámara debe seguir un ancla estable (centro del tile), no el sprite del body
  // que puede cambiar su X por offsets al cambiar de body.
  public localPlayerFollowTarget: Phaser.GameObjects.GameObject | null = null;

  // Cámara UI separada (no afectada por zoom)
  private uiCamera: Phaser.Cameras.Scene2D.Camera | null = null;
  
  // AO-like: Posición del mundo del jugador local (para calcular offsets relativos)
  private localPlayerWorldX: number = 0;
  private localPlayerWorldY: number = 0;
  
  // AO-like: Contenedor para el tilemap (fijo, la cámara se mueve sobre él)
  public mapContainer: Phaser.GameObjects.Container | null = null;
  
  // Sprites del mapa para poder destruirlos al recargar
  private mapTileSprites: Phaser.GameObjects.Image[] = [];

  // ---------------------------------------------------------------------------
  // VIEWPORT CULLING DEL MAPA
  // ---------------------------------------------------------------------------
  // El mapa puede tener decenas de miles de tiles (mundo generado). En vez de
  // instanciar todos los sprites al cargar (inviable), guardamos las entradas
  // de tile y sólo creamos los GameObjects de los chunks visibles, creando y
  // destruyendo a medida que la cámara se mueve.
  /** Entradas de tile del mapa actual (dato, no GameObject). */
  public mapTileEntries: Array<{ tile: TileData; layerName: string }> = [];
  /** Set "x,y" de tiles de agua (para detectar costa y atenuar el shader). */
  public waterTileSet: Set<string> = new Set();
  /**
   * Set "bx,by" (origen de bloque) de bloques que son ORILLA: tienen tierra o costa
   * (pasto/arena) encima. Ojo: los bloques de costa tienen AGUA en la capa 1 (relleno
   * para el difuminado), así que están en waterTileSet; por eso el bajío/cyan del agua
   * se mide contra este set (la orilla real) y no contra "no-agua", que caería un
   * bloque tierra adentro. Ver waterCalmFactor.
   */
  public shoreBlockSet: Set<string> = new Set();
  /** chunkKey ("cx,cy") → índices en mapTileEntries. */
  public mapTileChunks: Map<string, number[]> = new Map();
  /** índice de entrada → GameObject vivo (imagen del tile). */
  public activeTileObjects: Map<number, Phaser.GameObjects.Image> = new Map();
  /**
   * Subconjunto de `activeTileObjects` que son TECHOS (capa roof), para el efecto de
   * interior de casa: cuando el player entra a un área `interior`, estos techos hacen
   * fade a casi invisible si su caja se solapa con el interior. Ver updateInteriorEffect.
   */
  public roofObjects: Map<number, Phaser.GameObjects.Image> = new Map();
  /** Nivel eased (0..1) del efecto de interior de casa que maneja los TECHOS. */
  public interiorLevel = 0;
  /**
   * Nivel eased (0..1) del OVERLAY de oscuridad. Igual que `interiorLevel` al entrar,
   * pero al salir se apaga un poquito más rápido: el techo "se ve" lleno antes (su
   * alpha llega casi a opaco temprano) y si el overlay se apagara a la misma velocidad
   * quedaría tenue colgado un instante después de que el techo ya está. Ver
   * updateInteriorEffect.
   */
  public overlayLevel = 0;
  /** Rects del interior actual en world px (union de los `rects` del área `interior`). */
  public interiorRevealRects: Array<{ x: number; y: number; w: number; h: number }> = [];
  /** Alpha al que se atenúa un techo cuando estás adentro de la casa. */
  public static readonly ROOF_DIM_ALPHA = 0.08;
  /**
   * Agua animada por BLOQUE (128px). índice de entrada (tile origen del bloque)
   * → { sprite, type }. El timer global cicla los frames en fase. Ver
   * WaterSpriteManager + docs/WORLD_BUILDER.md.
   */
  public waterSprites: Map<number, { sprite: Phaser.GameObjects.Image; type: string }> = new Map();
  /** Índice de frame global del agua (todos los bloques en fase). */
  public waterFrame = 0;
  /** Timer que avanza el frame de agua. */
  public waterAnimTimer: Phaser.Time.TimerEvent | null = null;
  /** Pipeline de agua "Seascape" (océano procedural). Se setea en create(). */
  public waterPipeline: WaterPipeline | null = null;
  /** Cloak de invisibilidad (Predator). Se setea en create(). Ver docs/INVISIBILITY.md. */
  public invisibilityPipeline: InvisibilityPipeline | null = null;
  /** Entidades con el cloak de invisibilidad aplicado (para restaurar al volver visible). */
  public invisibleEntities: Set<string> = new Set();
  /** chunks actualmente instanciados (o en cola de instanciación). */
  public activeMapChunks: Set<string> = new Set();
  /** Cola de índices de entrada pendientes de crear (amortización por frame). */
  public tileCreateQueue: number[] = [];
  /** Máximo de tiles a crear por frame (evita picos al cruzar chunks). */
  public static readonly MAP_TILES_PER_FRAME = 250;
  /** última posición/tamaño de viewport con la que se recalculó el culling. */
  public lastCullCamX = Number.NaN;
  public lastCullCamY = Number.NaN;
  public lastCullCamW = Number.NaN;
  public lastCullCamH = Number.NaN;
  /** Lado de un chunk de culling, en tiles. */
  public static readonly MAP_CHUNK_TILES = 16;
  /** Margen extra de tiles alrededor del viewport (cubre sprites altos/offset). */
  public static readonly MAP_CULL_MARGIN_TILES = 8;

  // Depth threshold para separar UI del juego (UI >= 800, juego < 800)
  private static readonly UI_DEPTH_THRESHOLD = 800;
  
  // Indicador de zoom
  private zoomIndicatorText: Phaser.GameObjects.Text | null = null;
  private zoomIndicatorTimer: Phaser.Time.TimerEvent | null = null;

  // Pantalla de carga

  public loadingScreen: {
    bg: Phaser.GameObjects.Rectangle;

    logo: Phaser.GameObjects.Image | Phaser.GameObjects.Text;

    shadow?: Phaser.GameObjects.Image;

    bar?: LoadingBar;
  } | null = null;

  public isLoadingComplete = false;
  /** True cuando las texturas de surface ya están horneadas y el mapa re-renderizado
   *  con ellas (o cuando no hay surfaces que cargar). Gatea el fade de la pantalla de
   *  carga para evitar el flash de pasto negro. */
  public surfacesReady = false;
  /** True cuando la "foto inicial" de entidades del AOI (players/NPCs/nodos que había al joinear)
   *  ya se creó por completo. El burst inicial NO se hace de golpe (congelaba el frame de "Entrar"):
   *  se encola en `initialEntityQueue` y se drena de a poco desde update(), todo BAJO la pantalla de
   *  carga. Gatea el fade junto a surfacesReady. Ver docs/AUTH_AND_LOGIN.md. */
  public entitiesPopulated = false;
  /** Cola de creadores de entidades de la foto inicial (todo menos el player local). Cada entrada
   *  crea una entidad; se drenan `ENTITY_DRAIN_PER_FRAME` por frame para repartir el costo. */
  public initialEntityQueue: Array<() => void> = [];
  private initialEntityQueueTotal = 0;
  /** Presupuesto de tiempo (ms) por frame para el drenado de la foto inicial. En vez de un conteo
   *  fijo, creamos entidades hasta gastar ~este tiempo por frame — así ningún frame del drenado se
   *  pasa de largo aunque cada label (add.text) sea caro, y el muñeco/barra no se traban. Más chico
   *  = más suave pero más lento (el usuario prefiere carga inicial más larga y gameplay fluido). */
  private static readonly ENTITY_DRAIN_BUDGET_MS = 6;
  // Frames consecutivos con todo "listo" antes de arrancar el fade. Da un pequeño "settle" para
  // que el burst de carga (bake del minimapa, primer render completo del mundo, primer paso de
  // lighting/sombras/culling sobre las entidades recién creadas) pase BAJO el overlay opaco, así el
  // fade anima fluido en vez de trabarse. Se resetea si algo deja de estar listo.
  public loadingReadyFrames = 0;
  public static readonly LOADING_SETTLE_FRAMES = 20;
  /** Progreso máximo alcanzado por la loading bar (monotónico: nunca baja, así
      los batches de carga posteriores no la "resetean"). */
  public loadingBarMaxProgress = 0;

  // Visibilidad de nombres de NPC: 'always' = siempre visible, 'hover' = solo al pasar el mouse.
  // Modos separados para NPCs neutrales y hostiles (ambos default 'hover'). La regla de
  // muerto (solo sacerdotes en hover/select) se aplica aparte en updateNpcNameVisibility.

  public npcNameVisibilityMode: 'always' | 'hover' = 'hover';

  public hostileNpcNameVisibilityMode: 'always' | 'hover' = 'hover';

  // HUD Principal



  // Skin ui/main: globos laterales (vida izq / mana der). Fill con crop vertical desde arriba.
  // Banda de mana RESERVADA (arriba del globo, color más claro) + su label.
  // Sockets (origin = centro del círculo) y demás capas para escalar/posicionar.
  // overlay_bl (brillo) se posiciona con offset propio (es más chico que el globo).
  // overlay_effects (fx): se recorta al líquido igual que el humo (no aparece sobre el vacío).
  // Humo sutil dentro de cada orbe (emitter + zona de emisión). La máscara es un Graphics que
  // dibuja el SEGMENTO de líquido actual (círculo recortado por la superficie) → el humo sólo
  // se ve sobre el líquido visible, no sobre la reserva ni el vacío.
  // Máscaras geométricas del líquido (se redibujan según el nivel actual del orbe).
  // Geometría del orbe (centro + radio en px de pantalla) y fracción de fill actual, para
  // redibujar las máscaras tanto al cambiar layout como al cambiar stats.


  public manaReservedPercent: number = 0;
  /** Max mana without any reserves (captured when reserve is 0) */
  private trueMaxMana: number = 0;

  // Piezas estáticas del skin ui/main (escala+posición en updateHUDPositions)
  // Keycaps de las posiciones NO funcionales (7,8,9,0,-,=,R,T): caja = asset
  // main_shortcut_frame(_small) + número (el receso ya está horneado en main_frame).

  // Botones del HUD (4 cuadrados en el HUD principal)
  // Botón de pantalla completa (glyph ⛶ / ⤢). Esquina superior-izquierda; alterna la Fullscreen API.
  public fullscreenButton: Phaser.GameObjects.Text | null = null;

  // Input extra

  public inputManager: InputManager | null = null;
  public inputSystem: InputSystem | null = null;

  // Chat simple

  // Chat simple

  private chatMessages: Phaser.GameObjects.Text[] = [];

  public chatInputText: Phaser.GameObjects.Text | null = null;

  public chatCursor: Phaser.GameObjects.Text | null = null;

  public chatBackground: Phaser.GameObjects.Rectangle | null = null;

  public chatCurrentInput = '';

  public chatIsFocused = false;

  public chatCursorBlinkTimer: number = 0;

  public activeChatBubbles: Map<string, Phaser.GameObjects.Text> = new Map();

  public activeNpcChatBubbles: Map<string, Phaser.GameObjects.Text> = new Map();

  // Textos de ganancia de HP/Mana que necesitan seguimiento de posición
  public activeHealthManaGainTexts: Map<string, Phaser.GameObjects.Text> = new Map();
  // Textos de daño (rojo, a la izquierda) — mismo sistema de follow que las curas.
  public activeDamageTexts: Map<string, Phaser.GameObjects.Text> = new Map();
  // Separación horizontal: curas a la derecha (+), daño a la izquierda (−).
  public static readonly DAMAGE_HEAL_X_OFFSET = 8;

  // Inventario

  public inventoryPanel: InventoryPanel | null = null;
  public characterInfoPanel: CharacterInfoPanel | null = null;
  // Cache de los visualIds del player local para detectar cambios per-frame y
  // refrescar el preview del CharacterInfoPanel (los mensajes de Colyseus llegan
  // antes que los state patches, así que no se puede leer el schema desde un
  // handler de mensaje sin riesgo de visualIds stale).
  private lastLocalVisualIdsKey: string | null = null;
  public passiveTreePanel: PassiveTreePanel | null = null;
  /** Renderer de yacimientos (minería). Ver docs/MINING_AND_SMITHING.md. */
  private resourceNodeRenderer?: ResourceNodeRenderer;
  /** Renderer de estaciones de crafteo + ventana de crafteo. Ver docs/CRAFTING_SYSTEM.md. */
  private craftingStationRenderer?: CraftingStationRenderer;
  private craftingPanel: CraftingPanel | null = null;
  /** Ventanita del modo trabajo + nodo que estamos trabajando (para actualizarla en vivo). */
  private workPanel: WorkProgressPanel | null = null;
  private activeWorkNodeId: string | null = null;
  /** Niveles de skill del player (para el estado gris del crafteo). Se llena en SKILLS_SYNC. */
  private skillLevels = new Map<string, number>();
  /** Panel de Skills de progresión (Minería/Herrería/…). Ver docs/SKILLS_SYSTEM.md. */
  private skillsPanel: SkillsPanel | null = null;
  /** Feed de XP de habilidades (drops estilo MMO). Ver docs/SKILLS_SYSTEM.md. */
  private skillXpFeed: SkillXpFeed | null = null;
  /** Ventanita de debug (admin) para dar XP de skill. Ver docs/SKILLS_SYSTEM.md. */
  private debugSkillXpPanel: DebugSkillXpPanel | null = null;

  // Items en el piso

  public groundItemObjects: Map<

    string,

    {
      sprite: Phaser.GameObjects.Sprite;

      label: Phaser.GameObjects.Text;

      effect?: Phaser.GameObjects.Container; // Efecto visual para items de rareza (SET)

      enchantEffect?: Phaser.GameObjects.Container; // Efecto visual para encantamientos (sobre rareza)

      uniqueEffect?: Phaser.GameObjects.Container; // Fallback Canvas: glow-círculo con heartbeat (único)

      uniqueGlow?: Phaser.FX.Glow; // WebGL: glow de silueta colgado del sprite del ítem (único)

      itemId: string;

      quantity: number;

      id: string;
    }

  > = new Map();

  // Consola de mensajes de sistema (arriba a la izquierda)
  public systemConsole: SystemConsole | null = null;

  // Banner de área (nombre centrado arriba al entrar a un área con nombre).
  public areaBannerText: Phaser.GameObjects.Text | null = null;
  public areaBannerTween: Phaser.Tweens.Tween | null = null;
  /** Id del área-banner actual (evita re-disparar el banner por mensajes repetidos). */
  public currentAreaBannerId: string | null = null;

  // -----------------

  // Panel /invocar

  // -----------------

  public invokePanel: InvokePanel | null = null;

  public invokeIsOpen: boolean = false;

  public invokeNpcTemplates: NpcTemplateListMessage['templates'] = [];
  public invokeSelectedIndex: number = -1;
  /**
   * Snapshot del último displayList renderizado en el InvokePanel. El panel
   * envía clicks por row index; este array nos dice si esa fila es un header
   * (skip) o un item (mapear a templateIndex en invokeNpcTemplates).
   */
  public invokeDisplayList: Array<
    { type: 'header'; label: string } | { type: 'item'; templateIndex: number }
  > = [];

  // -----------------

  // Panel de templates de items

  // -----------------

  public itemPanel: ItemPanel | null = null;

  // Panel de info de NPC (right-click): persiste con la última info recibida.
  public npcInfoPanel: NpcInfoPanel | null = null;

  // Panel del vendedor: instancia se crea on-demand al abrir el primer merchant.
  public merchantPanel: MerchantPanel | null = null;
  // Panel de la Casa de Subastas (Exchange): on-demand al primer OPEN. Ver
  // docs/ECONOMY_EXCHANGE_MARKETPLACE.md.
  public exchangePanel: ExchangePanel | null = null;
  // Panel del Bazar (Marketplace de ítems rolled): on-demand.
  public marketplacePanel: MarketplacePanel | null = null;
  // Panel del vendedor de hechizos: instancia on-demand al abrir el primer spell vendor.
  public spellVendorPanel: SpellVendorPanel | null = null;
  // UI de quests (tracker + diálogo de oferta/entrega + log). Ver docs/QUESTS.md.
  public questUI: QuestUI | null = null;
  // Party (grupos entre jugadores): ventana de gestión + buscador. Ver docs/PARTY_SYSTEM.md.
  public partyController: PartyController | null = null;
  /** Cache local del oro del player. Actualizado por GOLD_UPDATED. */
  public localGold: number = 0;
  /** Stats de facción del player local (privados). Actualizado por FACTION_STATS.
   * Usado por el minimapa (aliados desde Teniente) y la UI. Ver docs/FACTIONS.md. */
  public localFaction: string = '';
  public localFactionRank: number = 0;
  public localHonor: number = 0;
  public localEnemiesKilled: number = 0;
  /** Overlay DOM activo de facción (confirmación de unión o panel del rey). */
  public factionOverlay: HTMLDivElement | null = null;
  /** Snapshot del último INVENTORY_SYNC, para alimentar al MerchantPanel. */
  public lastInventorySnapshot: Array<{
    slotIndex: number;
    itemId: string;
    quantity: number;
    rolledRarity: number;
    rolledAffixes: Record<string, Array<{ v: number; t: number }>>;
    equippedIn?: string;
    enchantId?: string | null;
  }> = [];

  // -----------------

  // Panel de galería de bodies

  // -----------------

  public bodyGalleryPanel: BodyGalleryPanel | null = null;

  // Panel de galería de weapons (preview client-side)
  public weaponGalleryPanel: WeaponGalleryPanel | null = null;

  // Panel de debug de frames del personaje local (stepper de cuadros)
  public charFrameDebugPanel: CharFrameDebugPanel | null = null;
  // Preview client-side del arma del player local (galería de weapons).
  // null = sin override (se usa el arma real que dicta el server).
  public localWeaponPreviewSheetKey: string | null = null;

  // -----------------

  // Panel de animaciones

  // -----------------

  public animPanel: AnimPanel | null = null;
  public particlePanel: ParticlePanel | null = null;
  public iconViewerPanel: IconAtlasViewerPanel | null = null;
  /** ParticleManager singleton (gestiona v1 y v2 con anchors). Lazy-init. */
  public particleManager: ParticleManager | null = null;
  /** Handles de partículas ambiente del mapa (sectores). Se limpian al recargar. */
  public mapParticleHandles: EmitterHandle[] = [];
  /** Handles activos lanzados desde ParticlePanel (invocar/shoot) — para cleanup. */
  public activeNativeHandles: EmitterHandle[] = [];
  /**
   * Auras sincronizadas server-side (F4). entityId → fxId → handle.
   * Se sincroniza por diff en addOrUpdatePlayer/updateNPC contra el array
   * `auraFxIds` del PlayerState (Colyseus Schema).
   */
  public activeAuraEmitters: Map<string, Map<string, EmitterHandle>> = new Map();
  /**
   * Pérdida de movilidad: emitter de raíces (inmovilizado) por entidad y set de
   * entidades con tint pétreo (paralizado). Driveado por el `impairState` synced.
   * Ver docs/CC_IMMOBILIZE_PARALYZE.md.
   */
  public activeRootsEmitters: Map<string, EmitterHandle> = new Map();
  public paralyzeTintedEntities: Set<string> = new Set();
  /**
   * VFX de items equipados (F5). entityId → `${slot}|${fxId}` → handle.
   * Sincronizado por diff en addOrUpdatePlayer contra weaponId/bodyId/helmId/
   * shieldId/bootId del PlayerState. Lookup del template via
   * getItemTemplateByVisualId().
   */
  public activeEquippedEmitters: Map<string, Map<string, EmitterHandle>> = new Map();
  public buffBar: BuffBar | null = null;
  public spellsPanel: SpellsPanel | null = null;
  public spellQuickBar: SpellQuickBar | null = null;
  /** Spell targeting mode state */
  public spellTargeting: { spellId: string; areaSize: number; preview: Phaser.GameObjects.Graphics | null; startedAt: number } | null = null;
  /** Global spell interval — last cast timestamp */
  public lastSpellCastTime: number = 0;
  public readonly SPELL_INTERVAL_MS = 500; // Global cooldown between any spell cast
  /** Active cast bars visible above players (visible to all) */
  public activeCastBars: Map<string, { spellId: string; startTime: number; duration: number; bar: Phaser.GameObjects.Graphics; text: Phaser.GameObjects.Text; fillColor?: number; timerLabel?: string }> = new Map();
  /** Spell words bubbles (dark cyan, separate from chat) */
  public activeSpellWords: Map<string, Phaser.GameObjects.Text> = new Map();
  /** Self error texts (red, only local player) */
  public activeErrorTexts: Map<string, Phaser.GameObjects.Text> = new Map();
  /** Looping cast animations per entity */
  public activeCastAnimations: Map<string, Phaser.GameObjects.Sprite> = new Map();
  /** Meditation state: looping particle FX handles per entity. */
  public meditationFxHandles: Map<string, EmitterHandle> = new Map();

  /**
   * FX de meditación según el nivel del personaje: una distinta cada 10
   * niveles (1-9, 10-19, ..., 50), escalando de chica/calma a la full de 50.
   */
  public static meditationFxIdForLevel(level: number): string {
    if (level >= 50) return 'fx_meditate_level50';
    if (level >= 40) return 'fx_meditate_level40';
    if (level >= 30) return 'fx_meditate_level30';
    if (level >= 20) return 'fx_meditate_level20';
    if (level >= 10) return 'fx_meditate_level10';
    return 'fx_meditate_level1';
  }
  /** Blue screen overlay for local player meditation */
  public meditationOverlay: Phaser.GameObjects.Rectangle | null = null;

  /** Viñeta roja + cartel "TAUNTEADO" para el jugador local provocado. Ver docs/TAUNT.md. */
  public tauntOverlayGfx: Phaser.GameObjects.Graphics | null = null;
  public tauntLabel: Phaser.GameObjects.Text | null = null;
  public tauntOverlayActive = false;

  /** Spawn protection: halo dorado (preFX glow) por entidad (visible para todos). */
  public spawnProtectionGlows: Map<
    string,
    {
      sprites: Phaser.GameObjects.Sprite[];
      controllers: Phaser.FX.Glow[];
      tween: Phaser.Tweens.Tween | null;
    }
  > = new Map();
  /** Golden screen overlay for local player spawn protection. */
  public spawnProtectionOverlay: Phaser.GameObjects.Rectangle | null = null;
  /** Countdown label centered-top for local player spawn protection. */
  public spawnProtectionLabel: Phaser.GameObjects.Text | null = null;
  /** Timer event que actualiza el contador de protección del local. */
  public spawnProtectionTimer: Phaser.Time.TimerEvent | null = null;
  /** True mientras el local player tiene protección de aparición (gate de input). */
  public localSpawnProtected: boolean = false;

  public animButton: Phaser.GameObjects.Text | null = null;
  public particleButton: Phaser.GameObjects.Text | null = null;

  // -----------------

  // Mapas

  // -----------------

  public currentMap: MapData | null = null;
  /** Minimapa in-game (sigue al player, zoom, marcadores). UI gráfica la pone otro agente. */
  public minimap: Minimap | null = null;

  public blockedTiles: Set<string> | null = null;
  /** Tiles de agua NAVEGABLE ("x,y") — espejo del waterIndex del server. Ver docs/WATER_AND_BOATS.md. */
  public waterTiles: Set<string> | null = null;
  /**
   * Índice liviano de sonido de paso por superficie: "x,y" → id de sonido, SÓLO para superficies
   * NO-default (ej. caminos). El pasto (default) NO se indexa. Se construye al cargar el mapa
   * (MapLoader). Ver docs/SOUND_AND_MUSIC.md.
   */
  public footstepSurfaceSounds: Map<string, string> = new Map();
  /** Sprites/animaciones de los `decoObjects` del mapa (fogatas, etc.) — para teardown en reload. */
  public decoObjectSprites: Map<string, Phaser.GameObjects.GameObject> = new Map();
  /** IDs de las luces acopladas a `decoObjects` (para removerlas del LightingSystem en reload). */
  public decoObjectLightIds: Set<string> = new Set();

  public showBorderBlocks: boolean = false;

  public showFloorBlocks: boolean = false;

  public mapDebugGridSystem: MapDebugGridSystem | null = null;

  // Patrón de fondo anclado a la cámara
  private mapBgPattern: Phaser.GameObjects.TileSprite | null = null;

  // Shader de agua para efectos de agua en el mapa
  // Ahora guardamos un shader por cada tile de agua
  private waterTileShaders: Map<string, Phaser.GameObjects.Shader> = new Map();

  public decorationIndex: Record<string, { 
    name: string; 
    type: string; 
    light?: { radius: number; intensity: number; color: string; flicker?: boolean };
  }> | null = null;

  public animationsIndex: Record<string, AnimationDefinition> | null = null;


  // Opción de debug: resaltar el tile actual del jugador local

  public showPlayerTile: boolean = false;

  public playerTileGraphics: Phaser.GameObjects.Graphics | null = null;

  // Debug: Crosshair en el centro de la pantalla (fijo)
  private showCrosshair: boolean = false;
  private crosshairGraphics: Phaser.GameObjects.Graphics | null = null;
  
  // Debug: Tile del servidor (rojo) vs tile del cliente (verde)
  public serverTileGraphics: Phaser.GameObjects.Graphics | null = null;

  // Rate limiting para sincronizar con el servidor
  // El servidor usa Date.now() para rate limiting, así que el cliente también debe
  public lastMoveTimestamp: number = 0;
  /** Última dirección enviada al server (move o giro). Evita spamear giros. */
  public lastSentMoveDir: Direction | null = null;

  // -----------------

  // Inventario

  // -----------------

  private createInventoryPanel(): void {
    const { width, height } = this.scale;
    const savedOptions = this.persistence.loadClientOptions();
    const panelX = savedOptions?.inventoryPanelX ?? width / 2;
    const panelY = savedOptions?.inventoryPanelY ?? height / 2;

    this.inventoryPanel = new InventoryPanel(
      this,
      {
        onMove: (x, y) => this.persistence.savePanelPosition('inventory', x, y),
        onDrop: (slotIndex) => this.sendInventoryDrop(slotIndex),
        onEquip: (_slotIndex, slot) => this.sendInventoryEquip(slot),
        onUnequip: (_slotIndex, slot) => this.sendInventoryUnequip(slot),
        onUse: (_slotIndex, slot) => this.sendInventoryUse(slot),
        onMoveItem: (fromSlot, toSlot) => this.sendInventoryMoveItem(fromSlot, toSlot),
      },
      {
        initialX: panelX,
        initialY: panelY,
      }
    );
  }

  public toggleInventoryPanel(): void {
    if (!this.inventoryPanel) return;
    const wasOpen = this.inventoryPanel.isOpenPanel();
    this.inventoryPanel.toggle();
    // Reconfigurar filtros de cámara para asegurar que el panel quede en la cámara UI
    if (!wasOpen || this.inventoryPanel.isOpenPanel()) {
      this.setupCameraFilters();
    }
    // Sincronizar el oro mostrado en la esquina con el valor actual del player.
    if (this.inventoryPanel.isOpenPanel()) {
      this.inventoryPanel.setGold(this.localGold);
    }
  }

  private createCharacterInfoPanel(): void {
    const { width, height } = this.scale;
    const savedOptions = this.persistence.loadClientOptions();
    const panelX = savedOptions?.characterInfoPanelX ?? width / 2;
    const panelY = savedOptions?.characterInfoPanelY ?? height / 2;

    this.characterInfoPanel = new CharacterInfoPanel(
      this,
      {
        onMove: (x, y) => this.persistence.savePanelPosition('characterInfo', x, y),
        onUnequip: (slot) => this.sendUnequipBySlot(slot),
        getEquippedItems: () => this.collectEquippedItems(),
        getLocalPlayerVisualIds: () => this.collectLocalPlayerVisualIds(),
      },
      {
        initialX: panelX,
        initialY: panelY,
      }
    );
  }

  /** Envía UNEQUIP_ITEM al server por nombre de slot del enum EquipmentSlot. */
  private sendUnequipBySlot(slot: EquipmentSlot): void {
    if (!this.colyseusClient) return;
    const msg = {
      type: ClientMessageType.UNEQUIP_ITEM,
      slot,
    } as never;
    this.colyseusClient.send(msg);
  }

  /** Items equipados → indexados por EquipmentSlot. Devuelve un Map vacío si no hay snapshot. */
  private collectEquippedItems(): Map<EquipmentSlot, InventorySlotData> {
    const result = new Map<EquipmentSlot, InventorySlotData>();
    for (const slot of this.lastInventorySnapshot) {
      if (!slot.equippedIn) continue;
      result.set(slot.equippedIn as EquipmentSlot, {
        slotIndex: slot.slotIndex,
        itemId: slot.itemId,
        enchantId: slot.enchantId ?? null,
        quantity: slot.quantity,
        rolledRarity: slot.rolledRarity,
        rolledAffixes: slot.rolledAffixes,
        equippedIn: slot.equippedIn,
      });
    }
    return result;
  }

  /**
   * Muestra/oculta el ícono HUD de barco equipado (a la derecha del indicador
   * de zona, mismo tamaño) según el snapshot de inventario. Se llama en cada
   * INVENTORY_SYNC. Ver docs/WATER_AND_BOATS.md.
   */
  private refreshBoatIndicator(): void {
    if (!this.boatIndicator) return;
    const boat = this.lastInventorySnapshot.find(
      (s) => s.equippedIn === EquipmentSlot.BOAT
    );
    if (!boat) {
      this.boatIndicator.setVisible(false);
      this.currentBoatIndicatorIcon = null;
      return;
    }
    const tpl = getItemTemplate(boat.itemId);
    const key =
      tpl && this.textures.exists(`item_icon_${tpl.icon}`)
        ? `item_icon_${tpl.icon}`
        : 'item_icon_noicon';
    if (key !== this.currentBoatIndicatorIcon) {
      this.currentBoatIndicatorIcon = key;
      this.boatIndicator.setTexture(key).setDisplaySize(14, 14);
    }
    this.boatIndicator.setVisible(true);
  }

  /** Lee los IDs visuales del player local del schema, o null si no hay sesión. */
  private collectLocalPlayerVisualIds(): {
    bodyId: number;
    headId: number;
    helmId: number;
    bootId: number;
    weaponId: number;
    shieldId: number;
  } | null {
    if (!this.localSessionId) return null;
    const p = this.room?.state.players.get(this.localSessionId) as
      | (PlayerStateView & { helmId?: number; bootId?: number })
      | undefined;
    if (!p) return null;
    return {
      bodyId: p.bodyId ?? 0,
      headId: p.headId ?? 0,
      helmId: p.helmId ?? 0,
      bootId: p.bootId ?? 0,
      weaponId: p.weaponId ?? 0,
      shieldId: p.shieldId ?? 0,
    };
  }


  public toggleCharacterInfoPanel(): void {
    if (!this.characterInfoPanel) return;
    const wasOpen = this.characterInfoPanel.isOpenPanel();
    this.characterInfoPanel.toggle();
    
    // Si se está abriendo, solicitar estadísticas al servidor
    if (!wasOpen && this.characterInfoPanel.isOpenPanel() && this.room) {
      this.requestCharacterStats();
    }

    // Al cerrar, resetear el cache de visualIds para que reabrir re-renderice el preview.
    if (!this.characterInfoPanel.isOpenPanel()) {
      this.lastLocalVisualIdsKey = null;
    }

    // Reconfigurar filtros de cámara
    if (!wasOpen || this.characterInfoPanel.isOpenPanel()) {
      this.setupCameraFilters();
    }
  }

  private requestCharacterStats(): void {
    if (!this.colyseusClient) return;
    
    const message: RequestCharacterStatsMessage = {
      type: ClientMessageType.REQUEST_CHARACTER_STATS,
    };
    
    this.colyseusClient.send(message as never);
  }

  private handleCharacterStats(message: CharacterStatsMessage): void {
    if (this.characterInfoPanel) {
      this.characterInfoPanel.updateStats(message.stats);
    }
  }

  private handleTileInfo(message: TileInfoMessage): void {
    // Mostrar información del tile desde el servidor
    message.players.forEach((player) => {
      this.chat.addSystemMessage(`${player.name} [${message.x},${message.y}] (${player.id})`);
    });
    message.npcs.forEach((npc) => {
      this.chat.addSystemMessage(`${npc.name} [${message.x},${message.y}] (${npc.id})`);
    });
    message.items.forEach((item) => {
      const itemName = formatItemName(item.itemId, item.enchantId);
      const rarity = item.rolledRarity as ItemRarity;
      const rarityColor = '#' + (ITEM_RARITY_COLORS[rarity] ?? 0xffffff).toString(16).padStart(6, '0');
      const debugInfo = import.meta.env.DEV ? ` [${item.id}]` : '';
      this.chat.addSystemMessage(`${itemName} x${item.quantity}${debugInfo}`, rarityColor);
    });
  }

  // ============================================================================
  // Skill Tree (Espíritu) Message Handlers
  // ============================================================================


  public fpsText: Phaser.GameObjects.Text | null = null;

  public coordText: Phaser.GameObjects.Text | null = null;

  /** Indicador de tipo de zona (abajo-izquierda): icono según safe/neutral/especial/danger. */
  public zoneIndicator: Phaser.GameObjects.Image | null = null;

  /** Último tipo de zona renderizado (evita setTexture cada frame). */
  private currentZoneType: ZoneType | null = null;

  /**
   * Indicador de barco equipado (HUD): ícono del barco a la derecha del
   * indicador de zona, mismo tamaño. Visible mientras haya un barco en el slot
   * `boat`. Se refresca en cada INVENTORY_SYNC. Ver docs/WATER_AND_BOATS.md.
   */
  public boatIndicator: Phaser.GameObjects.Image | null = null;

  /** Última textura pintada en el boatIndicator (evita setTexture redundante). */
  private currentBoatIndicatorIcon: string | null = null;

  // Debug panel for lighting/shadow testing
  public lightingDebugPanel: HTMLDivElement | null = null;
  public timeSlider: SliderControl | null = null;
  public timeLabel: HTMLSpanElement | null = null;
  public useManualTime: boolean = false;
  public manualHour: number = 12;

  // UI de menú / opciones

  public isMenuOpen = false;

  private characterInfoButton: Phaser.GameObjects.Text | null = null;

  public gearButton: Phaser.GameObjects.Text | null = null;

  // Botón de sol (☀️) a la izquierda del ⚙: togglea el panel debug de hora del día.
  public sunButton: Phaser.GameObjects.Text | null = null;

  private inventoryButton: Phaser.GameObjects.Text | null = null;

  private passiveTreeButton: Phaser.GameObjects.Text | null = null;

  public invokeButton: Phaser.GameObjects.Text | null = null;

  public itemsButton: Phaser.GameObjects.Text | null = null;

  public bodyGalleryButton: Phaser.GameObjects.Text | null = null;

  public weaponGalleryButton: Phaser.GameObjects.Text | null = null;

  public charFrameButton: Phaser.GameObjects.Text | null = null;

  public menuOverlay: Phaser.GameObjects.Rectangle | null = null;

  // UI de reconexión

  public reconnectOverlay: Phaser.GameObjects.Rectangle | null = null;

  public reconnectText: Phaser.GameObjects.Text | null = null;

  public isReconnecting = false;

  public reconnectTimeout: Phaser.Time.TimerEvent | null = null;

  public menuButtons:
    | {
        opciones: UiButton;

        salir: UiButton;

        volver: UiButton;
      }

    | null = null;

  public optionsPanel:
    | {
        bg: Phaser.GameObjects.Rectangle;

        aureumLabel: Phaser.GameObjects.Text;

        sectionButtons: Phaser.GameObjects.Text[];

        contentText: Phaser.GameObjects.Text;

        videoToggleGrid: Phaser.GameObjects.Text;

        videoToggleCoords: Phaser.GameObjects.Text;

        currentTileToggle: Phaser.GameObjects.Text;

        headingArrowToggle: Phaser.GameObjects.Text;

        closeButton: Phaser.GameObjects.Text;

        npcNamesToggle: Phaser.GameObjects.Text;

        hostileNpcNamesToggle: Phaser.GameObjects.Text;

        borderBlocksToggle: Phaser.GameObjects.Text;

        floorBlocksToggle: Phaser.GameObjects.Text;

        hudScaleLabel: Phaser.GameObjects.Text;

        sliderBg: Phaser.GameObjects.Rectangle;

        sliderMarks: Phaser.GameObjects.Text[];

        sliderThumb: Phaser.GameObjects.Arc;

        soundControls: SoundControls;
      }

    | null = null;

  constructor() {
    super('GameScene');

    this.configData = {
      serverUrl: 'ws://localhost:9000',

      characterId: '',
    };
  }

  preload(): void {
    // Loading bar (frame+fill) + logo: encolar PRIMERO para que estén listos
    // temprano y mostrar el progreso de la carga masiva. Ver docs/UI_SKINNING.md.
    this.load.image('loading_frame', '/assets/ui/loading/loading_frame.png');
    this.load.image('loading_fill', '/assets/ui/loading/loading_fill.png');
    this.load.image('logo', '/assets/logo.png');
    // Assets del TargetPanel (frame + barra de HP roja + bisel circular del avatar).
    this.load.image('target_frame', '/assets/ui/target/target_frame.png');
    this.load.image('target_fill_red', '/assets/ui/target/target_fill_red.png');
    this.load.image('target_avatar_overlay', '/assets/ui/target/target_avatar_overlay.png');
    // Scrollbar dorado (CharacterInfoPanel) + checkbox dorado (PassiveTreePanel). Mismo
    // arte que el kit DOM (ui/scrollbar, ui/form). Ver docs/UI_SKINNING.md.
    this.load.image('scroll_bar', '/assets/ui/scrollbar/scroll_bar.png');
    this.load.image('scroll_handler', '/assets/ui/scrollbar/scroll_handler.png');
    this.load.image('scroll_handler_hover', '/assets/ui/scrollbar/scroll_handler-HOVER.png');
    this.load.image('scroll_top_arrow', '/assets/ui/scrollbar/scroll_top_arrow.png');
    this.load.image('scroll_top_arrow_hover', '/assets/ui/scrollbar/scroll_top_arrow-HOVER.png');
    this.load.image('scroll_bot_arrow', '/assets/ui/scrollbar/scroll_bot_arrow.png');
    this.load.image('scroll_bot_arrow_hover', '/assets/ui/scrollbar/scroll_bot_arrow-HOVER.png');
    this.load.image('form_checkbox_empty', '/assets/ui/form/checkbox_empty.png');
    this.load.image('form_checkbox_selected', '/assets/ui/form/checkbox_selected.png');
    // Mostrar logo + barra apenas estén las texturas (logo+frame+fill cargan
    // primero), y actualizar el progreso. El gate evita construir la pantalla con
    // el logo en fallback de texto antes de que cargue el logo real.
    this.load.on('progress', (value: number) => {
      if (
        this.textures.exists('logo') &&
        this.textures.exists('loading_frame') &&
        this.textures.exists('loading_fill')
      ) {
        this.loadingScreenUi.createLoadingScreen();
      }
      this.loadingScreenUi.setLoadingBarProgress(value * 0.7); // preload = 0..0.7 (monotónico)
    });

    // Precargar SÓLO los bodies que tienen asset real (vestimentas + naked + NPC).
    // Antes se iteraba 1..112 a ciegas → ~80 ids inexistentes = un 404 por cada uno +
    // flood de "Frame N not found" al intentar cortarlos. La fuente de verdad de qué
    // existe es getAllExistingBodyIds() (VESTIMENT + NAKED + NPC). Ver BodySpriteManager.
    const allBodyIds: number[] = getAllExistingBodyIds();
    preloadBodySprites(this, allBodyIds);

    // Precargar SÓLO las cabezas con asset real (EXISTING_HEAD_IDS). Antes iteraba 1..100:
    // 404eaba las faltantes en ese rango y NO cargaba 101-113 (que existen). Ver HeadSpriteManager.
    preloadHeadSprites(this, EXISTING_HEAD_IDS);

    // Preload boot sprites
    const allBootIds: number[] = [];
    for (let i = 1; i <= 10; i++) {
      allBootIds.push(i);
    }
    preloadBootSprites(this, allBootIds);

    // Precargar todos los weapons, shields y helmets usados en los items
    const allWeaponIds = new Set<number>();
    const allShieldIds = new Set<number>();
    const allHelmetIds = new Set<number>();
    const allItemTemplates = getAllItemTemplates();
    
    allItemTemplates.forEach((template: ItemDefinition) => {
      if (template.weaponId) {
        allWeaponIds.add(template.weaponId);
      }
      if (template.shieldId) {
        allShieldIds.add(template.shieldId);
      }
      if (template.helmId) {
        allHelmetIds.add(template.helmId);
      }
    });
    
    // Convertir Sets a arrays ordenados
    const weaponIdsArray = Array.from(allWeaponIds).sort((a, b) => a - b);
    const shieldIdsArray = Array.from(allShieldIds).sort((a, b) => a - b);
    const helmetIdsArray = Array.from(allHelmetIds).sort((a, b) => a - b);
    
    // Precargar todos los weapons, shields y helmets necesarios
    if (weaponIdsArray.length > 0) {
      preloadWeaponSprites(this, weaponIdsArray);
    }
    if (shieldIdsArray.length > 0) {
      preloadShieldSprites(this, shieldIdsArray);
    }
    if (helmetIdsArray.length > 0) {
      preloadHelmetSprites(this, helmetIdsArray);
    }

    // Flecha de heading desde assets estáticos

    this.load.image('headingArrow', '/assets/arrow.png');

    // Atlas de partículas — preload eager para FX equipped/auras que se
    // disparan apenas el player sincroniza visualIds (antes de abrir 🎆).
    this.load.spritesheet(
      PARTICLE_SHEET.key,
      `/assets/${PARTICLE_SHEET.path}`,
      { frameWidth: PARTICLE_SHEET.frameWidth, frameHeight: PARTICLE_SHEET.frameHeight }
    );

    // Dropshadow para entidades (sombra debajo de los pies)
    this.load.image('dropshadow', '/assets/dropshadow.png');
    // Footsteps (huellas al caminar) — sheet único 12×8 (8 sets de 3×4)
    preloadFootsteps(this);
    // Patrón de fondo (mismo que en pantalla de auth)
    this.load.image('mapBgPattern', '/assets/world/floors/pattern1.png');

    // Logo para pantalla de carga (con manejo de error si no existe)

    this.load.image('logo', '/assets/logo.png');

    // Iconos de razas para el árbol de pasivas (para keystones/notables)
    const races = ['dwarf', 'elf', 'gnome', 'human', 'orc', 'drow'];
    for (const race of races) {
      // drow usa icon_nightelf
      const iconName = race === 'drow' ? 'nightelf' : race;
      this.load.image(`icon_${race}`, `/assets/icon/icon_${iconName}.png`);
    }

    // Iconos de nodos "clave" del árbol (todas las puntas de rama). Se derivan de la data:
    // node.icon = "passive_<id>" → archivo keystone_<id>.png.
    const passiveIconKeys = new Set<string>();
    for (const n of PASSIVE_TREE_DATA.nodes) if (n.icon) passiveIconKeys.add(n.icon);
    for (const key of passiveIconKeys) {
      this.load.image(key, `/assets/icon/passive/${key.replace(/^passive_/, 'keystone_')}.png`);
    }
    
    // Cabezas de razas para los nodos de inicio del árbol de pasivas
    const raceHeadIds = [1, 21, 41, 61, 81, 101]; // human, elf, orc, drow, dwarf, gnome
    for (const headId of raceHeadIds) {
      this.load.spritesheet(`head${headId}`, `/assets/entities/head/head${headId}.png`, {
        frameWidth: 23,
        frameHeight: 30,
      });
    }

    // Si el logo no se puede cargar, se usará texto como fallback en createLoadingScreen

    // Índice de assets (apéndice) compartido: nombres, tipos y particularidades.

    this.load.json('assetsIndex', '/assets/assets-index.json');

    // HUD assets (skin ui/main). Key = nombre de archivo sin extensión.
    // Ver docs/UI_SKINNING.md. El HUD viejo (esferas) vive en client/assets/hud/
    // y queda como backup (ver _HudLegacy.snapshot.ts).
    for (const k of [
      'main_frame',          // barra inferior con recesos de slots
      'main_fill_red',       // fuente de color para recolorear globo de vida
      'main_fill_blue',      // fuente de color para recolorear globo de mana
      'main_shortcut_frame', // slot grande (1-6)
      'main_shortcut_frame_small', // slot chico (R/T)
      'main_xp_empty_frame', // marco de la barra de XP
      'main_xp_fill',        // fill de XP (crop L→R)
      // Globos laterales (vida izq / mana der)
      'main_globe_empty_left',
      'main_globe_empty_right',
      'main_globe_fill_green',   // → recolor a rojo (vida)
      'main_globe_fill_orange',  // → recolor a azul (mana)
      'main_globe_overlay_effects',
      'main_globe_overlay_bl',
      'main_globe_border_overlay',
      // Efectos de slot
      'spell_frame_icon_OVERLAY',
      'spell_frame_hover_effect',
      'spell_frame_push_effect',
      'spell_frame_cooldown_effect',
    ]) {
      this.load.image(k, `/assets/ui/main/${k}.png`);
    }

    // Frames de agua animada (multi-frame, en loop). Reemplazan el agua estática.
    preloadWaterFrames(this);

    // Botones del HUD (normal y hover)
    this.load.image('hud_button_character', '/assets/hud/button_character.png');
    this.load.image('hud_button_character_hover', '/assets/hud/button_character_hover.png');
    this.load.image('hud_button_inventory', '/assets/hud/button_inventory.png');
    this.load.image('hud_button_inventory_hover', '/assets/hud/button_inventory_hover.png');
    this.load.image('hud_button_spirit', '/assets/hud/button_spirit.png');
    this.load.image('hud_button_spirit_hover', '/assets/hud/button_spirit_hover.png');
    this.load.image('hud_button_spells', '/assets/hud/button_spells.png');
    this.load.image('hud_button_spells_hover', '/assets/hud/button_spells_hover.png');

    // Skin del minimapa (frame + viñeta + botones). Key = nombre del archivo sin extensión.
    for (const k of [
      'mp2_frame',
      'mp2_map_overlay',
      'mp1_button',
      'mp1_button-HOVER',
      'mp1_button-PUSH',
      'mp_icon_map',
      'mp_icon_search',
      'mp_icon_settings',
      'mp_icon_star',
    ]) {
      this.load.image(k, `/assets/ui/minimap/${k}.png`);
    }

    // Skin de ventana genérica (frame con header + botón de cierre). Ver QuestUI/UI_SKINNING.md.
    for (const k of ['c_full_header', 'c_header_close', 'c_header_close-HOVER', 'c_header_close-PUSH']) {
      this.load.image(k, `/assets/ui/window/${k}.png`);
    }

    // Skin del quest log (filas, checkbox, separadores, fondos de recompensa, iconos).
    for (const k of [
      'quest_list_row_bg',
      'quest_list_row-HOVER',
      'quest_list_row_ACTIVE',
      'quest_list_row_checkbox',
      'quest_list_row_checkbox_selected',
      'container_vertical_seperator',
      'quest_description_container_bg_effect',
      'open_quest_seperator',
      'open_quest_rewards_bg',
      'open_quest_reward_bg',
      'quest_reward_coin',
      'quest_reward_crystal',
      'quest_reward_xp',
    ]) {
      this.load.image(k, `/assets/ui/quests/${k}.png`);
    }

    // Bitmap fonts para textos del juego
    // Generamos el XML automáticamente basado en las especificaciones estándar
    // Todas las fuentes comparten: 19 chars/row, 30x30 grid, letras centradas
    for (const font of AVAILABLE_BITMAP_FONTS) {
      const xmlString = generateBitmapFontXML(font.id, font.fontSize);
      const xmlBlob = new Blob([xmlString], { type: 'application/xml' });
      const xmlUrl = URL.createObjectURL(xmlBlob);
      
      this.load.bitmapFont(font.id, `/assets/fonts/${font.id}.png`, xmlUrl);
    }

    // Monogram como bitmap font real (atlas horneado a 16px + .fnt per-char generado
    // desde monogram.ttf). A diferencia de las anteriores (grilla monoespaciada ASCII),
    // este es el Monogram del juego con acentos y avances reales. Se usa en paneles
    // (InventoryPanel) para texto pixel-perfect: los glifos se copian 1:1 sin
    // rasterizado por tamaño, así "la misma letra" siempre se ve igual. Clave crisp:
    // solo tamaños múltiplo de 16 (16 = ×1 nativo, 32 = ×2).
    this.load.bitmapFont(
      MONOGRAM_BMP_FONT,
      '/assets/fonts/monogram/monogram.png',
      '/assets/fonts/monogram/monogram.fnt'
    );

    this.mapLoader.preloadMapAssets(getMap1Bordered());

    // Precargar todos los iconos de items
    this.preloadItemIcons();

    // Precargar todos los iconos de spells
    this.preloadSpellIcons();

    // Precargar sprites de yacimientos (ore_node_<tier>_<stage>)
    this.preloadResourceNodeSprites();

    // Iconos del indicador de tipo de zona (HUD abajo-izquierda). PNGs sueltos
    // en assets/icon/area_<tipo>.png; el tipo se deriva de los flags del área
    // vía classifyZoneType (ver docs/AREAS.md).
    for (const z of ['safe', 'neutral', 'especial', 'danger']) {
      this.load.image(`zone_icon_${z}`, `/assets/icon/area_${z}.png`);
    }

    // Atlases de íconos UI (oro, stats, armas, etc.). Registry en
    // `IconAtlases.ts`: agregar un objeto ahí para sumar más PNGs.
    for (const atlas of ICON_ATLASES) {
      this.load.spritesheet(atlas.key, atlas.asset, {
        frameWidth: atlas.cellSize,
        frameHeight: atlas.cellSize,
      });
    }
    validateIconAtlases();
  }

  /**
   * Precarga todos los iconos de items definidos en shared/data/items.ts
   */
  private preloadItemIcons(): void {
    const allTemplates = getAllItemTemplates();
    // iconName -> carpeta del gráfico. Los gráficos de item de armas viven en
    // `assets/weapon/<icon>.png` (raíz de weapon/, junto a la subcarpeta anims/
    // con los spritesheets); los de vestimentas/armaduras (slot 'body') en
    // `assets/entities/body/<icon>.png` (junto a la subcarpeta anims/ con los
    // spritesheets); los barcos (slot 'boat') en `assets/item/ships/<icon>.png`
    // (su spritesheet de navegación body20-23 sí vive en entities/body/anims/);
    // el resto de íconos vive en `assets/item/<icon>.png`.
    const iconDir = new Map<string, string>();

    // Resolver la carpeta de cada icono único. Prioridad boat > weapon > body > item:
    // si CUALQUIER template marca ese icono con una carpeta no-default, gana. Los
    // barcos van primero porque tienen bodyId (navegación) pero su ÍCONO vive aparte.
    allTemplates.forEach((template: ItemDefinition) => {
      if (!template.icon) return;
      const isBoat = template.slot === 'boat';
      const isWeapon = template.weaponId != null || template.slot === 'weapon';
      const isBody = template.bodyId != null || template.slot === 'body';
      const dir = isBoat
        ? 'item/ships'
        : isWeapon
          ? 'weapon'
          : isBody
            ? 'entities/body'
            : 'item';
      const prev = iconDir.get(template.icon);
      if (prev === undefined || prev === 'item') iconDir.set(template.icon, dir);
    });

    // Precargar cada icono desde su carpeta correspondiente.
    iconDir.forEach((dir, iconName) => {
      const iconKey = `item_icon_${iconName}`;
      this.load.image(iconKey, `${ASSETS_BASE_URL}/${dir}/${iconName}.png`);
    });

    // Precargar icono por defecto para cuando un icono no existe
    this.load.image('item_icon_noicon', `${ASSETS_BASE_URL}/item/noicon.png`);
  }

  /**
   * Precarga los iconos de todos los spells definidos en shared/data/spells.ts.
   * La clave de Phaser es el `icon` crudo (sin prefijo): resolveSpellIcon() en
   * SpellsPanel/SpellVendorPanel busca con scene.textures.exists(spell.icon).
   * Spells sin PNG correspondiente caen al placeholder item_icon_noicon.
   */
  private preloadSpellIcons(): void {
    const uniqueIcons = new Set<string>();
    getAllSpellDefinitions().forEach((spell) => {
      if (spell.icon) uniqueIcons.add(spell.icon);
    });
    uniqueIcons.forEach((iconKey) => {
      this.load.image(iconKey, `${ASSETS_BASE_URL}/spell/${iconKey}.png`);
    });
  }

  /**
   * Precarga los sprites de yacimientos (assets/world/nodes/), generados con
   * client/scripts/gen_tinted_assets.py: minería `ore_node_<tier>_<stage>` (frame por
   * cuánto queda) y tala `tree_node_<especie>[2]_<tree|stump>` (árbol/tocón). Si falta
   * la textura, el ResourceNodeRenderer cae al peñasco procedural.
   */
  private preloadResourceNodeSprites(): void {
    const load = (key: string): void =>
      this.load.image(key, `${ASSETS_BASE_URL}/world/nodes/${key}.png`);
    const oreStages = ['full', 'mid', 'low', 'depleted'];
    for (const type of Object.values(RESOURCE_NODE_TYPES)) {
      if (type.toolType === 'hatchet') {
        // Árboles: árbol/tocón por especie + variante 'a' si tiene.
        const species = type.id.replace('tree_node_', '');
        for (const stage of ['tree', 'stump']) {
          load(`tree_node_${species}_${stage}`);
          if (type.variantCount >= 2) load(`tree_node_${species}2_${stage}`);
        }
      } else {
        // Menas: la key sale del ID del nodo (`ore_node_<x>`), igual que el renderer, +
        // sus variantes visuales. El carbón (id ore_node_coal, 1 variante) entra acá.
        for (let v = 1; v <= type.variantCount; v++) {
          const suffix = v > 1 ? String(v) : '';
          oreStages.forEach((stage) => load(`${type.id}${suffix}_${stage}`));
        }
      }
    }
  }

  init(data: Partial<GameSceneConfig>): void {
    this.inputManager = new InputManager(this);

    this.configData = {
      serverUrl: data.serverUrl ?? 'ws://localhost:9000',

      characterId: data.characterId ?? '',

      mapId: data.mapId,

      arrivalAnimationId: data.arrivalAnimationId,

      randomizeArrival: data.randomizeArrival,
    };

    // Gate de warmup (ver campo characterReady). create() hace todo el trabajo
    // pesado independiente del personaje y luego espera esta promesa antes de
    // joinear la room. Se recrea en cada entrada a la escena (init corre también
    // en scene.restart) para gatear limpio cada vez.
    this.characterReady = new Promise<void>((resolve) => {
      this.resolveCharacterReady = resolve;
    });
    // Abrir el gate de inmediato si ya tenemos el personaje:
    //  - `data.characterId`: scene.restart de translado cross-map / reconexión.
    //  - `pendingCharacterId`: provideCharacter() ya corrió (main.ts entregó el
    //    personaje antes de que la escena arrancara init; caso assets lentos).
    if (!this.configData.characterId && this.pendingCharacterId) {
      this.configData.characterId = this.pendingCharacterId;
    }
    if (this.configData.characterId) {
      this.resolveCharacterReady?.();
    }
  }

  /**
   * Desbloquea el gate de warmup: lo llama main.ts cuando el jugador elige
   * personaje en el login. Setea el characterId y resuelve `characterReady`, con
   * lo que `create()` continúa al join de la room. Guarda `pendingCharacterId`
   * por si se llama antes de que la escena haya corrido init(). Ver
   * docs/AUTH_AND_LOGIN.md.
   */
  provideCharacter(characterId: string): void {
    this.pendingCharacterId = characterId;
    this.configData.characterId = characterId;
    this.resolveCharacterReady?.();
  }

  async create(): Promise<void> {
    // Crear pantalla de carga primero

    this.loadingScreenUi.createLoadingScreen();

    // Controles deshabilitados durante toda la carga; se reactivan al terminar el fade-out
    // de la pantalla de carga (en checkAndHideLoadingScreen → onComplete del tween).
    this.setControlsEnabled(false);

    this.persistence.loadClientOptions();

    // Aplicar configuración de visibilidad de flechas
    this.npcSync.applyHeadingArrowVisibility();

    this.createOrUpdateGridSystem();
    this.mapDebugGridSystem?.drawGrid();

    // Fondo de patrón del mundo (tamaño de viewport, se actualiza en update)
    if (this.textures.exists('mapBgPattern')) {
      const cam = this.cameras.main;
      const bgW = cam.width + TILE_SIZE * 4; // Un poco más grande para cubrir bordes
      const bgH = cam.height + TILE_SIZE * 4;
      this.mapBgPattern = this.add.tileSprite(
        0, // Se actualizará en update()
        0,
        bgW,
        bgH,
        'mapBgPattern'
      );
      this.mapBgPattern.setOrigin(0, 0);
      this.mapBgPattern.setDepth(-1000); // Muy atrás pero visible
      this.mapBgPattern.setScrollFactor(1); // se mueve con el mundo
      this.mapBgPattern.setAlpha(0.5); // Hacerlo visible pero no opaco
    }

    // El shader de agua ahora se crea por cada tile de agua individual
    // (ver método renderMap donde se detectan tiles de tipo "water")

    // NOTA: el join a la Colyseus room NO se hace acá. Todo lo de create() por
    // encima del gate `await this.characterReady` (al final del método) es WARMUP
    // independiente del personaje: corre durante el login, detrás del overlay de
    // auth, así el pase de la lista de personajes al mundo es casi instantáneo.
    // El join + setup de listeners viven en el bloque gateado al final. Ver
    // docs/AUTH_AND_LOGIN.md (warmup) y provideCharacter()/init().

    this.cursors = this.input.keyboard!.createCursorKeys();

    // Anular el menú contextual del browser para que el right-click sea
    // un gesto disponible para nuestro juego (info de NPC y, en el futuro,
    // otras acciones contextuales).
    this.input.mouse?.disableContextMenu();

    this.configureActionKeys();

    // Anti-tecla-pegada: si el canvas/ventana pierde foco (alt-tab, overlay de
    // reconexión, deslogueo), el navegador no entrega el `keyup` y Phaser deja
    // la tecla de movimiento con isDown=true. Como el movimiento es continuo
    // (tryStartLocalMovement manda MOVE mientras la tecla esté "apretada"), el
    // jugador queda auto-caminando solo: el server camina tile a tile y la
    // posición persistida deriva (ej. Y sube sin que el jugador toque nada).
    // resetKeys() al perder foco limpia ese estado fantasma.
    this.fullscreen.setupFocusLossInputReset();

    // Pantalla completa: botón (Fullscreen API) + detección de F11. Cambia el modo de escala
    // a FIT mientras esté en fullscreen para estirar el canvas fijo sin tocar el rango de visión.
    this.fullscreen.setupFullscreen();

    if (this.inputManager) {
      this.inputSystem = new InputSystem(this.inputManager, {
        // Nota: sendMove ya no existe - los movimientos los inicia tryStartLocalMovement()
        sendStop: () => {
          // NO enviar STOP_MOVE si hay un movimiento activo - dejar que termine primero
          // Esto evita que el servidor envíe IDLE y interrumpa el movimiento visual
          if (!this.localSessionId) return;
          const render = this.playerObjects.get(this.localSessionId);
          if (render && render.moveDuration > 0) {
            // Hay movimiento activo - NO enviar STOP_MOVE, dejar que termine
            return;
          }
          
          // Solo enviar STOP_MOVE si no hay movimiento activo
          if (this.colyseusClient) {
            this.colyseusClient.send({ type: ClientMessageType.STOP_MOVE } as never);
          }
        },
        onAttack: () => this.handleMeleeAttack(),
        onInventoryToggle: () => this.toggleInventoryPanel(),
        onPickup: () => this.interaction.requestPickupItemAtPlayerTile(),
        onEquip: () => this.handleEquipToggle(),
        onCharacterInfoToggle: () => this.toggleCharacterInfoPanel(),
        onDropItem: () => this.handleDropItem(),
        onPassiveTreeToggle: () => this.passiveTreeUi.togglePassiveTreePanel(),
        onSpellsToggle: () => this.spellsPanel?.toggle(),
        onSkillsToggle: () => this.toggleSkillsPanel(),
        onMeditate: () => {
          if (this.colyseusClient) this.colyseusClient.send({ type: 'toggle_meditate' as any } as any);
        },
        onSpellSlot: (slotIndex) => {
          if (this.spellsPanel?.isPickingSlot) return; // Don't cast while picking slot
          this.spellQuickBar?.castSlot(slotIndex);
        },
        onClearTarget: () => this.targets.clearTarget(),
        onCycleTarget: (reverse) => this.targets.cycleTargetEnemy(reverse),
        onToggleMap: () => this.mapOverlay?.toggle(),
        onToggleBoat: () => this.sendToggleBoat(),
        onCycleSummonMode: () => {
          if (this.colyseusClient) {
            this.colyseusClient.send({ type: ClientMessageType.CYCLE_SUMMON_MODE } as never);
          }
        },
        onUseItem: () => this.inventoryPanel?.handleUseItem(),
      }, this);
    }

    // Initialize day/night cycle and lighting systems BEFORE state listeners
    // so that entity lights can be registered when players/NPCs are created
    this.timeOfDayManager = new TimeOfDayManager();
    this.lightingSystem = new LightingSystem(this);
    console.log('[GameScene] TimeOfDayManager and LightingSystem initialized');

    // Create elliptical shadow texture for entities
    this.entityLight.createShadowTexture();

    // Create debug panel for lighting/shadow testing
    this.lightingDebug.createLightingDebugPanel();

    // NOTA: setupStateListeners()/setupServerMessageHandlers() se llaman en el
    // bloque gateado al final de create() (necesitan la room ya joineada).

    // Crear buff bar para mostrar buffs/debuffs activos
    this.buffBar = new BuffBar(this);

    const { width: screenW, height: screenH } = this.scale;
    this.spellQuickBar = new SpellQuickBar(this, {
      onCastSpell: (spellId) => this.spellCast.sendSpellCast(spellId),
      onSetSlot: (slotIndex, spellId, isSwap) => {
        if (!this.colyseusClient) return;
        this.colyseusClient.send({ type: 'set_spell_bar_slot' as any, slotIndex, spellId, isSwap } as any);
      },
    }, this.inputManager!);

    // Position buff bar: same X as first spell slot, above the HUD
    const spellBarWorldX = screenW / 2 - 66 - 84 - 20; // barX + firstSlotX - 20
    const hudTopY = screenH - (216 * this.hud.hudScale) + 320;
    this.buffBar.setAnchor(spellBarWorldX, hudTopY);

    // Crear panel de hechizos (H para abrir/cerrar)
    this.spellsPanel = new SpellsPanel(this, {
      onMove: () => {},
      onAssignToQuickBar: (spellId) => {
        this.spellQuickBar?.assignToNextSlot(spellId);
      },
      onAssignToSlot: (spellId, slotIndex) => {
        const isNew = !this.spellQuickBar?.hasSpell(spellId);
        this.spellQuickBar?.setSlot(slotIndex, spellId);
        if (isNew) this.spellQuickBar?.startAssignCooldown(spellId);
        if (this.colyseusClient) {
          this.colyseusClient.send({ type: 'set_spell_bar_slot' as any, slotIndex, spellId } as any);
        }
      },
      getBarSlotSize: () => this.spellQuickBar?.getSlotSize() ?? 44,
      onDropSpellAtPointer: (spellId, pointer) => {
        const idx = this.spellQuickBar?.getSlotIndexAt(pointer) ?? -1;
        if (idx < 0) return; // soltó fuera de la barra
        // Asignación nueva (el hechizo no estaba en la barra) → cooldown optimista.
        const isNew = !this.spellQuickBar?.hasSpell(spellId);
        this.spellQuickBar?.setSlot(idx, spellId);
        if (isNew) this.spellQuickBar?.startAssignCooldown(spellId);
        if (this.colyseusClient) {
          this.colyseusClient.send({ type: 'set_spell_bar_slot' as any, slotIndex: idx, spellId } as any);
        }
      },
    }, { initialX: screenW / 2, initialY: screenH / 2 });

    // NOTA: setupReconnectionHandlers() se llama en el bloque gateado al final
    // de create() (necesita la room ya joineada). El overlay de reconexión es
    // sólo UI, así que se crea acá en el warmup.
    this.reconnect.createReconnectOverlay();

    // Crear animaciones de body (una vez que los sprites han sido cargados).
    // Sólo los bodies con asset real (vestimentas + naked + NPC): iterar 1..112 a ciegas
    // pedía ~80 sheets inexistentes y floodeaba la consola. Ver getAllExistingBodyIds().
    const bodyIdsForAnims: number[] = getAllExistingBodyIds();

    // Cortar los spritesheets de body (112 armaduras/naked + NPC bodies) es ~3-4ms cada uno →
    // hacerlos todos de golpe congelaba el login ~400ms (medido con el perfProbe: 7 freezes de
    // ~55ms seguidos). Se reparte en frames (presupuesto por frame) para mantener el login fluido
    // SIN dejar de precalentar todo por adelantado. Igual para las animaciones de body/weapon/shield.
    // Ver docs/AUTH_AND_LOGIN.md (warmup) y docs/PERFORMANCE_OPTIMIZATION.md.
    await this.runWarmupChunked(bodyIdsForAnims, (id) => setupBodySpritesheets(this, [id]));
    if (!this.scene.isActive()) return;

    // Tempo canónico de AO: ~55.4ms/frame (333ms/6f vertical, 277ms/5f lateral)
    await this.runWarmupChunked(bodyIdsForAnims, (id) =>
      createBodyAnimations(this, [id], WALK_ANIM_FRAME_RATE)
    );
    if (!this.scene.isActive()) return;

    // Crear animaciones de weapons y shields
    const allTemplates = getAllItemTemplates();
    const allWeaponIds = new Set<number>();
    const allShieldIds = new Set<number>();
    allTemplates.forEach((template: ItemDefinition) => {
      if (template.weaponId) {
        allWeaponIds.add(template.weaponId);
      }
      if (template.shieldId) {
        allShieldIds.add(template.shieldId);
      }
    });
    const weaponIdsArray = Array.from(allWeaponIds).sort((a, b) => a - b);
    const shieldIdsArray = Array.from(allShieldIds).sort((a, b) => a - b);

    await this.runWarmupChunked(weaponIdsArray, (id) =>
      createWeaponAnimations(this, [id], WALK_ANIM_FRAME_RATE)
    );
    await this.runWarmupChunked(shieldIdsArray, (id) =>
      createShieldAnimations(this, [id], WALK_ANIM_FRAME_RATE)
    );
    if (!this.scene.isActive()) return;

    // Cargar índice de decoraciones, animaciones y superficies desde el apéndice de assets
    // Importar tipo AssetsIndex para tipado correcto
    const rawAssetsIndex = this.cache.json.get('assetsIndex') as
      | { 
          decorations?: Record<string, { name: string; type: string }>; 
          animations?: Record<string, AnimationDefinition>;
          surfaces?: Record<string, { name: string; type: string; subtype?: string; atlasFile: string; gridX: number; gridY: number }>;
        }
      | undefined;

    if (rawAssetsIndex?.decorations) {
      this.decorationIndex = rawAssetsIndex.decorations;
    }

    // Flags para saber qué assets dinámicos hay que cargar
    const hasSurfaces = !!(rawAssetsIndex?.surfaces && Object.keys(rawAssetsIndex.surfaces).length > 0);
    // Sin surfaces async no hay nada que esperar: el gate de carga puede arrancar el fade.
    if (!hasSurfaces) this.surfacesReady = true;
    const hasAnimations = !!(rawAssetsIndex?.animations);
    let animationIds: number[] = [];

    // Encolar superficies
    if (hasSurfaces) {
      console.log('[GameScene] Precargando superficies desde assets-index.json');
      preloadSurfaceAtlases(this, rawAssetsIndex as { surfaces?: Record<string, { name: string; type: string; subtype?: string; atlasFile: string; gridX: number; gridY: number }> });
    }

    // Encolar animaciones
    if (hasAnimations) {
      this.animationsIndex = rawAssetsIndex!.animations!;
      if (this.animPanel) {
        this.animPanel.setAnimationsIndex(rawAssetsIndex!.animations!);
      }

      animationIds = Object.keys(rawAssetsIndex!.animations!)
        .map((key) => parseInt(key.replace('animation', ''), 10))
        .filter((id) => !isNaN(id));

      if (animationIds.length > 0) {
        preloadAnimationSprites(this, animationIds);
      }
    }

    // Un solo load.start() para todos los assets dinámicos
    if (hasSurfaces || animationIds.length > 0) {
      this.load.once('complete', () => {
        // Crear animaciones primero (no depende de superficies ni del mapa)
        if (animationIds.length > 0) {
          createAnimations(this, animationIds, rawAssetsIndex!.animations!);
        }
        if (hasSurfaces) {
          setupSurfaceTextures(this);
          // Recrear SÓLO los tiles visibles para que tomen la textura de superficie real
          // (refresh liviano). Antes se llamaba reloadCurrentMap() = loadAndCreateMap COMPLETO,
          // que recorría el mundo entero 3 veces + recreaba overlays → ~1.8s de freeze de entrada
          // cuando lo único que cambió eran las texturas. Ver MapTileRenderer.refreshVisibleTilesForSurfaces.
          if (this.currentMap) this.mapTiles.refreshVisibleTilesForSurfaces();
          // Mundo re-renderizado con el pasto real: habilitar el fade ya (los tiles visibles
          // están recreados). El horneado del mapa (minimap + overlay M) NO bloquea la entrada:
          // es una textura de ~7350² (206MB) que no se necesita en los primeros frames → se
          // hornea en tiempo ocioso post-entrada. Si el jugador abre M antes, MapOverlay hornea
          // on-demand igual (prebakeTexture es sólo una optimización).
          this.surfacesReady = true;
          const bakeWorldMaps = (): void => {
            if (!this.scene.isActive()) return;
            if (this.currentMap) this.minimap?.setMap(this.currentMap);
            this.mapOverlay?.prebakeTexture();
          };
          const w = window as unknown as {
            requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
          };
          if (typeof w.requestIdleCallback === 'function') {
            w.requestIdleCallback(bakeWorldMaps, { timeout: 4000 });
          } else {
            this.time.delayedCall(1500, bakeWorldMaps);
          }
        }
      });

      this.load.start();
    }

    // Cargar y dibujar mapa lógico (suelo + decoraciones + techos)

    this.mapLoader.loadAndCreateMap(getMap1Bordered());

    // Si el mapa no tiene superficies pendientes de carga async, sus texturas
    // (decoraciones) ya están listas → hornear el minimapa ahora. Con superficies, se
    // hornea en el callback 'complete' de arriba (una vez que setupSurfaceTextures corrió).
    if (!hasSurfaces && this.currentMap) this.minimap?.setMap(this.currentMap);

    // HUD de estado: coordenadas abajo izquierda, FPS abajo derecha

    const { width, height } = this.scale;

    // HUD inferior derecho: FPS arriba, coordenadas abajo (alineados a la derecha).
    const HUD_LINE_H = 15; // alto de línea para fontSize 11px

    this.coordText = this.addText(width - 8, height - 4, 'M, 0, 0', {
        fontSize: '11px',

        color: '#ffffff',
      })

      .setOrigin(1, 1)

      .setDepth(850) // UI depth para no verse afectado por zoom

      .setScrollFactor(0); // fijo en pantalla

    this.fpsText = this.addText(width - 8, height - 4 - HUD_LINE_H, '0', {
        fontSize: '11px',

        color: '#ffffff',
      })

      .setOrigin(1, 1)

      .setDepth(850) // UI depth para no verse afectado por zoom

      .setScrollFactor(0); // fijo en pantalla

    // Indicador de tipo de zona: icono fijo en la esquina abajo-izquierda (donde
    // antes iban las coords). Arranca en 'danger' (mundo abierto);
    // updateZoneIndicator() lo actualiza por frame según el área donde está el player.
    this.zoneIndicator = this.add
      .image(8, height - 6, 'zone_icon_danger')
      .setOrigin(0, 1)
      .setDisplaySize(14, 14)
      .setDepth(850)
      .setScrollFactor(0);
    this.currentZoneType = 'danger';

    // Indicador de barco equipado: a la derecha del indicador de zona, mismo
    // tamaño. Oculto hasta que un INVENTORY_SYNC confirme un barco en el slot.
    this.boatIndicator = this.add
      .image(8 + 14 + 4, height - 6, 'item_icon_noicon')
      .setOrigin(0, 1)
      .setDisplaySize(14, 14)
      .setDepth(850)
      .setScrollFactor(0)
      .setVisible(false);

    this.menu.createMenuUI();

    this.audioWorld.init();

    this.chat.createChatUI();

    this.createInventoryPanel();
    this.createCharacterInfoPanel();

    this.chat.createSystemConsoleUI();
    this.targets.createTargetPanelUI();

    this.hud.createHUD();

    // Verificar si todo está listo para ocultar la pantalla de carga

    // (se verificará también en update cuando el personaje local esté renderizado)

    this.loadingScreenUi.checkAndHideLoadingScreen();

    // Cursor custom por defecto del juego (.cur). El resto de estados (click/drag/talk/...)
    // se aplican on-demand en hovers/drags y vuelven a éste.
    this.cursor.setGameCursor('main');

    // Input global para drag / resize de la consola

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      // Siempre llamar handlePointerMove del inventoryPanel si hay un drag de item activo o iniciándose
      // (incluso si otro panel está siendo arrastrado)
      if (this.inventoryPanel?.isDraggingItem() || this.inventoryPanel?.hasDragStartSlot()) {
        this.inventoryPanel.handlePointerMove(pointer);
      }
      
      if (this.systemConsole?.isDraggingOrResizing()) {
        this.systemConsole.handlePointerMove(pointer);
      } else if (this.inventoryPanel?.isDraggingPanel()) {
        this.inventoryPanel.handlePointerMove(pointer);
      } else if (this.characterInfoPanel?.isDraggingPanel()) {
        this.characterInfoPanel.handlePointerMove(pointer);
      } else if (this.invokePanel?.isDraggingPanel()) {
        this.invokePanel.handlePointerMove(pointer);
      } else if (this.animPanel?.isDraggingPanel()) {
        this.animPanel.handlePointerMove(pointer);
      } else if (this.particlePanel?.isDraggingPanel()) {
        this.particlePanel.handlePointerMove(pointer);
      } else if (this.itemPanel?.isDraggingPanel()) {
        this.itemPanel.handlePointerMove(pointer);
      } else if (this.bodyGalleryPanel?.isDraggingPanel()) {
        this.bodyGalleryPanel.handlePointerMove(pointer);
      } else if (this.weaponGalleryPanel?.isDraggingPanel()) {
        this.weaponGalleryPanel.handlePointerMove(pointer);
      } else if (this.charFrameDebugPanel?.isDraggingPanel()) {
        this.charFrameDebugPanel.handlePointerMove(pointer);
      } else if (this.spellQuickBar?.isDraggingSlot()) {
        this.spellQuickBar.handlePointerMove(pointer);
      } else if (this.spellsPanel?.isDraggingPanel() || this.spellsPanel?.isDraggingSpell()) {
        this.spellsPanel.handlePointerMove(pointer);
      } else if (this.targets.targetPanel?.isDraggingOrResizing()) {
        this.targets.targetPanel.handlePointerMove(pointer);
      } else if (this.npcInfoPanel?.isDraggingPanel()) {
        this.npcInfoPanel.handlePointerMove(pointer);
      } else if (this.merchantPanel?.isDraggingPanel() || this.merchantPanel?.isDraggingItem() || this.merchantPanel?.isPointerOver(pointer)) {
        // El pointermove tiene que llegar al MerchantPanel cuando el cursor
        // está sobre él (para activar drag de item) o ya hay drag activo.
        this.merchantPanel.handlePointerMove(pointer);
      } else if (this.spellVendorPanel?.isDraggingPanel()) {
        this.spellVendorPanel.handlePointerMove(pointer);
      } else if (this.exchangePanel?.isDraggingPanel()) {
        this.exchangePanel.handlePointerMove(pointer);
      } else if (this.marketplacePanel?.isDraggingPanel()) {
        this.marketplacePanel.handlePointerMove(pointer);
      }

      // Cursor `drag` mientras se arrastra cualquier ventana/panel.
      this.cursor.updateDragCursor();
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {

      // Capturar ANTES de que los handlers de drag lo limpien: si veníamos de un
      // drag de hechizo (barra o spellbook), NO propagar el click al mundo (mover /
      // pickup de items del piso).
      const wasSpellDragging = !!(
        this.spellsPanel?.isDraggingSpell() || this.spellQuickBar?.isDraggingSlot()
      );

      if (this.systemConsole?.isDraggingOrResizing()) {
        this.systemConsole.handlePointerUp(pointer);
      }
      if (
        this.inventoryPanel?.isDraggingPanel() ||
        this.inventoryPanel?.isDraggingItem() ||
        this.inventoryPanel?.hasDragStartSlot()
      ) {
        this.inventoryPanel.handlePointerUp(pointer);
      }
      if (this.characterInfoPanel?.isDraggingPanel()) {
        this.characterInfoPanel.handlePointerUp(pointer);
      }
      if (this.invokePanel?.isDraggingPanel()) {
        this.invokePanel.handlePointerUp(pointer);
      }
      if (this.itemPanel?.isDraggingPanel()) {
        this.itemPanel.handlePointerUp(pointer);
      }
      if (this.animPanel?.isDraggingPanel()) {
        this.animPanel.handlePointerUp(pointer);
      }
      if (this.particlePanel?.isDraggingPanel()) {
        this.particlePanel.handlePointerUp(pointer);
      }
      if (this.bodyGalleryPanel?.isDraggingPanel()) {
        this.bodyGalleryPanel.handlePointerUp(pointer);
      }
      if (this.weaponGalleryPanel?.isDraggingPanel()) {
        this.weaponGalleryPanel.handlePointerUp(pointer);
      }
      if (this.charFrameDebugPanel?.isDraggingPanel()) {
        this.charFrameDebugPanel.handlePointerUp(pointer);
      }
      if (this.spellsPanel?.isDraggingPanel() || this.spellsPanel?.hasSpellDrag()) {
        this.spellsPanel.handlePointerUp(pointer);
      }
      if (this.spellQuickBar?.hasSlotDrag()) {
        this.spellQuickBar.handlePointerUp(pointer);
      }
      if (this.targets.targetPanel?.isDraggingOrResizing()) {
        this.targets.targetPanel.handlePointerUp(pointer);
      }
      if (this.npcInfoPanel?.isDraggingPanel()) {
        this.npcInfoPanel.handlePointerUp(pointer);
      }
      if (this.merchantPanel?.isDraggingPanel() || this.merchantPanel?.isDraggingItem()) {
        this.merchantPanel.handlePointerUp(pointer);
      }
      if (this.spellVendorPanel?.isDraggingPanel()) {
        this.spellVendorPanel.handlePointerUp();
      }
      if (this.exchangePanel?.isDraggingPanel()) {
        this.exchangePanel.handlePointerUp(pointer);
      }
      if (this.marketplacePanel?.isDraggingPanel()) {
        this.marketplacePanel.handlePointerUp(pointer);
      }

      // El drag terminó: si veníamos del cursor `drag`, volver a `main`.
      this.cursor.updateDragCursor();

      if (!wasSpellDragging && !this.interaction.isPointerOverAnyOpenPanel(pointer)) {
        this.interaction.handleWorldClick(pointer);
      }
    });

    // Configurar cámara: mundo de WORLD_TILES x WORLD_TILES tiles.

    // Ampliamos ligeramente los bounds para que la cámara pueda mantener

    // SIEMPRE al jugador en el centro de la pantalla, incluso cerca de los bordes

    // del mundo (se verá borde negro, pero el tile del jugador queda fijo en el centro).

    const worldWidth = GameScene.WORLD_TILES_X * TILE_SIZE;

    const worldHeight = GameScene.WORLD_TILES_Y * TILE_SIZE;

    const cam = this.cameras.main;

    const halfViewW = cam.width / 2;

    const halfViewH = cam.height / 2;

    cam.setBounds(-halfViewW, -halfViewH, worldWidth + cam.width, worldHeight + cam.height);

    // Configurar zoom inicial
    cam.setZoom(GameScene.ZOOM_DEFAULT);

    // Crear cámara UI separada (zoom fijo en 1, no sigue al jugador)
    this.uiCamera = this.cameras.add(0, 0, cam.width, cam.height);
    this.uiCamera.setScroll(0, 0);
    this.uiCamera.setZoom(1);
    this.uiCamera.setName('uiCamera');
    
    // Configurar filtros de cámara iniciales
    this.setupCameraFilters();

    // Pipeline de agua (displacement + highlight). Un solo pipeline batcheado que
    // se aplica a cada bloque de agua (ver createMapTileObject). Persiste en el
    // renderer entre restarts de la escena, por eso el guard has/get.
    const renderer = this.game.renderer;
    if (renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer) {
      this.waterPipeline = renderer.pipelines.has(WATER_PIPELINE_KEY)
        ? (renderer.pipelines.get(WATER_PIPELINE_KEY) as WaterPipeline)
        : (renderer.pipelines.add(WATER_PIPELINE_KEY, new WaterPipeline(this.game)) as WaterPipeline);
      // Cloak de invisibilidad (refracción + shimmer). Se aplica por sprite de una
      // entidad invisible (EntityVfxController). Ver docs/INVISIBILITY.md.
      this.invisibilityPipeline = renderer.pipelines.has(INVIS_PIPELINE_KEY)
        ? (renderer.pipelines.get(INVIS_PIPELINE_KEY) as InvisibilityPipeline)
        : (renderer.pipelines.add(INVIS_PIPELINE_KEY, new InvisibilityPipeline(this.game)) as InvisibilityPipeline);
    }

    // Listener para zoom con rueda del mouse (solo afecta cámara principal, no UI).
    // Requiere CTRL presionado: Ctrl + rueda arriba/abajo = zoom in/out.
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, gameObjects: Phaser.GameObjects.GameObject[], deltaX: number, deltaY: number) => {
      // Si el world map overlay está abierto, el wheel lo maneja él (zoom del mapa).
      if (this.mapOverlay?.isVisible()) return;
      if (!this.enableWheelZoom) return; // master toggle del feature
      // Solo zoomeamos con Ctrl presionado. Sin Ctrl, la rueda no hace zoom.
      const wheelEvent = pointer.event as WheelEvent | undefined;
      if (!wheelEvent?.ctrlKey) return;
      // Evitar el zoom de página del navegador (Ctrl + rueda).
      wheelEvent.preventDefault?.();
      // deltaY negativo = scroll up = zoom in
      // deltaY positivo = scroll down = zoom out
      const currentZoom = this.cameras.main.zoom;
      let newZoom = currentZoom;
      
      if (deltaY < 0) {
        // Zoom in
        newZoom = Math.min(currentZoom + GameScene.ZOOM_STEP, GameScene.ZOOM_MAX);
      } else if (deltaY > 0) {
        // Zoom out
        newZoom = Math.max(currentZoom - GameScene.ZOOM_STEP, GameScene.ZOOM_MIN);
      }
      
      if (newZoom !== currentZoom) {
        this.cameras.main.setZoom(newZoom);
        // Mostrar indicador de zoom con fade in/out
        this.showZoomIndicator(newZoom);
      }
    });

    // Layout inicial y en cada resize: UI siempre responsive

    this.layout.layoutUI();

    this.scale.on('resize', () => {
      this.mapDebugGridSystem?.drawGrid();

      this.layout.layoutUI();

      this.updateGridWindow(true);
    });

    // ===================================================================
    // GATE: warmup terminado — esperar a que el jugador elija personaje.
    // ===================================================================
    // Todo lo de arriba es trabajo independiente del personaje (texturas,
    // animaciones, superficies, pre-render del mapa, UI/HUD) y corrió durante el
    // login, detrás del overlay de auth. Recién ahora joineamos la Colyseus room.
    // En boot inicial, `characterReady` la resuelve provideCharacter() cuando el
    // jugador elige personaje; en scene.restart (translado cross-map) o
    // reconexión ya viene resuelta desde init() (characterId presente). Ver
    // docs/AUTH_AND_LOGIN.md.
    await this.characterReady;

    // La escena pudo haberse reiniciado (translado cross-map) mientras
    // esperábamos el gate: no seguir con una instancia muerta.
    if (!this.scene.isActive()) return;

    this.colyseusClient = new ColyseusClient(this.configData.serverUrl);

    const targetMapId = this.configData.mapId ?? DEFAULT_MAP_ID;

    this.room = await this.colyseusClient.joinGameRoom({
      characterId: this.configData.characterId,

      map: targetMapId,

      arrivalAnimationId: this.configData.arrivalAnimationId,

      randomizeArrival: this.configData.randomizeArrival,

      // Login-only spawn protection: el server NO la otorga si isMapChange=true.
      isMapChange: this.configData.isMapChange ?? false,
    });

    this.currentMapId = targetMapId;

    this.localSessionId = this.room.sessionId;

    // Listeners de estado + handlers de mensajes + reconexión: necesitan la room
    // ya joineada. Corren después de que todo el UI/HUD existe (más seguro que
    // antes: cuando Colyseus dispara onAdd del estado inicial, los paneles y la
    // cámara ya están creados).
    this.setupStateListeners();

    this.setupServerMessageHandlers();

    this.reconnect.setupReconnectionHandlers();

    // Reintentar el gate de fin de carga ahora que hay room (también se poolea
    // cada frame desde update() cuando el jugador local se renderiza).
    this.loadingScreenUi.checkAndHideLoadingScreen();
  }

  private cameraFilterUpdateCounter = 0;
  
  // =====================================================================
  // RENDERING FIX: Fixed timestep accumulator for consistent interpolation.
  // This accumulates real time and processes movement in fixed increments,
  // preventing jittery motion under variable frame rates.
  // =====================================================================
  private interpolationAccumulator = 0;
  private lastUpdateTimestamp = 0;
  
  update(_time: number, delta: number): void {
    if (!this.cursors || !this.colyseusClient || !this.room) return;

    // Drenar la foto inicial de entidades de a poco (repartir el burst del join en varios frames,
    // bajo la pantalla de carga) antes de cualquier otro trabajo del frame. No-op una vez vaciada.
    if (!this.entitiesPopulated) this.drainInitialEntityQueue();

    // =====================================================================
    // DELTA TIME CAPPING
    // =====================================================================
    // Cap delta to prevent extreme values during frame drops or tab switching.
    // 50ms max = 20 FPS minimum, prevents physics/animation jumps.
    // Note: Main interpolation uses absolute timestamps (this.time.now) so
    // it's already framerate-independent, but this cap helps other systems.
    // =====================================================================
    const cappedDelta = Math.min(delta, 50);
    this.currentDelta = cappedDelta; // Guardar para uso en interpolación

    // Audio del mundo (oyente/viewport 3D, música por zona, ambiente, heartbeat, baja vida, muerte).
    this.audioWorld.update(cappedDelta);

    // Ventanita del modo trabajo (tenés / restante en vivo). No-op si no estás trabajando.
    if (this.activeWorkNodeId) this.updateWorkPanel();

    // Death grayscale: si el local player está DEAD, aplicar grayscale postFX
    // a la cámara principal del juego. Como `cameras.main` sólo muestra
    // objetos con depth < 800 (game), el HUD (depth >= 800, en uiCamera)
    // no se ve afectado. Sólo se toca en transiciones para no re-aplicar
    // el shader en cada frame.
    if (this.localSessionId) {
      const localPlayer = this.room.state.players.get(this.localSessionId);
      const isLocalDead = localPlayer?.state === EntityState.DEAD;
      // Grayscale se aplica vía ColorMatrix FX (no existe addGrayscale directo
      // en el pipeline postFX de la cámara en Phaser 3.80).
      const postFX = this.cameras.main.postFX;
      if (isLocalDead && !this.deathGrayscaleApplied) {
        postFX.addColorMatrix().grayscale(1);
        this.deathGrayscaleApplied = true;
        // Bajo el grayscale, las almas usan blend NORMAL (ADD deja un cuadrado negro).
        this.setAllSoulsAdditive(false);
      } else if (!isLocalDead && this.deathGrayscaleApplied) {
        postFX.clear();
        this.deathGrayscaleApplied = false;
        // Sin grayscale, las almas vuelven a ADD (look "perfecto").
        this.setAllSoulsAdditive(true);
      }
    }

    // Update buff bar timers
    if (this.buffBar) {
      this.buffBar.update();
    }

    // Pipeline de agua: actualizar uniforms por frame (tiempo + worldView para
    // anclar la onda al mundo). Sólo si hay agua a la vista.
    if (this.waterPipeline && this.waterSprites.size > 0) {
      this.waterPipeline.setFrame(this.time.now / 1000, this.cameras.main.worldView);
    }
    // Cloak de invisibilidad: uniforms por frame (tiempo + worldView para anclar la
    // fase del pulso al mundo). Sólo si hay alguna entidad invisible a la vista.
    if (this.invisibilityPipeline && this.invisibleEntities.size > 0) {
      this.invisibilityPipeline.setFrame(this.time.now / 1000, this.cameras.main.worldView);
    }
    // Auto-cierre del MerchantPanel si el player se alejó del NPC (>2 tiles).
    // Throttled: chequea cada ~500ms.
    this.vendorPanels.maybeAutoCloseMerchantPanel();
    // Update spell quick bar cooldowns
    if (this.spellQuickBar) {
      this.spellQuickBar.update();
    }
    // Update cast bars above players
    this.castVisuals.updateCastBars();

    // Update minimapa (sigue al player, marcadores de NPCs neutrales / radar / players)
    this.updateMinimap();

    // Update active particle FX vía ParticleManager (no-op si no hay manager
    // o si no hay emitters activos — updateAll itera sobre Map interno).
    this.particleFx.updateActiveParticleEmitters(cappedDelta);

    // Update spell targeting preview + cancel with right click
    if (this.spellTargeting) {
      this.spellCast.updateSpellTargetingPreview();
      if (this.input.activePointer.rightButtonDown()) {
        this.spellCast.exitSpellTargeting();
      }
    }

    // World map overlay: actualizar posición del marker mientras camina.
    this.mapOverlay?.update();

    // Update time of day and lighting systems
    if (this.timeOfDayManager) {
      // Use manual time from debug slider if active, otherwise use TimeOfDayManager
      let hour: number;
      let darkness: number;
      
      if (this.useManualTime) {
        hour = this.manualHour;
        // Calculate darkness based on manual hour
        if (hour >= 6 && hour <= 18) {
          const distFromNoon = Math.abs(hour - 12);
          darkness = Math.max(0, (distFromNoon - 4) / 6) * 0.3;
        } else {
          const nightHour = hour < 6 ? hour + 6 : hour - 18;
          const distFromMidnight = Math.abs(nightHour - 6);
          darkness = 0.5 + (1 - distFromMidnight / 6) * 0.5;
        }
      } else {
        this.timeOfDayManager.update();
        hour = this.timeOfDayManager.getCurrentHour();
        darkness = this.timeOfDayManager.getAmbientDarkness();
        
        // Sync slider with real time
        if (this.timeSlider) {
          this.timeSlider.setValue(hour);
          this.lightingDebug.updateTimeLabel(hour);
        }
      }
      
      // Update shadow offsets based on time
      this.updateShadowTimeOffset(hour);
      
      // Update debug panel info
      this.lightingDebug.updateDebugPanelInfo(darkness);
      
      // Efecto de interior de casa (oscurecer alrededor + revelar interior + atenuar techo).
      this.mapAmbient.updateInteriorEffect(cappedDelta);

      // Update lighting system
      if (this.lightingSystem) {
        this.lightingSystem.setAmbientDarkness(darkness);
        this.lightingSystem.setInterior(this.interiorRevealRects, this.overlayLevel);
        this.lightingSystem.update(cappedDelta);
      }
    }

    // Viewport culling del mapa: recalcular chunks visibles + crear tiles
    // encolados con presupuesto por frame (evita picos al cruzar chunks).
    this.mapTiles.updateMapCulling(false);
    this.mapTiles.processTileCreateQueue();

    // Actualizar shaders de agua para todos los tiles de agua
    const timeInSeconds = this.time.now / 1000;
    for (const waterShader of this.waterTileShaders.values()) {
      if (waterShader && waterShader.active) {
        // setUniform acepta solo el valor cuando el uniform ya está definido en el shader
        waterShader.setUniform('time', timeInSeconds);
      }
    }

    // Actualizar filtros de cámara cada 30 frames para capturar nuevos objetos de UI
    this.cameraFilterUpdateCounter++;
    if (this.cameraFilterUpdateCounter >= 30) {
      this.cameraFilterUpdateCounter = 0;
      this.setupCameraFilters();
    }

    // Si el menú está activo o estamos reconectando, no procesamos nada
    if (this.isMenuOpen || this.isReconnecting) {
      return;
    }

    // Obtener moveSpeed del jugador local (default 1.0 si no está disponible)
    const localPlayer = this.localSessionId ? this.room?.state.players.get(this.localSessionId) : null;
    const localMoveSpeed = (localPlayer as any)?.moveSpeed ?? 1.0;

    // Procesar input (solo cuando no hay chat enfocado)
    // Nota: InputSystem ahora solo detecta cambios de dirección, no inicia movimientos
    this.inputSystem?.processFrame(
      {
        blocked: false,
        chatFocused: this.chatIsFocused,
        moveSpeed: localMoveSpeed,
      },
      this.time.now
    );

    // Re-sincronizar posiciones desde el estado del servidor en cada frame
    // IMPORTANTE: Primero procesamos la interpolación de todos los jugadores
    const state = this.room.state as unknown as GameStateView;
    const players = state.players as SchemaMap<PlayerStateView>;
    players.forEach((player, id) => {
      this.playerSync.addOrUpdatePlayer(id, player);
    });

    // Idem NPCs: re-leer su estado cada frame. NO alcanza con `npcs.onChange`
    // (MapSchema.onChange de Colyseus 0.15/schema 2.x no dispara confiablemente en
    // cambios de campos anidados como x/y), así que sin esto el sprite del mob
    // quedaba clavado en su tile de spawn aunque el server lo moviera. addOrUpdateNPC
    // es idempotente y barato para NPCs ya existentes (guarda por positionChanged).
    const npcsMap = state.npcs as SchemaMap<NPCStateView>;
    npcsMap.forEach((npc, id) => {
      this.npcSync.addOrUpdateNPC(id, npc);
    });

    // Preview del CharacterInfoPanel: polling timing-independiente (igual que
    // addOrUpdatePlayer hace para el sprite in-game). Detecta cambios de
    // visualIds del player local cuando el state patch finalmente llega y
    // re-renderiza el preview. No se puede hacer desde el handler de
    // INVENTORY_SYNC porque ahí el schema todavía tiene visualIds viejos.
    if (this.characterInfoPanel?.isOpenPanel()) {
      const visualIds = this.collectLocalPlayerVisualIds();
      if (visualIds) {
        const key = `${visualIds.bodyId},${visualIds.headId},${visualIds.helmId},${visualIds.bootId},${visualIds.weaponId},${visualIds.shieldId}`;
        if (key !== this.lastLocalVisualIdsKey) {
          this.lastLocalVisualIdsKey = key;
          this.characterInfoPanel.refreshCharacterPreview();
        }
      }
    }

    // =====================================================================
    // ESTILO ARGENTUMONLINE.IO: Intentar iniciar movimiento DESPUÉS de la interpolación
    // =====================================================================
    // Así cuando un movimiento termina en la interpolación, inmediatamente
    // se puede iniciar el siguiente en el MISMO frame (sin pausa de un frame).
    // =====================================================================
    if (!this.chatIsFocused) {
      this.movement.tryStartLocalMovement();
    }

    // =====================================================================
    // RENDERING FIX: Direct camera control for smooth, jitter-free movement.
    // Instead of relying on Phaser's follow system (which can introduce lag),
    // we directly center the camera on the local player's interpolated position
    // every frame. This ensures perfect synchronization.
    // =====================================================================
    this.cameraCtl.updateCameraPosition();

    // AO-like: Actualizar patrón de fondo (se mueve con la cámara automáticamente con scrollFactor)
    // No necesita actualización manual ya que usa scrollFactor(1)

    // Verificar si todo está listo para ocultar la pantalla de carga (en cada frame hasta que se oculte)

    this.loadingScreenUi.checkAndHideLoadingScreen();

    // Actualizar posición de los globos de chat sobre los jugadores

    this.overheadText.updateChatBubblesPositions();

    // AO-like: Actualizar posiciones de items en el suelo en cada frame
    this.groundItems.updateGroundItemsPositions();

    // AO-like: Actualizar posiciones de NPCs en cada frame
    this.npcSync.updateNPCsPositions();

    // Target lock server-authoritative: detectar cambios del schema y reflejar en UI.
    // IMPORTANTE: corre DESPUÉS de addOrUpdatePlayer/updateNPCsPositions para que el
    // retículo lea el currentWorldX/Y interpolado de este frame y no quede 1 frame atrás
    // (causa el flickering al moverse).
    this.targets.syncServerTarget();
    this.targets.updateTargetReticle();
    this.targets.updateTargetPanelLive();
    this.targets.updateNpcNameVisibility();

    // NOTA: Las decoraciones ground_deco_over ahora usan depth estático (como en el editor)
    // No necesitan actualización dinámica, se mueven automáticamente con el container

    // Actualizar grilla (líneas + coords) según la posición real del personaje
    const localPlayerPos = this.cameraCtl.getLocalPlayerWorldPosition();
    if (localPlayerPos) {
      this.mapDebugGridSystem?.setLocalPlayerWorldPosition(localPlayerPos.x, localPlayerPos.y);
    }
    this.updateGridWindow();

    // Parpadeo del cursor del chat

    if (this.chatIsFocused && this.chatCursor) {
      this.chatCursorBlinkTimer += this.game.loop.delta;

      const blinkInterval = 500; // 500ms para parpadeo

      const visible = Math.floor(this.chatCursorBlinkTimer / blinkInterval) % 2 === 0;

      this.chatCursor.setVisible(visible);
    }

    // Actualizar HUD de HP/Mana

    this.hud.updateHUDStats();
  }

  private pendingMoveToSlot: number | null = null;

  private applyInventorySync(message: InventorySyncMessage): void {
    const selectSlot = this.pendingMoveToSlot;
    this.pendingMoveToSlot = null; // Reset después de usar
    this.inventoryPanel?.applyInventorySync(message.inventory, selectSlot ?? undefined);
    // Cachear forma simplificada (con rolled rarity + affixes para tooltips).
    this.lastInventorySnapshot = message.inventory.map((s) => ({
      slotIndex: s.slotIndex,
      itemId: s.itemId,
      quantity: s.quantity,
      rolledRarity: s.rolledRarity,
      rolledAffixes: s.rolledAffixes,
      equippedIn: s.equippedIn,
      enchantId: s.enchantId,
    }));
    // Refrescar los iconos de slots (timing-independiente: vienen en el payload
    // del mensaje). El char preview lo maneja el polling per-frame en update()
    // porque depende de los visualIds del schema, que llegan después del mensaje.
    this.characterInfoPanel?.refreshEquippedSlots();
    // Indicador HUD de barco equipado (junto al indicador de zona).
    this.refreshBoatIndicator();
    // Pedir stats actualizados (el server también los emite tras equip/unequip,
    // pero el doble request es idempotente y cubre otras fuentes de inventory sync).
    if (this.characterInfoPanel?.isOpenPanel()) {
      this.requestCharacterStats();
    }
    if (this.merchantPanel?.isOpenPanel()) {
      this.merchantPanel.setInventory(this.lastInventorySnapshot);
    }
    if (this.spellVendorPanel?.isOpenPanel()) {
      this.spellVendorPanel.setInventory(this.lastInventorySnapshot);
    }
    if (this.exchangePanel?.isOpenPanel()) {
      this.exchangePanel.setInventory(this.lastInventorySnapshot);
    }
    if (this.marketplacePanel?.isOpenPanel()) {
      this.marketplacePanel.setInventory(this.lastInventorySnapshot);
    }
    if (this.craftingPanel?.isOpenPanel()) {
      this.craftingPanel.refresh();
    }
  }

  private handleEquipToggle(): void {
    this.inventoryPanel?.handleEquipToggle();
  }

  private handleDropItem(): void {
    this.inventoryPanel?.handleDropItem();
  }

  private sendInventoryDrop(slotIndex: number): void {
    if (!this.colyseusClient) return;

    const msg = {
      type: ClientMessageType.INVENTORY_DROP,
      slotIndex,
    } as never;

    this.colyseusClient.send(msg);
  }


  /** Muestra los tiles del rango de un aura alrededor de una entidad por 1s, luego fade-out. */
  private showAuraRangePreview(entityId: string, radiusTiles: number): void {
    const render = this.playerObjects.get(entityId);
    if (!render) return;

    const TILE = 32;
    const tileX = Math.floor(render.currentWorldX / TILE);
    const tileY = Math.floor(render.currentWorldY / TILE);

    const gfx = this.add.graphics().setDepth(500);

    for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
      for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
        const tx = (tileX + dx) * TILE;
        const ty = (tileY + dy) * TILE;

        if (dx === 0 && dy === 0) {
          gfx.fillStyle(0x4488ff, 0.25);
          gfx.fillRect(tx, ty, TILE, TILE);
          gfx.lineStyle(1, 0x4488ff, 0.6);
          gfx.strokeRect(tx, ty, TILE, TILE);
        } else {
          gfx.fillStyle(0x2266cc, 0.15);
          gfx.fillRect(tx, ty, TILE, TILE);
          gfx.lineStyle(1, 0x2266cc, 0.35);
          gfx.strokeRect(tx, ty, TILE, TILE);
        }
      }
    }

    // Fade in
    gfx.setAlpha(0);
    this.tweens.add({
      targets: gfx,
      alpha: 1,
      duration: 400,
      ease: 'Sine.easeOut',
    });

    // Fade out after 1 second
    this.time.delayedCall(1400, () => {
      this.tweens.add({
        targets: gfx,
        alpha: 0,
        duration: 500,
        onComplete: () => gfx.destroy(),
      });
    });
  }

  // =========================================================================
  // LEVEL UP SCREEN
  // =========================================================================

  private showLevelUpScreen(level: number, hpGain: number, manaGain: number, staminaGain: number): void {
    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2 - 50;

    // Dark overlay (subtle)
    const overlay = this.add.rectangle(cx, cy, 300, 160, 0x000000, 0.6)
      .setScrollFactor(0).setDepth(2000).setOrigin(0.5);

    // Level number big
    const levelText = this.add.text(cx, cy - 40, `NIVEL ${level}`, {
      fontFamily: GAME_FONT_FAMILY,
      fontSize: '28px',
      color: '#ffdd44',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
      resolution: 2,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(2001);

    // Stat gains
    const lines: string[] = [];
    if (hpGain > 0) lines.push(`+${hpGain} Vida`);
    if (manaGain > 0) lines.push(`+${manaGain} Maná`);
    if (staminaGain > 0) lines.push(`+${staminaGain} Stamina`);

    const statsText = this.add.text(cx, cy + 10, lines.join('\n'), {
      fontFamily: GAME_FONT_FAMILY,
      fontSize: '14px',
      color: '#88ff88',
      align: 'center',
      stroke: '#000000',
      strokeThickness: 1,
      resolution: 2,
    }).setOrigin(0.5, 0).setScrollFactor(0).setDepth(2001);

    // Fade in then fade out after 6 seconds
    const elements = [overlay, levelText, statsText];
    elements.forEach(e => e.setAlpha(0));
    this.tweens.add({ targets: elements, alpha: 1, duration: 300 });

    let dismissed = false;
    const dismiss = (): void => {
      if (dismissed) return;
      dismissed = true;
      this.tweens.add({
        targets: elements,
        alpha: 0,
        duration: 500,
        onComplete: () => elements.forEach(e => e.destroy()),
      });
    };

    const timer = this.time.delayedCall(6000, dismiss);

    // Click anywhere to dismiss
    const clickHandler = (): void => {
      timer.remove(false);
      dismiss();
      this.input.off('pointerdown', clickHandler);
    };
    this.input.on('pointerdown', clickHandler);
  }

  /**
   * Efecto visual de subida de nivel visible para todos los jugadores.
   * Placeholder: flash dorado + anillo expansivo (igual que entrada a partida).
   */
  private playLevelUpEffect(entityId: string): void {
    const render = this.playerObjects.get(entityId);
    if (!render) return;

    const x = render.sprite.x;
    const y = render.sprite.y;
    const depth = render.sprite.depth + 1;

    // Flash blanco sobre el personaje
    const flash = this.add.circle(x, y, 24, 0xffdd44, 0.7)
      .setDepth(depth)
      .setScrollFactor(1);

    this.tweens.add({
      targets: flash,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 600,
      ease: 'Sine.easeOut',
      onComplete: () => flash.destroy(),
    });

    // Anillo expansivo dorado
    const ring = this.add.circle(x, y, 16, 0x000000, 0)
      .setStrokeStyle(2, 0xffdd44, 1)
      .setDepth(depth)
      .setScrollFactor(1);

    this.tweens.add({
      targets: ring,
      scaleX: 4,
      scaleY: 4,
      alpha: 0,
      duration: 800,
      ease: 'Sine.easeOut',
      onComplete: () => ring.destroy(),
    });

    // Segundo anillo con delay
    const ring2 = this.add.circle(x, y, 16, 0x000000, 0)
      .setStrokeStyle(1.5, 0xffaa00, 0.8)
      .setDepth(depth)
      .setScrollFactor(1);

    this.tweens.add({
      targets: ring2,
      scaleX: 3.5,
      scaleY: 3.5,
      alpha: 0,
      duration: 700,
      delay: 150,
      ease: 'Sine.easeOut',
      onComplete: () => ring2.destroy(),
    });
  }


  // --- Cursor manager (cursores .cur custom) -------------------------------------------------
  // Aplica el cursor por su key de juego; cachea el último CSS aplicado para no re-setear el
  // style del canvas en cada pointermove (no-op si no cambió).
  public appliedCursorCss = '';


  /**
   * Tecla N: pide embarcar/desembarcar. El server decide la dirección según si
   * el player está navegando y arranca el canal de 2s. Ver docs/WATER_AND_BOATS.md.
   */
  private sendToggleBoat(): void {
    if (!this.colyseusClient) return;
    this.colyseusClient.send({ type: ClientMessageType.TOGGLE_BOAT } as never);
  }

  private sendInventoryEquip(slot: InventorySlotData): void {
    if (!this.colyseusClient) return;

    let equipSlot = 'body';
    // Weapons ahora usan el formato: weapon_subtipo_00X (ej: weapon_sword_001, weapon_staff_001)
    if (slot.itemId.startsWith('weapon_')) {
      equipSlot = 'weapon';
    } else if (slot.itemId.startsWith('shield_')) {
      equipSlot = 'shield';
    } else if (slot.itemId.startsWith('armor_') || slot.itemId.startsWith('body_')) {
      equipSlot = 'body';
    } else if (slot.itemId.startsWith('boots_')) {
      equipSlot = 'boots';
    } else if (slot.itemId.startsWith('helm_') || slot.itemId.startsWith('head_') || slot.itemId.startsWith('helmet_')) {
      equipSlot = 'head';
    } else if (slot.itemId.startsWith('ring_')) {
      equipSlot = 'ring';
    } else if (slot.itemId.startsWith('amulet_') || slot.itemId.startsWith('necklace_')) {
      equipSlot = 'amulet';
    } else if (slot.itemId.startsWith('boat_')) {
      equipSlot = 'boat';
    }

    const msg = {
      type: ClientMessageType.EQUIP_ITEM,
      itemId: slot.itemId,
      slot: equipSlot,
    } as never;

    this.colyseusClient.send(msg);

    // Sonido de equipar (por tipo de armadura del ítem; default para armas/otros).
    this.audioWorld.playEquipSound(slot.itemId);
  }

  /** Usar un item (invocación): manda USE_ITEM con su itemId. Ver docs/SUMMONS.md. */
  private sendInventoryUse(slot: InventorySlotData): void {
    if (!this.colyseusClient) return;
    const msg = {
      type: ClientMessageType.USE_ITEM,
      itemId: slot.itemId,
    } as never;
    this.colyseusClient.send(msg);
  }

  private sendInventoryUnequip(slot: InventorySlotData): void {
    if (!this.colyseusClient || !slot.equippedIn) return;

    const msg = {
      type: ClientMessageType.UNEQUIP_ITEM,
      slot: slot.equippedIn,
    } as never;

    this.colyseusClient.send(msg);
  }

  private sendInventoryMoveItem(fromSlot: number, toSlot: number): void {
    if (!this.colyseusClient) return;

    // Guardar el slot de destino para seleccionarlo después del sync
    this.pendingMoveToSlot = toSlot;

    const msg = {
      type: ClientMessageType.INVENTORY_MOVE_ITEM,
      fromSlot,
      toSlot,
    } as never;

    this.colyseusClient.send(msg);
  }




  /**

   * Busca un item en el piso en el tile actual del jugador local y envía

   * la petición de pickup al servidor.

   */


  public getDirectionFromInput(): Direction | null {
    return this.inputManager?.getDirectionFromInput() ?? null;
  }

  /**
   * Estado de pérdida de movilidad del player local synced por el server:
   * 0 = ninguno, 1 = inmovilizado, 2 = paralizado. Ver docs/CC_IMMOBILIZE_PARALYZE.md.
   */
  public getLocalImpairState(): number {
    if (!this.localSessionId) return 0;
    const ps = this.room?.state.players.get(this.localSessionId);
    return (ps as unknown as { impairState?: number } | undefined)?.impairState ?? 0;
  }

  /** True si el jugador local está provocado (taunt): el server maneja su movimiento. */
  public getLocalTaunted(): boolean {
    if (!this.localSessionId) return false;
    const ps = this.room?.state.players.get(this.localSessionId);
    return ((ps as unknown as { taunted?: number } | undefined)?.taunted ?? 0) === 1;
  }

  /**
   * CLIENT-SIDE COLLISION CHECK (estilo Argentum Online)
   * Verifica si el jugador local puede moverse a un tile específico.
   * El cliente ya tiene toda la información necesaria:
   * - Mapa con tiles bloqueados
   * - Posiciones de otros jugadores
   * - Posiciones de NPCs
   * 
   * @param tileX Coordenada X del tile destino
   * @param tileY Coordenada Y del tile destino
   * @returns true si el movimiento es válido, false si hay colisión
   */
  public canMoveTo(tileX: number, tileY: number): boolean {
    // 1. Verificar límites del mapa
    if (!this.currentMap) return false;
    const bounds = this.currentMap.bounds;
    if (tileX < bounds.minX || tileX > bounds.maxX || tileY < bounds.minY || tileY > bounds.maxY) {
      return false;
    }

    // 2. Verificar tiles bloqueados del mapa
    if (this.blockedTiles?.has(`${tileX},${tileY}`)) {
      return false;
    }

    // Si el player local está muerto camina como fantasma: atraviesa players y
    // NPCs HOSTILES, pero SIGUE chocando con NPCs no-hostiles (vendors, sacerdote,
    // etc.). Espeja la lógica del server para que la predicción no haga rubber-band.
    // Los muros (pasos 1 y 2) siguen bloqueando.
    const localPlayer = this.localSessionId
      ? this.room.state.players.get(this.localSessionId)
      : undefined;
    if (localPlayer?.state === EntityState.DEAD) {
      for (const [npcId, npcRender] of this.npcObjects) {
        const npcTileX = Math.round(npcRender.currentWorldX / TILE_SIZE - 0.5);
        const npcTileY = Math.round(npcRender.currentWorldY / TILE_SIZE - 0.5);
        if (npcTileX === tileX && npcTileY === tileY) {
          const templateId = this.room.state.npcs.get(npcId)?.templateId;
          const hostile = templateId ? getNPCTemplate(templateId)?.hostile === true : false;
          if (!hostile) return false; // NPC no-hostil bloquea al fantasma
        }
      }
      return true;
    }

    // Agua navegable: espeja la regla del server (performPlayerStep) para que
    // la predicción no haga rubber-band. Sin barco, el agua profunda bloquea;
    // navegando (sailing), SOLO se puede pisar agua (la costa/tierra bloquea
    // hasta desembarcar). El fantasma (arriba) cruza libre. Ver docs/WATER_AND_BOATS.md.
    const destIsWater = this.waterTiles?.has(`${tileX},${tileY}`) ?? false;
    const sailing = localPlayer?.sailing === true;
    if (destIsWater !== sailing) {
      return false;
    }

    // 3. Verificar colisión con otros jugadores
    for (const [playerId, playerRender] of this.playerObjects) {
      // No colisionar con nosotros mismos
      if (playerId === this.localSessionId) continue;
      
      // Obtener tile actual del jugador (basado en su posición visual)
      const playerTileX = Math.round(playerRender.currentWorldX / TILE_SIZE - 0.5);
      const playerTileY = Math.round(playerRender.currentWorldY / TILE_SIZE - 0.5);
      
      if (playerTileX === tileX && playerTileY === tileY) {
        return false;
      }
    }

    // 4. Verificar colisión con NPCs
    for (const [_npcId, npcRender] of this.npcObjects) {
      const npcTileX = Math.round(npcRender.currentWorldX / TILE_SIZE - 0.5);
      const npcTileY = Math.round(npcRender.currentWorldY / TILE_SIZE - 0.5);
      
      if (npcTileX === tileX && npcTileY === tileY) {
        return false;
      }
    }

    return true;
  }

  /**
   * Calcula el tile destino basado en la posición actual y una dirección
   */

  /**
   * true si (tileX, tileY) es "costa": el tile pisado NO es agua pero al menos
   * uno de sus 4 vecinos ortogonales sí lo es (borde del agua). Reusa
   * `waterTileSet` construido en buildMapTileIndex.
   */
  public isCoastTile(tileX: number, tileY: number): boolean {
    if (this.waterTileSet.size === 0) return false;
    if (this.waterTileSet.has(`${tileX},${tileY}`)) return false; // parado en el agua, no es costa
    return (
      this.waterTileSet.has(`${tileX + 1},${tileY}`) ||
      this.waterTileSet.has(`${tileX - 1},${tileY}`) ||
      this.waterTileSet.has(`${tileX},${tileY + 1}`) ||
      this.waterTileSet.has(`${tileX},${tileY - 1}`)
    );
  }

  /**
   * Intenta iniciar movimiento del jugador local (estilo argentumonline.io tratarDeMover)
   * 
   * Esta función se llama CADA FRAME al inicio del update, ANTES de procesar transiciones.
   * Si hay tecla presionada Y no hay movimiento activo, inicia el movimiento.
   * 
   * Esto es exactamente como funciona argentumonline.io:
   * - tratarDeMover() se llama cada frame
   * - Solo mueve si estaCaminando() && !estaMoviendose()
   * 
   * IMPORTANTE: Limitamos cuántos movimientos puede predecir el cliente sin confirmación
   * del servidor para evitar desync severo.
   */

  /**

   * Maneja un click en el mundo de juego: detecta el tile sobre el que

   * se hizo click y, si hay entidades (jugadores o NPCs) en ese tile,

   * escribe en la consola de sistema su nombre y coordenadas.

   */

  /**
   * True si el pointer está sobre alguna ventana abierta (o hay un panel modal
   * abierto). Centraliza el chequeo para que un click sobre el fondo de una
   * ventana NO atraviese al juego (target/tile-info/spell-cast). Las ventanas
   * flotantes bloquean sólo su propio rectángulo; los modales existentes
   * (menú, /invocar, body gallery) bloquean todo mientras están abiertos.
   */

  /**
   * True si el player local es admin (PlayerState.isAdmin, sincronizado del
   * server). Gatea el input de acciones de staff localmente; el server siempre
   * revalida. No hay sistema de admins todavía: el flag viene de characters.is_admin.
   */
  public isLocalAdmin(): boolean {
    if (!this.localSessionId || !this.room) return false;
    return this.room.state.players.get(this.localSessionId)?.isAdmin === true;
  }

  /**
   * True si el player local está muerto (mismo source que el grayscale en update()).
   * Un muerto sólo puede seleccionar NPCs que reviven (ver isPriestNpc).
   */
  public isLocalPlayerDead(): boolean {
    if (!this.localSessionId || !this.room) return false;
    return this.room.state.players.get(this.localSessionId)?.state === EntityState.DEAD;
  }

  /**
   * True si el id corresponde a un NPC sacerdote (priestService). Es lo único que
   * un player muerto puede targetear. El template se resuelve localmente vía templateId
   * del NPCState (mismo lookup que usa applyServerTarget para boss/typeIcon).
   */
  public isPriestNpc(id: string): boolean {
    if (!this.room || !this.npcObjects.has(id)) return false;
    const npcState = this.room.state.npcs.get(id) as NPCStateView | undefined;
    const templateId = npcState?.templateId;
    if (!templateId) return false;
    return getNPCTemplate(templateId)?.priestService === true;
  }



  // -----------------
  // Icon Atlas Viewer (dev tool — Ctrl+I)
  // -----------------

  private createIconViewerPanel(): void {
    const { width, height } = this.scale;
    this.iconViewerPanel = new IconAtlasViewerPanel(this, {
      initialX: Math.max(20, (width - 540) / 2),
      initialY: Math.max(20, (height - 460) / 2),
    });
  }

  public toggleIconViewerPanel(): void {
    if (!this.iconViewerPanel) {
      this.createIconViewerPanel();
    }
    this.iconViewerPanel!.toggle();
  }




  // Visuales de pérdida de movilidad (driveados por el impairState synced).
  public static readonly ROOTS_FX_ID = 'fx_roots_immobilize';
  public static readonly PARALYZE_TINT = 0x8a8a9a; // gris-azulado "pétreo"
  // Halo dorado cálido de la protección de aparición (familia del 0xd4af37 de la viñeta).
  public static readonly SPAWN_PROTECTION_GLOW = 0xffd86b;






  public drawGrid(): void {
    this.createOrUpdateGridSystem();
    this.mapDebugGridSystem?.drawGrid();
  }

  private createOrUpdateGridSystem(): void {
    if (!this.mapDebugGridSystem) {
      this.mapDebugGridSystem = new MapDebugGridSystem(
        this,
        {
          showGrid: this.showGrid,
          showGridCoords: this.showGridCoords,
          showBorderBlocks: this.showBorderBlocks,
          showFloorBlocks: this.showFloorBlocks,
        },
        this.blockedTiles ?? new Set<string>()
      );
      return;
    }

    this.mapDebugGridSystem.updateFlags({
      showGrid: this.showGrid,
      showGridCoords: this.showGridCoords,
      showBorderBlocks: this.showBorderBlocks,
      showFloorBlocks: this.showFloorBlocks,
    });
    this.mapDebugGridSystem.setBlockedTiles(this.blockedTiles ?? new Set<string>());
  }


  /**

   * Carga un mapa lógico (MapData) y crea sus sprites en pantalla.

   */

  /**
   * Verifica si un tile es de tipo agua
   * Detecta tiles de agua por su sprite o tipo de surface
   */

  /** Gris de 24 bits (R=G=B) que codifica la calma 0..1 en el canal R del tint. */
  public static grayCalmTint(calm: number): number {
    const g = Math.round(Phaser.Math.Clamp(calm, 0, 1) * 255);
    return (g << 16) | (g << 8) | g;
  }

  /** Calma de un bloque cualquiera; 0 si no es agua (costa/tierra → oleaje plano). */


  /** Capas de suelo cuyas superficies (pasto/arena, centro o borde) marcan ORILLA. */
  public static isShoreSurfaceLayer(name: string): boolean {
    return (
      name === 'ground' ||
      name === 'layer1_ground' ||
      name === 'ground_deco_under' ||
      name === 'layer2_floor_objects'
    );
  }

  /**
   * Factor de "calma" (0..1) de un bloque de agua. 0 = agua baja (cyan / oleaje plano),
   * 1 = agua profunda. El bajío cyan queda en el AGUA ABIERTA pegada a la orilla, NO en
   * el agua tapada por la costa: así el efecto no asoma por el borde difuminado del pasto
   * (queda "debajo/afuera" de la costa). Regla:
   *  - Bloque con costa/tierra encima (`shoreBlockSet`, agua de relleno bajo el pasto) →
   *    profundo (1.0), nada de cyan bajo el pasto.
   *  - Agua abierta (sin costa encima) que TOCA la orilla → bajío (cyan), en el agua.
   *  - Resto de mar abierto → profundo (1.0).
   */

  private updateMinimap(): void {
    if (!this.minimap || !this.currentMap || !this.localSessionId) return;
    const render = this.playerObjects.get(this.localSessionId);
    if (!render) return;

    // Posición CONTINUA en coords de tile (sin redondear): el minimapa sigue el movimiento
    // interpolado del player de forma suave, sin saltos de a un tile. Tile N centrado ⇒ N.
    const ptx = render.currentWorldX / TILE_SIZE - 0.5;
    const pty = render.currentWorldY / TILE_SIZE - 0.5;

    const npcs: MinimapNpc[] = [];
    this.room?.state.npcs.forEach((npc) => {
      npcs.push({
        x: npc.x,
        y: npc.y,
        attackable: npc.attackable ?? 0,
        interact: npc.interact ?? 0,
      });
    });

    // Aliados de facción en el minimapa: visibles desde rango Teniente
    // (MINIMAP_ALLY_RANK_INDEX). Marca a los players de la MISMA facción con el
    // color de la facción. Ver docs/FACTIONS.md.
    const showAllies =
      this.localFaction !== '' && this.localFactionRank >= MINIMAP_ALLY_RANK_INDEX;
    const otherPlayers: MinimapEntity[] = [];
    if (showAllies) {
      const allyColor = Phaser.Display.Color.HexStringToColor(getFactionColor(this.localFaction as FactionId)).color;
      this.room?.state.players.forEach((p, id) => {
        if (id === this.localSessionId) return;
        if ((p.faction ?? '') !== this.localFaction) return;
        otherPlayers.push({ x: p.x, y: p.y, color: allyColor });
      });
    }
    this.minimap.showOtherPlayers = showAllies;

    this.minimap.update({
      playerX: ptx,
      playerY: pty,
      npcs,
      otherPlayers,
      areas: this.currentMap.areas,
    });

    // Indicador de tipo de zona (HUD abajo-izquierda): derivado de las áreas que
    // cubren el tile del player. Solo cambia la textura en transiciones.
    if (this.zoneIndicator) {
      const zoneAreas = getAreasAtTile(this.currentMap.areas, Math.round(ptx), Math.round(pty));
      const zoneType = classifyZoneType(zoneAreas);
      if (zoneType !== this.currentZoneType) {
        this.currentZoneType = zoneType;
        this.zoneIndicator.setTexture(`zone_icon_${zoneType}`).setDisplaySize(14, 14);
      }
    }
  }



  /**
   * Recarga el mapa actual, destruyendo los sprites existentes y recreándolos.
   * Útil cuando las texturas se cargan después de que el mapa ya se dibujó.
   */
  private reloadCurrentMap(): void {
    if (!this.currentMap) return;
    
    // Destruir todos los GameObjects de tile activos + índice de culling.
    this.mapTiles.clearMapTiles();
    for (const sprite of this.mapTileSprites) {
      sprite.destroy();
    }
    this.mapTileSprites = [];

    // Destruir todos los shaders de agua (por si quedó alguno fuera del índice)
    for (const waterShader of this.waterTileShaders.values()) {
      waterShader.destroy();
    }
    this.waterTileShaders.clear();
    
    // Clear only decoration light sources (entity lights are preserved)
    if (this.lightingSystem) {
      this.lightingSystem.clearDecorationLights();
    }
    
    // Recrear el mapa
    this.mapLoader.loadAndCreateMap(this.currentMap);
  }

  /**

   * Calcula una profundidad coherente para un tile de mapa, en función de su capa

   * y su coordenada Y en mundo. Esto permite que, por ejemplo, los árboles puedan

   * tapar al personaje cuando éste se mete "detrás".

   */

  public getDepthForMapLayer(

    layerName: string,

    tileX: number,

    tileY: number,

    zOverride?: number

  ): number {
    // Desempate determinístico por X: evita que dos decoraciones de la misma fila que se
    // solapan compartan depth exacto (y por ende su orden de pintado titile al moverse).
    const xTie =
      (Phaser.Math.Clamp(tileX, 0, WORLD_TILES - 1) / WORLD_TILES) *
      GameScene.DECO_X_TIEBREAK_RANGE;

    if (typeof zOverride === 'number') {
      return this.getDepthFromTileY(tileY) + zOverride + xTie;
    }

    // =========================================================================
    // SISTEMA DE 4 CAPAS + ENTIDADES
    // =========================================================================
    // Orden de renderizado (de atrás hacia adelante):
    //   1. Capa 1 (ground)        → DEPTH FIJO -200  - Suelo base (SIEMPRE atrás)
    //   2. Capa 2 (floor_objects) → DEPTH FIJO -100  - Caminos, alfombras (SIEMPRE debajo de entidades)
    //   3. Capa 3 (deco_over)     → DEPTH DINÁMICO   - Árboles, columnas (REGLA ESPECIAL con Y)
    //   4. Entidades              → DEPTH DINÁMICO   - Jugadores, NPCs (offset 0.5)
    //   5. Capa 4 (roof)          → DEPTH FIJO 800   - Techos (SIEMPRE encima)
    //
    // REGLA ESPECIAL de Capa 3:
    //   - Si entidad.Y >= decoración.Y → Entidad DELANTE (entidad más abajo en pantalla)
    //   - Si entidad.Y < decoración.Y  → Decoración DELANTE (tapa a la entidad)
    //   Esto funciona porque el depth BASE depende de Y (tiles con Y mayor = más adelante)
    //
    // NOTA: Capas 1, 2 y 4 usan depth FIJO para que sprites multi-tile no
    //       tengan problemas de ordenamiento con entidades en diferentes Y.
    // =========================================================================

    switch (layerName) {
      // CAPA 1: Suelo base - DEPTH FIJO (siempre atrás de todo)
      case 'ground':
      case 'layer1_ground':
        return -200;

      // CAPA 2: Decoración de piso - DEPTH FIJO (siempre debajo de entidades)
      // Sprites multi-tile no afectan el ordenamiento con entidades
      case 'ground_deco_under':
      case 'layer2_floor_objects':
        return -100;

      // CAPA 3: Decoración con interacción Y (árboles, columnas)
      // USA DEPTH DINÁMICO basado en Y para la regla especial
      // Offset 0.4 < Entidades 0.5 → En mismo tile, entidad adelante
      case 'ground_deco_over':
      case 'layer3_solid':
        return this.getDepthFromTileY(tileY) + 0.4 + xTie;

      // CAPA 4: Techos - DEPTH FIJO (siempre encima del mundo, pero DEBAJO del umbral
      // de UI: `configureObjectCamera` manda los objetos con depth >= UI_DEPTH_THRESHOLD
      // (800) a la cámara de UI —fija a pantalla, sin scroll del mundo—, así que un techo
      // en 800 quedaba pegado al HUD y no se veía en el mundo.
      // Van a 798: encima de personajes/decoración (~700) y debajo del HUD, pero
      // JUSTO por debajo del overlay de iluminación (LightingSystem.OVERLAY_DEPTH = 799)
      // a propósito, para que la oscuridad/luz ambiental los afecte igual que al resto
      // del mundo (a 799 empataban con el overlay y, por orden de inserción, quedaban
      // dibujados encima → nunca se oscurecían).
      case 'roof':
      case 'layer4_roof':
        return GameScene.UI_DEPTH_THRESHOLD - 2;

      default:
        return this.getDepthFromTileY(tileY) + xTie;
    }
  }

  /**

   * Normaliza la coordenada Y de grilla (0..WORLD_TILES-1) a un rango de depth

   * seguro para el mundo (0..~700), dejando espacio libre para toda la UI por encima.

   */

  private getDepthFromTileY(tileY: number): number {
    const clamped = Phaser.Math.Clamp(tileY, 0, WORLD_TILES - 1);

    const normalized = clamped / WORLD_TILES; // 0..1

    return normalized * 700;
  }

  /**

   * Actualiza la porción de grilla (líneas + coords) visible en pantalla

   * más un margen de tiles alrededor para evitar popping.

   */

  /**
   * AO-like: Actualiza posiciones de items en el suelo en cada frame
   * para que se muevan correctamente con el mundo
   */

  /** Hide all visible ground item and NPC hover labels (called when player moves) */
  public hideAllHoverLabels(): void {
    this.groundItemObjects.forEach((obj) => {
      if (obj.label.visible) {
        obj.label.setVisible(false);
        obj.label.setAlpha(0);
      }
    });
    // NPCs: limpiar el flag de hover. updateNpcNameVisibility() oculta el label
    // en consecuencia (en modo 'always' el nombre sigue, no depende del hover).
    this.npcObjects.forEach((obj) => {
      obj.nameHovered = false;
    });
  }

  private updateGridWindow(force: boolean = false): void {
    // Actualizar las coordenadas del tile del jugador local si existe
    if (this.localSessionId && this.mapDebugGridSystem && this.room) {
      // Usar la posición del servidor (más confiable) para el tile marcado
      const playerState = this.room.state.players.get(this.localSessionId);
      if (playerState) {
        // El servidor usa coordenadas de tile directamente (player.x, player.y)
        this.mapDebugGridSystem.setPlayerTile(playerState.x, playerState.y);
      } else {
        this.mapDebugGridSystem.setPlayerTile(null, null);
      }
    }
    
    this.mapDebugGridSystem?.updateGridWindow(force);
  }



  /**
   * Dibuja un crosshair en el centro de la pantalla (fijo, no se mueve con la cámara)
   * Esto ayuda a diagnosticar si el personaje está centrado correctamente
   */
  public updateCrosshair(): void {
    if (!this.showCrosshair) {
      if (this.crosshairGraphics) {
        this.crosshairGraphics.clear();
      }
      return;
    }

    if (!this.crosshairGraphics) {
      this.crosshairGraphics = this.add.graphics();
      this.crosshairGraphics.setScrollFactor(0); // Fijo en pantalla
      this.crosshairGraphics.setDepth(100000); // Siempre visible
    }

    const gfx = this.crosshairGraphics;
    gfx.clear();

    const { width, height } = this.scale;
    const centerX = width / 2;
    const centerY = height / 2;

    // Líneas cyan semi-transparentes
    gfx.lineStyle(1, 0x00ffff, 0.7);
    
    // Línea horizontal
    gfx.beginPath();
    gfx.moveTo(0, centerY);
    gfx.lineTo(width, centerY);
    gfx.strokePath();
    
    // Línea vertical
    gfx.beginPath();
    gfx.moveTo(centerX, 0);
    gfx.lineTo(centerX, height);
    gfx.strokePath();
    
    // Cuadrado pequeño en el centro
    const boxSize = 8;
    gfx.strokeRect(centerX - boxSize/2, centerY - boxSize/2, boxSize, boxSize);
  }

  /**

   * Configura los handlers de mensajes del servidor (chat, ataques, errores, etc.)

   */

  public setupServerMessageHandlers(): void {
    if (!this.colyseusClient) return;

    // Quests: crear la UI (tracker + diálogo + log) antes de recibir QUEST_LOG_SYNC.
    if (!this.questUI) {
      this.questUI = new QuestUI(this, {
        onAccept: (questId, npcId) =>
          this.colyseusClient.send({ type: ClientMessageType.ACCEPT_QUEST, questId, npcId } as never),
        onTurnIn: (questId, npcId) =>
          this.colyseusClient.send({ type: ClientMessageType.TURN_IN_QUEST, questId, npcId } as never),
        onAbandon: (questId) =>
          this.colyseusClient.send({ type: ClientMessageType.ABANDON_QUEST, questId } as never),
        getCharacterId: () => this.configData?.characterId ?? null,
      });
      // Hotkey 'L' = abrir/cerrar el quest log (ignorado mientras se tipea en el chat).
      this.input.keyboard?.on('keydown-L', () => {
        if (this.chatIsFocused) return;
        this.questUI?.toggleLog();
      });
    }

    // Party: crear el controlador (ventana + buscador + invitaciones) y su hotkey 'P'.
    if (!this.partyController && this.localSessionId) {
      this.partyController = new PartyController(
        this,
        this.colyseusClient,
        this.localSessionId,
        (text, isError) => this.chat.addSystemMessage(text, isError ? '#e06a6a' : '#e8dcb0')
      );
      // Hotkey 'G' = abrir/cerrar la ventana del party (Grupo). 'P' ya es el Árbol.
      this.input.keyboard?.on('keydown-G', () => {
        if (this.chatIsFocused) return;
        this.partyController?.togglePanel();
      });
    }

    registerServerMessageHandlers(this.colyseusClient, {
      onChatMessage: (m) => this.chat.addChatMessage(m.playerName, m.message),
      onAttackResult: (m) => {
        this.overheadText.showDamageText(m.targetId, m.damage, m.isCrit);
        // SFX por resultado del golpe físico (posicional en el que recibe). Ver docs/SOUND_AND_MUSIC.md.
        if (m.evaded) {
          this.audioWorld.onMiss(m.targetId); // whoosh (fallo)
        } else if (m.blocked) {
          this.audioWorld.onBlock(m.targetId, m.shieldSubtype); // bloqueo (metal vs default)
        } else if (m.damage > 0) {
          this.audioWorld.onImpact(m.targetId, m.armorSubtype); // impacto por armadura
        }
      },
      onMeleeSwing: (m) => {
        this.particleFx.playMeleeSwingFx(m.entityId, m.direction);
        // SFX de golpe melee del atacante (default; override por arma cuando esté cableado el equip).
        this.audioWorld.onMeleeSwing(m.entityId);
      },
      onAoeTelegraph: (m) => this.showAoeTelegraph(m.x, m.y, m.size),
      onNpcTemplateList: (m) => this.invoke.handleNpcTemplateListMessage(m),
      onNpcDialogue: (m) => {
        this.worldMsg.handleNpcDialogueMessage(m);
        // SFX de "saludo/habla" del NPC (sólo si su template define sounds.greet).
        this.audioWorld.playNpcSound(m.npcId, 'greet');
      },
      onGroundItemsSync: (m) => {
        console.log(`[GameScene] Received GROUND_ITEMS_SYNC with ${m.items.length} items`);
        this.groundItems.applyGroundItemsSync(m);
      },
      onItemTemplateList: (m) => this.itemPanelCtl.handleItemTemplateListMessage(m),
      onInventorySync: (m) => this.applyInventorySync(m),
      onPlayAnimation: (m) => this.animPanelCtl.handlePlayAnimation(m),
      onCharacterStats: (m) => this.handleCharacterStats(m),
      onTileInfo: (m) => this.handleTileInfo(m),
      // Skill Tree (Espíritu)
      onSkillTreeSync: (m) => this.passiveTreeUi.handleSkillTreeSync(m),
      onSkillNodeAllocated: (m) => this.passiveTreeUi.handleSkillNodeAllocated(m),
      onSkillTreeReset: (m) => this.passiveTreeUi.handleSkillTreeReset(m),
      // Spell System
      onSpellCastResult: (m) => {
        const spell = getSpellDefinition(m.spellId);
        const spellName = spell?.nameEs ?? m.spellId;
        const onHitFxId = spell?.onHitFxId;

        // FX de buffs — momento "casteo": one-shot sobre el caster (ver docs/PARTICLES.md).
        if (m.success && spell?.castFxId) {
          this.particleFx.ensureParticleManager().spawn(spell.castFxId, {
            kind: 'entity',
            entityId: m.casterId,
            slot: 'center',
          });
        }
        // SFX del hechizo — momento "casteo/fin": sobre el caster (sólo si el spell define sonido).
        if (m.success && spell?.sounds?.castEnd) {
          this.audioWorld.playAtEntity(spell.sounds.castEnd, m.casterId);
        }
        // FX de buffs — momento "aplicación": ids one-shot de los effects buff/debuff.
        const applyFxIds = (spell?.effects ?? [])
          .filter((e) => (e.type === 'buff' || e.type === 'debuff') && !!e.applyFxId)
          .map((e) => (e as { applyFxId?: string }).applyFxId as string);

        // Color del número de daño según la ESCUELA del hechizo (física/mágica/pura).
        const dmgType = (spell?.effects ?? []).find((e) => e.type === 'damage') as
          | { damageType?: string }
          | undefined;
        const schoolColor =
          dmgType?.damageType === 'magical'
            ? '#8ab4ff' // arcano (azul)
            : dmgType?.damageType === 'pure'
              ? '#ffffff' // puro (blanco)
              : undefined; // físico → rojo por defecto

        // Show damage/heal numbers + console messages + onHit FX
        for (const t of m.targets) {
          if (t.damage) this.overheadText.showDamageText(t.entityId, t.damage, t.isCrit ?? false, schoolColor);
          if (t.heal) this.overheadText.showHealthManaGainText(t.entityId, t.heal, true, true);

          if (onHitFxId && t.damage) {
            const mgr = this.particleFx.ensureParticleManager();
            mgr.spawn(onHitFxId, { kind: 'entity', entityId: t.entityId, slot: 'center' });
          }
          // SFX de impacto del hechizo sobre cada objetivo dañado (sólo si el spell lo define).
          if (t.damage && spell?.sounds?.impact) {
            this.audioWorld.playAtEntity(spell.sounds.impact, t.entityId);
          }
          // FX de aplicación de buff sobre cada objetivo afectado.
          if (m.success && applyFxIds.length > 0) {
            const mgr = this.particleFx.ensureParticleManager();
            for (const fxId of applyFxIds) {
              mgr.spawn(fxId, { kind: 'entity', entityId: t.entityId, slot: 'center' });
            }
          }

          // Console message for local caster
          if (m.casterId === this.localSessionId) {
            const targetName = this.playerObjects.get(t.entityId)?.label?.text
              ?? (this as any).npcObjects?.get(t.entityId)?.label?.text
              ?? t.entityId;
            if (t.heal) {
              this.chat.addSystemMessage(`Has lanzado ${spellName} sobre ${targetName} y has restaurado ${t.heal} de vida`);
            }
            if (t.damage) {
              this.chat.addSystemMessage(`Has lanzado ${spellName} sobre ${targetName} y has causado ${t.damage} de daño${t.isCrit ? ' (CRITICO)' : ''}`);
            }
          }
        }
        // Start cooldown visual on quick bar
        if (m.success && m.casterId === this.localSessionId) {
          if (spell && spell.cooldown > 0) {
            this.spellQuickBar?.startCooldown(m.spellId, spell.cooldown * 1000);
          }
        }
      },
      onSpellLearned: (m) => {
        this.spellsPanel?.addLearnedSpell(m.spellId);
        console.log(`[GameScene] Spell learned: ${m.spellId}`);
      },
      onSpellBarSync: (m) => {
        this.spellQuickBar?.syncSlots(m.slots);
      },
      onLearnedSpellsSync: (m) => {
        this.spellsPanel?.setLearnedSpells(m.spells);
        console.log(`[GameScene] Learned spells: ${m.spells.length}`);
      },
      onAuraToggled: (m) => {
        if (m.entityId === this.localSessionId) {
          const spell = getSpellDefinition(m.spellId);
          const reserve = spell?.aura?.manaReservePercent ?? 0;
          if (m.active) {
            this.manaReservedPercent += reserve;
            // Show aura range tiles for 1 second then fade out
            if (spell?.aura) {
              this.showAuraRangePreview(m.entityId, spell.aura.radiusTiles);
            }
          } else {
            this.manaReservedPercent = Math.max(0, this.manaReservedPercent - reserve);
          }
          // Start cooldown only on deactivation (not on activation/restore)
          if (!m.active && spell && spell.cooldown > 0) {
            this.spellQuickBar?.startCooldown(m.spellId, spell.cooldown * 1000);
          }
        }
      },
      onSummonModeChanged: (m) => {
        const label = SUMMON_MODE_LABEL_ES[m.mode as SummonMode] ?? m.mode;
        this.chat.addSystemMessage(`Invocaciones: modo ${label}`);
      },
      onCastStarted: (m) => {
        console.log(`[GameScene] CAST_STARTED: entity=${m.entityId} spell=${m.spellId} castTime=${m.castTime}s`);
        this.castVisuals.showCastBar(m.entityId, m.spellId, m.castTime);
        // SFX de inicio de casteo (sólo si el spell lo define).
        const castStart = getSpellDefinition(m.spellId)?.sounds?.castStart;
        if (castStart) this.audioWorld.playAtEntity(castStart, m.entityId);
      },
      onCastInterrupted: (m) => {
        console.log(`[GameScene] CAST_INTERRUPTED: entity=${m.entityId}`);
        this.castVisuals.removeCastBar(m.entityId);
        this.castVisuals.removeCastAnimation(m.entityId);
      },
      onBoatChannelStarted: (m) => {
        this.castVisuals.showBoatBar(m.entityId, m.kind, m.duration / 1000);
      },
      onBoatChannelInterrupted: (m) => {
        this.castVisuals.removeCastBar(m.entityId);
      },
      onSpellWords: (m) => {
        this.overheadText.showSpellWords(m.entityId, m.words);
        if (m.castAnimationId) {
          this.castVisuals.playCastAnimation(m.entityId, m.castAnimationId);
        }
      },
      onMeditateState: (m) => {
        this.stateVfx.handleMeditateState(m.entityId, m.meditating);
      },
      onSpawnProtectionState: (m) => {
        this.stateVfx.handleSpawnProtectionState(m.entityId, m.active, m.durationMs);
      },
      onLevelUp: (m) => {
        // Show level up notification for local player
        if (m.entityId === this.localSessionId) {
          this.showLevelUpScreen(m.newLevel, m.hpGain, m.manaGain, m.staminaGain);
        }
        // Visual effect visible to all players
        this.playLevelUpEffect(m.entityId);
      },
      onExpGained: (m) => {
        // Burbuja overhead tipo "habla" sobre el propio personaje. Solo el
        // client del player que cobró XP recibe este mensaje (info privada).
        if (this.localSessionId && m.amount > 0) {
          this.overheadText.showChatBubble(this.localSessionId, `+${m.amount} EXP`);
        }
      },
      // Skills de progresión (Minería/Herrería/…). Ver docs/SKILLS_SYSTEM.md.
      onSkillsSync: (m) => {
        this.ensureSkillsPanel().applySkills(m.skills);
        for (const s of m.skills) this.skillLevels.set(s.skillId, s.level);
        this.craftingPanel?.refresh();
      },
      onSkillXpGained: (m) => {
        this.ensureSkillsPanel().setSkillXp(m.skillId, m.experience);
        // Feed de XP estilo MMO (drops que flotan y se acumulan por skill).
        this.ensureSkillXpFeed().push(m.skillId, m.amount);
      },
      onSkillLevelUp: (m) => {
        if (!this.localSessionId) return;
        this.skillLevels.set(m.skillId, m.newLevel);
        this.craftingPanel?.refresh();
        const name = getSkillDef(m.skillId)?.name ?? '';
        this.overheadText.showChatBubble(this.localSessionId, `¡${name} nivel ${m.newLevel}!`, '#ffd76a');
        this.playLevelUpEffect(this.localSessionId);
      },
      onSkillNotice: (m) => {
        // Aviso overhead coloreado (gates de minería, rotura de pico).
        if (this.localSessionId) {
          this.overheadText.showChatBubble(this.localSessionId, m.text, m.color);
        }
      },
      onWorkStarted: (m) => this.handleWorkStarted(m.nodeId),
      onWorkStopped: () => this.handleWorkStopped(),
      onMerchantOpened: (m) => this.vendorPanels.handleMerchantOpened(m),
      onSpellVendorOpened: (m) => this.vendorPanels.handleSpellVendorOpened(m),
      onExchangeOpened: (m) => this.vendorPanels.handleExchangeOpened(m),
      onExchangeBook: (m) => this.exchangePanel?.applyBook(m),
      onExchangeClaims: (m) => this.exchangePanel?.applyClaims(m),
      onMarketplaceOpened: (m) => this.vendorPanels.handleMarketplaceOpened(m),
      onMarketplaceResults: (m) => this.marketplacePanel?.applyResults(m),
      onMarketplaceAppraisal: (m) => this.marketplacePanel?.applyAppraisal(m),
      onMarketplaceSavedSearches: (m) => this.marketplacePanel?.applySavedSearches(m.searches),
      onExchangeWatches: (m) => this.exchangePanel?.applyWatches(m.watches),
      onMarketAlerts: (m) => this.marketNet.handleMarketAlerts(m),
      onTraderStats: (m) => this.marketplacePanel?.applyTraderStats(m.stats),
      onGoldUpdated: (m) => {
        this.marketNet.handleGoldUpdated(m);
        // Sonido de monedas al GANAR oro, tier por cantidad.
        this.audioWorld.playGoldGainSound(m.delta ?? 0);
      },
      onNpcRespawned: (m) => this.worldMsg.handleNpcRespawned(m),
      onActiveBuffsSync: (m) => {
        if (m.entityId === this.localSessionId && this.buffBar) {
          this.buffBar.updateBuffs(m.buffs);
        }
      },
      onMovementConfirmed: (m) => this.movement.handleMovementConfirmed(m),
      onPlayerTeleported: (m) => this.movement.handlePlayerTeleported(m),
      onPlayerMapChange: (m) => this.movement.handlePlayerMapChange(m),
      onPlayEntityFx: (m) => this.particleFx.handlePlayEntityFx(m),
      onPlaySound: (m) => this.audioWorld.handlePlaySound(m.soundId, m.x, m.y),
      onAreaEntered: (m) => this.worldMsg.handleAreaEntered(m),
      onAreaEntryDenied: (m) => this.overheadText.showSelfErrorText(m.message),
      onFactionStats: (m) => this.factionUI.handleFactionStats(m),
      onFactionJoinPrompt: (m) => this.factionUI.handleFactionJoinPrompt(m),
      onFactionRankInfo: (m) => this.factionUI.handleFactionRankInfo(m),
      onFactionBroadcast: (m) => this.chat.addSystemMessage(m.text, getFactionColor(m.factionId as FactionId)),
      onQuestLogSync: (m) => this.questUI?.onLogSync(m),
      onQuestOffer: (m) => this.questUI?.onOffer(m),
      onQuestProgress: (m) => this.questUI?.onProgress(m),
      onQuestCompleted: (m) => this.questUI?.onCompleted(m),
      onPartySync: (m) => {
        this.partyController?.onSync(m);
        this.labels.refreshPlayerNameColors();
      },
      onPartyInviteReceived: (m) => this.partyController?.onInviteReceived(m),
      onPartyFinderResults: (m) => this.partyController?.onFinderResults(m),
      onPartyNotify: (m) => this.partyController?.onNotify(m),
      onError: (message) => {
        const err = message as { code?: string; message?: string };
        // Melee sin stamina: texto rojo sobre el player local. (ON_COOLDOWN se
        // ignora a propósito para no spamear al machacar la tecla de ataque.)
        if (err.code === 'NO_STAMINA' && err.message && this.localSessionId) {
          this.overheadText.showSelfErrorText('No tenés energía suficiente');
          return;
        }
        // Errores de navegación (tecla N / barco): texto rojo sobre el player
        // local, igual que los requisitos de hechizos. Mensajes cortos; el
        // detalle del server queda en consola. Los códigos de canal en curso
        // se ignoran (anti-spam al machacar N). Ver docs/WATER_AND_BOATS.md.
        if (err.code && this.localSessionId) {
          if (err.code === 'BOAT_NEEDS_DOCK') {
            // Costa sin muelle: distinguir dirección con el sailing propio
            // (embarcar vs desembarcar usan el mismo código).
            const sailing =
              (this.room?.state.players.get(this.localSessionId) as unknown as { sailing?: boolean })
                ?.sailing === true;
            this.overheadText.showSelfErrorText(
              sailing ? 'Necesito un muelle para desembarcar' : 'Necesito un muelle para navegar'
            );
            return;
          }
          if (err.code === 'BOAT_NEEDS_WATER' || err.code === 'BOAT_IMPAIRED') {
            this.overheadText.showSelfErrorText('No puedo navegar aquí');
            return;
          }
          if (err.code === 'NO_BOAT_EQUIPPED') {
            this.overheadText.showSelfErrorText('Necesito un barco equipado');
            return;
          }
          if (err.code === 'BOAT_NEEDS_LAND') {
            this.overheadText.showSelfErrorText('No puedo desembarcar aquí');
            return;
          }
        }
        // Show cast errors as red text above local player (only visible to self)
        if ((err.code === 'CAST_FAILED' || err.code === 'MANA_FULL' || err.code === 'MANA_NOT_FULL') && err.message && this.localSessionId) {
          const errorText = err.message === 'Not enough mana' ? 'No tenés maná suficiente'
            : err.message === 'Not enough stamina' ? 'No tenés energía suficiente'
            : err.message === 'Not enough HP' ? 'No tenés HP suficiente'
            : err.message === 'Spell on cooldown' ? 'Hechizo en enfriamiento'
            : err.message === 'You are silenced' ? 'Estás silenciado'
            : err.message === 'Tu maná ya está completo' ? 'Tu maná ya está completo'
            : err.message;
          this.overheadText.showSelfErrorText(errorText);
        }
        console.error('[GameScene] Server error:', message);
      },
    });
  }

  /**
   * Muestra el indicador de zoom en el centro de la pantalla con fade in/out.
   */
  private showZoomIndicator(zoom: number): void {
    const { width, height } = this.scale;
    const zoomPercent = Math.round(zoom * 100);
    const text = `${zoomPercent}%`;
    
    // Crear el texto si no existe
    if (!this.zoomIndicatorText) {
      this.zoomIndicatorText = this.addText(width / 2, height / 2, text, {
          fontSize: '48px',
          color: '#ffffff',
          stroke: '#000000',
          strokeThickness: 4,
          shadow: {
            offsetX: 2,
            offsetY: 2,
            color: '#000000',
            blur: 4,
            fill: true,
          },
        })
        .setOrigin(0.5, 0.5)
        .setDepth(1200) // Muy alto para estar sobre todo
        .setScrollFactor(0)
        .setAlpha(0);
    }
    
    // Actualizar texto y posición
    this.zoomIndicatorText.setText(text);
    this.zoomIndicatorText.setPosition(width / 2, height / 2);
    
    // Cancelar timer anterior si existe
    if (this.zoomIndicatorTimer) {
      this.zoomIndicatorTimer.remove();
      this.zoomIndicatorTimer = null;
    }
    
    // Cancelar tweens anteriores en este objeto
    this.tweens.killTweensOf(this.zoomIndicatorText);
    
    // Fade in
    this.tweens.add({
      targets: this.zoomIndicatorText,
      alpha: 1,
      duration: GameScene.ZOOM_INDICATOR_FADE_DURATION,
      ease: 'Power2',
    });
    
    // Timer para iniciar fade out después del tiempo de display
    this.zoomIndicatorTimer = this.time.delayedCall(
      GameScene.ZOOM_INDICATOR_DISPLAY_TIME,
      () => {
        if (this.zoomIndicatorText) {
          this.tweens.add({
            targets: this.zoomIndicatorText,
            alpha: 0,
            duration: GameScene.ZOOM_INDICATOR_FADE_DURATION,
            ease: 'Power2',
          });
        }
      }
    );
  }

  /**
   * Configura un objeto individual para la cámara correcta basándose en su depth.
   */
  public configureObjectCamera(gameObject: Phaser.GameObjects.GameObject): void {
    if (!this.uiCamera) return;
    
    const depth = (gameObject as any).depth ?? 0;
    
    if (depth >= GameScene.UI_DEPTH_THRESHOLD) {
      // Objeto de UI: ignorar en cámara principal, mostrar en cámara UI
      this.cameras.main.ignore(gameObject);
    } else {
      // Objeto del juego: mostrar en cámara principal, ignorar en cámara UI
      this.uiCamera.ignore(gameObject);
    }
  }

  /**
   * Configura las cámaras para que la UI no se vea afectada por el zoom.
   * La cámara principal muestra solo objetos del juego (depth < 800).
   * La cámara UI muestra solo objetos de UI (depth >= 800) con zoom fijo.
   */
  public setupCameraFilters(): void {
    if (!this.uiCamera) return;
    
    // Iterar sobre todos los objetos de la escena
    this.children.each((child) => {
      this.configureObjectCamera(child as Phaser.GameObjects.GameObject);
      return true;
    });
  }


  public getKeyBindingsForStorage(): Record<ActionKeyId, SavedKeyBinding> {
    return this.inputManager?.getKeyBindingsForStorage() ?? createDefaultKeyBindings();
  }

  public configureActionKeys(): void {
    this.inputManager?.configureActionKeys();
  }

  private isActionKeyCode(action: ActionKeyId, keyCode?: number): boolean {
    return this.inputManager?.isActionKeyCode(action, keyCode) ?? false;
  }

  private isActionJustDown(action: ActionKeyId): boolean {
    return this.inputManager?.isActionJustDown(action) ?? false;
  }

  private isActionDown(action: ActionKeyId): boolean {
    return this.inputManager?.isActionDown(action) ?? false;
  }

  public getKeyName(keyCode?: number): string {
    return this.inputManager?.getKeyName(keyCode) ?? 'Sin asignar';
  }

  private getActionKeyLabel(action: ActionKeyId): string {
    return this.inputManager?.getActionKeyLabel(action) ?? 'Sin asignar';
  }

  private getKeyBindingsDescription(): string {
    return 'Haz click en cualquier tecla para cambiarla.\nPresiona ESC para cancelar.';
  }

  public getClosestHudScale(value: number): number {
    const candidates = GameScene.HUD_SCALE_VALUES;

    let closest = candidates[0];

    let bestDiff = Math.abs(value - closest);

    for (const candidate of candidates) {
      const diff = Math.abs(value - candidate);

      if (diff < bestDiff) {
        bestDiff = diff;

        closest = candidate;
      }
    }

    return closest;
  }





  /**

   * Actualiza (o limpia) el resaltado del tile actual del jugador local

   * según la opción de vídeo "Mostrar tile actual de jugador".

   */



  /**
   * Habilita/deshabilita TODO el input del jugador (pointer + teclado) de la escena. Se usa para
   * bloquear los controles durante la carga + el fade-out de la pantalla de carga, y reactivarlos
   * recién cuando el fade terminó. No afecta los listeners de red (Colyseus) ni el render/update.
   */
  public setControlsEnabled(enabled: boolean): void {
    this.input.enabled = enabled;
    if (this.input.keyboard) {
      this.input.keyboard.enabled = enabled;
    }
    if (enabled) {
      // Limpiar cualquier dirección residual para que no arranque caminando solo al habilitar.
      this.inputSystem?.resetMovement();
    }
  }

  public async handleLogoutAndReturnToCharacterSelect(): Promise<void> {
    // No limpiamos la sesión ni llamamos a logout: al recargar

    // volvemos a la pantalla de selección de personaje con la sesión actual.

    window.location.reload();
  }

  public setupStateListeners(): void {
    if (!this.room) return;

    // Reset del estado del pump de entidades por si venimos de un scene.restart (translado cross-map):
    // la instancia de la escena persiste, así que los inicializadores de campo NO vuelven a correr.
    this.initialEntityQueue = [];
    this.initialEntityQueueTotal = 0;
    this.entitiesPopulated = false;

    const state = this.room.state as unknown as GameStateView;

    const players = state.players as SchemaMap<PlayerStateView>;

    // Foto inicial: el player LOCAL se crea sincrónico ya (follow target de la cámara + predicción);
    // el resto de players + NPCs + nodos se ENCOLAN y se drenan de a poco desde update() para no
    // congelar el frame de "Entrar" (cada label es un add.text que rasteriza su canvas). Ver el pump
    // drainInitialEntityQueue() y docs/AUTH_AND_LOGIN.md.
    players.forEach((player, id) => {
      if (id === this.localSessionId) {
        this.playerSync.addOrUpdatePlayer(id, player);
      } else {
        this.initialEntityQueue.push(() => this.playerSync.addOrUpdatePlayer(id, player));
      }
    });

    players.onAdd?.((player, id) => {
      this.playerSync.addOrUpdatePlayer(id, player);
    });

    players.onChange?.((player, id) => {
      this.playerSync.addOrUpdatePlayer(id, player);
      // El refresh del CharacterInfoPanel se hace desde handleInventorySync
      // (single source of truth post-equip). MapSchema.onChange en Colyseus
      // 0.15+/schema 2.x no se dispara confiablemente para cambios de campos
      // anidados del schema hijo, así que no podemos depender de este callback.
    });

    players.onRemove?.((_player, id) => {
      this.playerSync.removePlayer(id);
    });

    const npcs = state.npcs as SchemaMap<NPCStateView>;

    npcs.forEach((npc, id) => {
      this.initialEntityQueue.push(() => this.npcSync.addOrUpdateNPC(id, npc));
    });

    npcs.onAdd?.((npc, id) => {
      this.npcSync.addOrUpdateNPC(id, npc);
    });

    npcs.onChange?.((npc, id) => {
      this.npcSync.addOrUpdateNPC(id, npc);
    });

    npcs.onRemove?.((_npc, id) => {
      this.npcSync.removeNPC(id);
    });

    // Yacimientos (nodos de recurso). Ver docs/MINING_AND_SMITHING.md.
    const resourceNodes = (state as unknown as { resourceNodes?: SchemaMap<ResourceNodeStateView> })
      .resourceNodes;
    if (resourceNodes) {
      if (!this.resourceNodeRenderer) {
        this.resourceNodeRenderer = new ResourceNodeRenderer(
          this,
          (nodeId) => this.sendGatherResource(nodeId),
          // Al click, loguear nombre + cantidad (actual/total) en la consola, como los items.
          (text) => this.chat.addSystemMessage(text, '#9ad3b8')
        );
      }
      resourceNodes.forEach((n, id) =>
        this.initialEntityQueue.push(() => this.resourceNodeRenderer?.addOrUpdate(id, n))
      );
      resourceNodes.onAdd?.((n, id) => this.resourceNodeRenderer?.addOrUpdate(id, n));
      resourceNodes.onChange?.((n, id) => this.resourceNodeRenderer?.addOrUpdate(id, n));
      resourceNodes.onRemove?.((_n, id) => this.resourceNodeRenderer?.remove(id));
    }

    // Estaciones de crafteo (bancos). Ver docs/CRAFTING_SYSTEM.md.
    const craftingStations = (state as unknown as { craftingStations?: SchemaMap<CraftingStationStateView> })
      .craftingStations;
    if (craftingStations) {
      if (!this.craftingStationRenderer) {
        this.craftingStationRenderer = new CraftingStationRenderer(
          this,
          (stationId, stationType, tx, ty) => this.onCraftingStationClick(stationId, stationType, tx, ty)
        );
      }
      craftingStations.forEach((s, id) =>
        this.initialEntityQueue.push(() => this.craftingStationRenderer?.addOrUpdate(id, s))
      );
      craftingStations.onAdd?.((s, id) => this.craftingStationRenderer?.addOrUpdate(id, s));
      craftingStations.onChange?.((s, id) => this.craftingStationRenderer?.addOrUpdate(id, s));
      craftingStations.onRemove?.((_s, id) => this.craftingStationRenderer?.remove(id));
    }

    // Foto inicial encolada: guardar el total para el progreso de la barra y, si no había nada que
    // crear, marcar poblado de una (no queda nadie que drene el pump).
    this.initialEntityQueueTotal = this.initialEntityQueue.length;
    if (this.initialEntityQueue.length === 0) this.entitiesPopulated = true;
  }

  /**
   * Pump del burst inicial de entidades: crea hasta ENTITY_DRAIN_PER_FRAME entidades de la foto
   * inicial por frame (players remotos/NPCs/nodos), repartiendo el costo en varios frames en vez de
   * un freeze único al joinear. Corre BAJO la pantalla de carga; alimenta la barra y marca
   * `entitiesPopulated` al vaciarse. Ver setupStateListeners() y docs/AUTH_AND_LOGIN.md.
   */
  /**
   * Ejecuta `fn` sobre cada item repartiendo el trabajo en frames: procesa items hasta gastar
   * ~`budgetMs` en el frame actual y CEDE el hilo (rAF) hasta el próximo frame antes de seguir. Así
   * un trabajo pesado del warmup (cortar 112 spritesheets, crear cientos de animaciones) no congela
   * el login — se hace TODO por adelantado, pero repartido. La escena corre su update() mientras
   * create() está suspendido en estos awaits (por eso el loading screen sigue animando). Ver create().
   */
  private async runWarmupChunked<T>(
    items: T[],
    fn: (item: T) => void,
    budgetMs = 6
  ): Promise<void> {
    let i = 0;
    while (i < items.length) {
      if (!this.scene.isActive()) return; // la escena se reinició (translado) → abortar
      const start = performance.now();
      do {
        fn(items[i]);
        i++;
      } while (i < items.length && performance.now() - start < budgetMs);
      if (i < items.length) {
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      }
    }
  }

  private drainInitialEntityQueue(): void {
    if (this.initialEntityQueue.length === 0) return;
    // Crear entidades hasta gastar el presupuesto de tiempo del frame (creamos al menos una para
    // garantizar progreso aunque una sola ya se pase del budget).
    const start = performance.now();
    do {
      const make = this.initialEntityQueue.shift();
      make?.();
    } while (this.initialEntityQueue.length > 0 && performance.now() - start < GameScene.ENTITY_DRAIN_BUDGET_MS);
    if (this.initialEntityQueueTotal > 0) {
      const done = this.initialEntityQueueTotal - this.initialEntityQueue.length;
      // Puentear el drenado al tramo 0.85→1.0 de la barra (el warmup ya la dejó ~0.85 en el login).
      this.loadingScreenUi.setLoadingBarProgress(0.85 + (done / this.initialEntityQueueTotal) * 0.15);
    }
    if (this.initialEntityQueue.length === 0) {
      this.entitiesPopulated = true;
      this.loadingScreenUi.checkAndHideLoadingScreen();
    }
  }

  /**
   * Click en un nodo → togglea el MODO TRABAJO (auto-extracción). El server corre el loop
   * (extrae hasta agotar/moverse) y valida todo. Ver docs/FISHING_SKILL.md.
   */
  private sendGatherResource(nodeId: string): void {
    this.colyseusClient?.send({ type: ClientMessageType.START_GATHER_WORK, nodeId } as never);
    // Los SFX de extracción (golpe de pico, fallo, gema, rotura) los dispara el SERVER por intento
    // vía PLAY_SOUND (posicional en el player) — así suenan en CADA golpe del modo trabajo, no sólo
    // al iniciar. Ver GameRoom.tryGatherOnce / docs/SOUND_AND_MUSIC.md.
  }

  /** Tile actual del player local (o null si aún no hay render). */
  private localPlayerTile(): { x: number; y: number } | null {
    const render = this.localSessionId ? this.playerObjects.get(this.localSessionId) : undefined;
    if (!render) return null;
    return { x: Math.floor(render.currentWorldX / TILE_SIZE), y: Math.floor(render.currentWorldY / TILE_SIZE) };
  }

  /**
   * Nivel de personaje del player local, derivado de su `experience` (schema de la room)
   * con la curva compartida. Lo usa el World Map para gatear por nivel el catálogo de
   * quests. Devuelve 1 si aún no hay estado. Ver docs/EXPERIENCE_AND_LEVELING.md.
   */
  public getLocalPlayerLevel(): number {
    const ps = this.localSessionId ? this.room?.state.players.get(this.localSessionId) : null;
    const exp = (ps as { experience?: number } | null | undefined)?.experience ?? 0;
    return getLevelFromTotalExp(exp).level;
  }

  /** Click en una estación de crafteo: abre la ventana si estás a ≤2 tiles. */
  private onCraftingStationClick(stationId: string, stationType: string, tx: number, ty: number): void {
    const tile = this.localPlayerTile();
    if (!tile || Math.abs(tile.x - tx) > 2 || Math.abs(tile.y - ty) > 2) {
      this.chat.addSystemMessage('Acercate a la estación para craftear.', '#ffcc55');
      return;
    }
    this.ensureCraftingPanel().toggle(stationId, stationType);
  }

  /** Crea la ventana de crafteo la primera vez que se necesita. */
  private ensureCraftingPanel(): CraftingPanel {
    if (!this.craftingPanel) {
      this.craftingPanel = new CraftingPanel(this, {
        onCraft: (stationId, recipeId) =>
          this.colyseusClient?.send({ type: ClientMessageType.CRAFT_ITEM, stationId, recipeId } as never),
        getItemCount: (itemId) =>
          this.lastInventorySnapshot.reduce((sum, s) => (s.itemId === itemId ? sum + s.quantity : sum), 0),
        getSkillLevel: (skillId) => this.skillLevels.get(skillId) ?? 1,
      });
    }
    return this.craftingPanel;
  }

  /** WORK_STARTED: abre la ventanita de progreso para el nodo que empezamos a trabajar. */
  private handleWorkStarted(nodeId: string): void {
    const node = (this.room?.state as unknown as { resourceNodes?: Map<string, { typeId?: string }> })
      .resourceNodes?.get(nodeId);
    const type = node?.typeId ? getResourceNodeType(node.typeId) : undefined;
    if (!type) return;
    if (!this.workPanel) this.workPanel = new WorkProgressPanel(this);
    const itemName = getItemTemplate(type.producesItemId)?.name ?? type.name;
    this.workPanel.show(type.producesItemId, type.name ?? itemName);
    this.activeWorkNodeId = nodeId;
  }

  /** WORK_STOPPED: cierra la ventanita. */
  private handleWorkStopped(): void {
    this.activeWorkNodeId = null;
    this.workPanel?.hide();
  }

  /** Actualiza en vivo la ventanita de trabajo (tenés / restante). Llamar desde el update loop. */
  private updateWorkPanel(): void {
    if (!this.activeWorkNodeId || !this.workPanel) return;
    const node = (
      this.room?.state as unknown as {
        resourceNodes?: Map<string, { typeId?: string; remaining?: number; maxQuantity?: number }>;
      }
    ).resourceNodes?.get(this.activeWorkNodeId);
    if (!node) {
      this.handleWorkStopped();
      return;
    }
    const type = node.typeId ? getResourceNodeType(node.typeId) : undefined;
    if (!type) return;
    const held = this.lastInventorySnapshot.reduce(
      (sum, s) => (s.itemId === type.producesItemId ? sum + s.quantity : sum),
      0
    );
    this.workPanel.update(held, node.remaining ?? 0, node.maxQuantity ?? 0);
  }

  /** Crea el panel de Skills la primera vez que se necesita. */
  private ensureSkillsPanel(): SkillsPanel {
    if (!this.skillsPanel) {
      this.skillsPanel = new SkillsPanel(this);
    }
    return this.skillsPanel;
  }

  /** Crea el feed de XP de habilidades la primera vez que se necesita. */
  private ensureSkillXpFeed(): SkillXpFeed {
    if (!this.skillXpFeed) {
      this.skillXpFeed = new SkillXpFeed(this);
    }
    return this.skillXpFeed;
  }

  /** Abre/cierra la ventanita de debug para dar XP de skill (botón 🎓). */
  public toggleDebugSkillXpPanel(): void {
    if (!this.debugSkillXpPanel) {
      this.debugSkillXpPanel = new DebugSkillXpPanel({
        getPlayers: () => {
          const out: Array<{ id: string; name: string; isSelf: boolean }> = [];
          this.room?.state.players.forEach((p, id) => {
            out.push({
              id,
              name: (p as { name?: string }).name ?? id,
              isSelf: id === this.localSessionId,
            });
          });
          return out;
        },
        onGrant: (targetSessionId, skillId, amount) => {
          this.colyseusClient?.send({
            type: ClientMessageType.DEBUG_GRANT_SKILL_XP,
            targetSessionId,
            skillId,
            amount,
          } as never);
        },
      });
    }
    this.debugSkillXpPanel.toggle();
  }

  /** Abre/cierra el panel de Skills y pide el estado fresco al abrir. */
  private toggleSkillsPanel(): void {
    const panel = this.ensureSkillsPanel();
    const opened = panel.toggle();
    if (opened) {
      this.colyseusClient?.send({ type: ClientMessageType.REQUEST_SKILLS } as never);
    }
  }


  /** Decal rojo breve sobre la zona ZxZ donde va a caer un hechizo de área (telegrafía). */
  private showAoeTelegraph(cx: number, cy: number, size: number): void {
    const half = Math.floor(size / 2);
    const w = size * TILE_SIZE;
    const left = (cx - half) * TILE_SIZE;
    const top = (cy - half) * TILE_SIZE;
    const rect = this.add
      .rectangle(left + w / 2, top + w / 2, w, w, 0xff3333, 0.28)
      .setStrokeStyle(2, 0xff5555, 0.9)
      .setDepth(50);
    this.tweens.add({
      targets: rect,
      alpha: 0,
      duration: 700,
      ease: 'Quad.easeOut',
      onComplete: () => rect.destroy(),
    });
  }


  /**
   * Golpe melee direccional (tecla de ataque). Manda el swing al server en el
   * heading actual; el intervalo, el Radio de ataque, la stamina y el daño los
   * resuelve el server (anti-cheat). Reproduce el telegraph local optimista.
   * Ver docs/COMBAT_SYSTEMS.md.
   */
  private handleMeleeAttack(): void {
    if (!this.localSessionId || !this.colyseusClient?.isConnected()) return;
    const render = this.playerObjects.get(this.localSessionId);
    if (!render) return;
    // Muerto (alma activa) no golpea; el server igual lo rechazaría.
    if (render.soul) return;
    // Heading: dirección de input si hay tecla apretada, si no el facing visual.
    const direction =
      this.getDirectionFromInput() ?? render.visualDirection ?? Direction.DOWN;
    render.visualDirection = direction;
    this.colyseusClient.send({ type: ClientMessageType.ATTACK, direction });
    // Telegraph local (a los demás clients les llega vía MELEE_SWING).
    this.particleFx.playMeleeSwingFx(this.localSessionId, direction);
  }


  public updateArrowOrientation(arrow: Phaser.GameObjects.Sprite, direction: Direction): void {
    // La textura base de 'headingArrow' apunta hacia la DERECHA.

    // Corregimos el mapeo: horizontal OK, vertical invertido.

    let angleDeg = 0;

    switch (direction) {
      case Direction.RIGHT:
        angleDeg = 0;

        break;

      case Direction.UP:
        angleDeg = 270;

        break;

      case Direction.LEFT:
        angleDeg = 180;

        break;

      case Direction.DOWN:
        angleDeg = 90;

        break;

      default:
        angleDeg = 0;

        break;
    }

    arrow.setAngle(angleDeg);
  }

  /**

   * Ajusta las profundidades relativas de body/head/weapon/shield según la dirección

   * para simular que algunas piezas quedan por delante o por detrás del cuerpo.

   *

   * Reglas:
   * - Mirando ARRIBA: weapon y shield por detrás del body/head.

   * - Mirando DERECHA: shield por detrás, weapon por delante.

   * - Mirando IZQUIERDA: weapon por detrás, shield por delante.

   * - Mirando ABAJO: ambos por delante del body.

   */

  public getEntityBaseDepthFromTileY(tileY: number): number {
    // Base ligeramente por encima de las capas del mapa para que el personaje

    // pueda quedar por delante o por detrás de decoraciones según la Y.

    return this.getDepthFromTileY(tileY) + GameScene.ENTITY_DEPTH_OFFSET;
  }

  /**
   * Update shadow offset based on time of day.
   * Shadows move throughout the day simulating sun position:
   * - Morning (6:00): Shadow extends to the left
   * - Noon (12:00): Shadow is centered and short
   * - Evening (18:00): Shadow extends to the right
   */
  
  /**
   * Updates shadow projection like clock hands.
   * 
   * Think of the shadow as a clock hand that rotates around the entity:
   * - 6am:  Shadow points WEST (left)   = 9 o'clock position
   * - 9am:  Shadow points NORTHWEST     = between 10-11 o'clock
   * - 12pm: Shadow points NORTH (up)    = 12 o'clock position (very short)
   * - 3pm:  Shadow points NORTHEAST     = between 1-2 o'clock
   * - 6pm:  Shadow points EAST (right)  = 3 o'clock position
   * 
   * In our coordinate system (Y increases downward):
   * - Angle 0 = right (+X)
   * - Angle PI/2 = down (+Y)
   * - Angle PI or -PI = left (-X)
   * - Angle -PI/2 = up (-Y)
   */
  private updateShadowTimeOffset(hour: number): void {
    // Normalize hour to 0-24
    hour = ((hour % 24) + 24) % 24;
    
    // Opacidad de noche: la sombra sigue ahí pero MUY tenue. Clave para que no
    // haya un "choque" visual cuando aparece/desaparece al cruzar 6h/18h: la
    // opacidad de día se desvanece hasta este mismo valor en el borde dawn/dusk,
    // así la transición es continua (sin pop).
    const NIGHT_ALPHA = 0.1;
    // Opacidad máxima (alrededor del mediodía, sol alto).
    const DAY_PEAK_ALPHA = 0.5;

    if (hour >= 6 && hour <= 18) {
      // DAYTIME: Shadow rotates like clock hands
      // Map 6am-6pm (12 hours) to a half rotation (PI radians)
      // 6am = PI (pointing left/west), 12pm = -PI/2 (pointing up), 6pm = 0 (pointing right/east)
      const dayProgress = (hour - 6) / 12; // 0 at 6am, 0.5 at noon, 1 at 6pm

      // Shadow angle: starts at PI (left), goes to -PI/2 (up) at noon, ends at 0 (right)
      // This creates a clockwise rotation from left -> up -> right
      this.shadowAngle = Math.PI - (dayProgress * Math.PI);

      // Shadow length: shortest at noon, longest at dawn/dusk
      const noonDistance = Math.abs(dayProgress - 0.5) * 2; // 0 at noon, 1 at 6am/6pm
      this.shadowLength = 0.15 + noonDistance * 0.85; // Range: 0.15 (noon) to 1.0 (dawn/dusk)

      // Shadow opacity: MÁS fuerte al mediodía (sol alto), se desvanece hacia el
      // amanecer/atardecer hasta NIGHT_ALPHA en el borde (6h/18h) → continuo con la
      // noche. Antes la opacidad PICABA en el borde (0.6) y caía de golpe a 0.15,
      // causando el pop. (noonCloseness: 1 al mediodía, 0 en 6h/18h.)
      const noonCloseness = 1 - noonDistance;
      this.shadowAlpha = NIGHT_ALPHA + noonCloseness * (DAY_PEAK_ALPHA - NIGHT_ALPHA);
    } else {
      // NIGHTTIME: sombra muy tenue, casi directamente debajo. Misma opacidad que
      // el borde dawn/dusk → sin salto al cruzar 6h/18h.
      this.shadowAngle = -Math.PI / 2; // Points up slightly
      this.shadowLength = 0.1;
      this.shadowAlpha = NIGHT_ALPHA;
    }
  }

  /** Altura (px) a la que flota el alma sobre los pies del personaje muerto. */
  private static readonly SOUL_FLOAT_OFFSET_Y = 24;

  /**
   * Modo "alma": al morir, ocultar TODAS las capas físicas del personaje
   * (body/head/helm/boots/shield/weapon/arrow/dropshadow) y dibujar en su lugar
   * un orbe de partículas (PlayerSoul) que flota y deja estela al moverse. Al
   * revivir, destruir el alma y restaurar las capas. Idempotente por frame.
   */
  /** Aplica el blend (ADD/NORMAL) a todas las almas activas. Ver setAdditive. */
  private setAllSoulsAdditive(additive: boolean): void {
    this.playerObjects.forEach((render) => render.soul?.setAdditive(additive));
  }

  public updatePlayerSoulVisuals(
    render: PlayerRenderObjects,
    isDead: boolean,
    x: number,
    feetY: number,
    faction: string
  ): void {
    if (isDead) {
      render.sprite.setVisible(false);
      render.head?.setVisible(false);
      render.helm?.setVisible(false);
      render.boots?.setVisible(false);
      render.shield?.setVisible(false);
      render.weapon?.setVisible(false);
      render.dropshadow?.setVisible(false);
      render.arrow.setVisible(false);

      const soulY = feetY - GameScene.SOUL_FLOAT_OFFSET_Y;
      const depth = render.sprite.depth;
      if (!render.soul) {
        render.soul = new PlayerSoul(this, x, soulY, depth, soulPaletteForFaction(faction));
        // Si el grayscale local está activo, nacer en NORMAL (evita el cuadrado).
        render.soul.setAdditive(!this.deathGrayscaleApplied);
      }
      render.soul.update(x, soulY, depth, render.isMoving, this.time.now);
      render.soul.setVisible(true);
    } else if (render.soul) {
      // Revivió: limpiar el alma y restaurar las capas (las sombras y la flecha
      // las maneja su propia lógica este mismo frame; el body/head/equipo no se
      // tocan en otro lado, así que hay que reactivarlos acá).
      render.soul.destroy();
      render.soul = null;
      render.sprite.setVisible(true);
      render.head?.setVisible(true);
      render.helm?.setVisible(true);
      render.boots?.setVisible(true);
      render.shield?.setVisible(true);
      render.weapon?.setVisible(true);
      render.arrow.setVisible(this.showHeadingArrow);
    }
  }

  /**
   * Decide si el nombre (label) de un player debe verse en pantalla para el
   * OBSERVADOR LOCAL. Implementa las reglas de "almas":
   *   - Estando muerto no ves tu propio nombre.
   *   - Un muerto no ve nombres de vivos.
   *   - El nombre de un muerto sólo se ve si: el observador tiene el mod
   *     `seeDeadNames` (vivo o muerto), o ambos son muertos de la MISMA
   *     facción-ciudad (aresgard/elvaria; rebeldes y newbies no).
   * Es puramente visual (el `name` sigue en el state sincronizado).
   */
  public isPlayerNameVisible(
    player: PlayerStateView,
    isSelf: boolean,
    id?: string,
    render?: PlayerRenderObjects
  ): boolean {
    // Navegando (barco equipado): el nombre NUNCA se muestra de forma
    // permanente — pisa cualquier config, incluido el nombre propio. Solo se ve
    // con el mouse encima de su tile o teniéndolo targeteado. Ver docs/WATER_AND_BOATS.md.
    if (player.sailing === true && !this.isPlayerHoveredOrTargeted(player, id, render)) {
      return false;
    }

    const local = this.localSessionId
      ? this.room?.state?.players?.get(this.localSessionId)
      : undefined;
    const pDead = player.state === EntityState.DEAD;
    const lDead = local?.state === EntityState.DEAD;

    if (isSelf) return !pDead; // nunca tu propio nombre estando muerto
    if (!pDead) return !lDead; // un muerto no ve nombres de vivos

    // player muerto: mod (de quien observa) o muertos de la misma facción-ciudad.
    if (local?.canSeeDeadNames) return true;
    if (!lDead) return false; // vivo sin mod no ve almas
    const lf = local?.faction ?? '';
    const sameCity =
      lf !== '' && lf === (player.faction ?? '') && FACTIONS[lf as FactionId]?.isCity === true;
    return sameCity;
  }

  /**
   * True si el player está targeteado por el local o el mouse está sobre su
   * tile VISUAL. Los players no tienen tileZone interactiva (a diferencia de
   * los NPCs con `nameHovered`), así que el hover se resuelve aritméticamente
   * contra el pointer del mundo — se usa para revelar el nombre de un
   * navegante. Ver docs/WATER_AND_BOATS.md.
   */
  private isPlayerHoveredOrTargeted(
    player: PlayerStateView,
    id?: string,
    render?: PlayerRenderObjects
  ): boolean {
    if (id && this.targets.selectedTargetType === 'player' && this.targets.selectedTargetId === id) {
      return true;
    }
    const pointer = this.input.activePointer;
    const ptX = Math.floor(pointer.worldX / TILE_SIZE);
    const ptY = Math.floor(pointer.worldY / TILE_SIZE);
    // Tile visual (interpolado) si hay render; sino el tile del server.
    const tileX = render ? Math.round(render.currentWorldX / TILE_SIZE - 0.5) : player.x;
    const tileY = render ? Math.round(render.currentWorldY / TILE_SIZE - 0.5) : player.y;
    return ptX === tileX && ptY === tileY;
  }

  public updateEquipmentDepth(

    render: PlayerRenderObjects,

    direction: Direction,

    tileY: number

  ): void {
    const base = this.getEntityBaseDepthFromTileY(tileY);

    // Body y head como referencia (todo el personaje se mueve como bloque,

    // con pequeñas variaciones sólo para simular delante/detrás del cuerpo).

    const headDelta = 0.01;

    const frontDelta = 0.02;

    const backDelta = -0.02;

    const arrowDelta = 0.03;

    render.sprite.setDepth(base);

    if (render.head) {
      render.head.setDepth(base + headDelta);
    }

    if (render.helm) {
      render.helm.setDepth(base + headDelta);
    }

    // Botas se dibujan encima del body (depth ligeramente mayor que el body)
    if (render.boots) {
      render.boots.setDepth(base + headDelta + 0.005);
    }

    let weaponDepthFront = base + frontDelta;

    let shieldDepthFront = base + frontDelta;

    let weaponDepthBack = base + backDelta;

    let shieldDepthBack = base + backDelta;

    let weaponDepth = weaponDepthFront;

    let shieldDepth = shieldDepthFront;

    switch (direction) {
      case Direction.UP:
        // Ambos detrás del cuerpo

        weaponDepth = weaponDepthBack;

        shieldDepth = shieldDepthBack;

        break;

      case Direction.RIGHT:
        // Shield detrás, weapon delante

        shieldDepth = shieldDepthBack;

        weaponDepth = weaponDepthFront;

        break;

      case Direction.LEFT:
        // Weapon detrás, shield delante

        weaponDepth = weaponDepthBack;

        shieldDepth = shieldDepthFront;

        break;

      case Direction.DOWN:
      default:
        // Ambos delante del cuerpo

        weaponDepth = weaponDepthFront;

        shieldDepth = shieldDepthFront;

        break;
    }

    if (render.weapon) {
      render.weapon.setDepth(weaponDepth);
    }

    if (render.shield) {
      render.shield.setDepth(shieldDepth);
    }

    // La flecha de heading siempre por delante del body/head/equipamiento,

    // pero sigue formando parte del "bloque" del personaje.

    render.arrow.setDepth(base + arrowDelta);

    const labelDepth = base + GameScene.LABEL_DEPTH_DELTA;
    render.label.setDepth(labelDepth);
    if (render.label instanceof Phaser.GameObjects.BitmapText) {
      this.labels.updateLabelShadowDepth(render.label, labelDepth - 0.01);
    }
  }

}
